/**
 * Cây route sinh từ design/extracted/routes.json + route con của từng feature.
 * Mỗi module là một route con lười (lazy) bọc RequireCapability/moduleCode. W11: trang feature nạp qua `featurePage(feature, Page)` —
 * cục bộ (code-split) hoặc từ remote Module Federation theo cụm (federation/clusters.ts) khi có VITE_MF_REMOTES; guard vẫn chạy trước khi nạp.
 */
import type {
  AffiliateKycStatus,
  AffiliateStatus,
  AgingBucket,
  AlertStatus,
  AlertType,
  ApiKeyStatus,
  AppUserStatus,
  AuditKind,
  AuditOutcome,
  CampaignStatus,
  CareGroup,
  CareTicketStatus,
  ChatOrderStatus,
  ConnectorStatus,
  ConnectorType,
  ContractRequestStatus,
  ContractStatus,
  ConversationStatus,
  CreditChangeStatus,
  CreditProfileStatus,
  CustomerStatus,
  CustomerType,
  DeadLetterStatus,
  DeliveryAttemptStatus,
  DeliveryExceptionType,
  DeliveryReconciliationStatus,
  DepositStatus,
  DisplaySurveyResult,
  DocumentTemplateStatus,
  DriftStatus,
  DunningRunStatus,
  EInvoiceStatus,
  EmployeeStatus,
  ExportJobStatus,
  FarmerSlipStatus,
  FileStatus,
  GpsVerification,
  Grain,
  HoldType,
  InsightCompareMode,
  InstallmentPlanStatus,
  KnowledgeCategory,
  KnowledgeStatus,
  KybStatus,
  LandedCostType,
  LeadStatus,
  LeaveStatus,
  LiveSessionStatus,
  LoyaltyProgramStatus,
  LoyaltyStatus,
  MarketplaceListingStatus,
  MarketplaceOrderSyncStatus,
  MasterDataStatus,
  MatchStatus,
  MemberStatus,
  MovementType,
  NcrSeverity,
  NcrStatus,
  NotificationCategory,
  NotificationChannel,
  OemContractStatus,
  OemOrderStatus,
  OemSettlementStatus,
  OpportunitySource,
  OpportunityStatus,
  OrderChangeRequestStatus,
  OrderQueueStatus,
  OrderStatus,
  OutletStatus,
  OutletType,
  PayableSourceType,
  PaymentStatus,
  PeriodStatus,
  PlanStatus,
  PriceScopeType,
  ProductionPlanStatus,
  ProductStatus,
  ProfileChangeStatus,
  PromotionStatus,
  PurchaseContractStatus,
  PurchaseOrderRevisionStatus,
  PurchaseOrderStatus,
  PurchaseRequestSource,
  PurchaseRequestStatus,
  PurchaseRfqStatus,
  QaStatus,
  QcResult,
  QcSourceType,
  QuoteRequestStatus,
  QuoteStatus,
  RebateAccrualStatus,
  RebatePolicyStatus,
  RebateSettlementStatus,
  RecallStatus,
  ReceiptStatus,
  ReconciliationSource,
  ReconciliationStatus,
  RecurringPlanStatus,
  RefundStatus,
  RegistrationStatus,
  ReportCategory,
  ReportedOrderStatus,
  RewardStatus,
  RiskGrade,
  RmaStatus,
  RouteStatus,
  RuleStatus,
  SaasInvoiceStatus,
  SalesReturnStatus,
  SpecialPriceRequestStatus,
  StatementStatus,
  SubscriptionChangeStatus,
  SupplierEvaluationStatus,
  SupplierEvaluationType,
  SupplierInvoiceStatus,
  SupplierType,
  SurveyResponseStatus,
  SyncMessageStatus,
  TemplateStatus,
  TenantStatus,
  TicketCategory,
  TicketPriority,
  TripStatus,
  VisitStatus,
  VoucherStatus,
  VoucherType,
  WorkOrderStatus,
  WorkspaceType,
} from '@kovix/api-client'; // T9-F2 (chéo B1): ?compare= ở /insight/reports/$code
import { ORDER_CHANNEL_LABEL, PAYMENT_METHOD_LABEL } from '@kovix/api-client'; // Q3-e bộ lọc nâng cao /orders
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { completeSignIn } from '@/app/auth/auth';
import { RequireCapability } from '@/app/auth/RequireCapability';
import { SessionBootstrap } from '@/app/auth/SessionBootstrap';
import { featureModule, featurePage } from '@/app/federation/loadFeature'; // W11 Module Federation: nạp cục bộ hoặc từ remote theo cụm
import { Shell } from '@/app/shell/Shell';
import type { PortalAccessSearch } from '@/features/admin/components/portal/types'; // Y6 — chỉ kiểu, không kéo mã feature vào shell
import { AccessDeniedPage } from '@/pages/AccessDeniedPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { RouteErrorPage } from '@/pages/RouteErrorPage';
import { listPageSize } from '@/shared/lib/listPaging';

const rootRoute = createRootRoute({ component: () => <Outlet /> });

// OIDC callback — ngoài shell
const authCallbackRoute = createRoute({
  path: '/auth/callback',
  getParentRoute: () => rootRoute,
  beforeLoad: async () => {
    const to = await completeSignIn();
    // 12/10/2026 — replace: trang callback (mã dùng một lần) không ở lại lịch sử → bấm Back không quay về /auth/callback
    throw redirect({ to, replace: true });
  },
  component: () => null,
});

// Shell có phiên đăng nhập + quyền
const shellRoute = createRoute({
  id: '_shell',
  getParentRoute: () => rootRoute,
  component: () => (
    <SessionBootstrap>
      <Shell />
    </SessionBootstrap>
  ),
});

const dashboardRoute = createRoute({
  path: '/',
  getParentRoute: () => shellRoute,
  component: lazyRouteComponent(() => import('@/pages/DashboardPage'), 'DashboardPage'),
});
// R5.9 (mockup v152 fbOps) — Trung tâm vận hành F&B, trang khởi đầu tenantAdmin/executive
const fbOpsRoute = createRoute({
  path: '/ops',
  getParentRoute: () => shellRoute,
  component: featurePage('dashboard', 'FbOpsPage'),
});
const workInboxRoute = createRoute({
  path: '/work-inbox',
  getParentRoute: () => shellRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { filter?: string | undefined; selected?: string | undefined; delegations?: 1 | undefined } => ({
    filter: str(s.filter),
    selected: str(s.selected),
    // KOVIX-3006 (FT-BPM-071 bước 1): cờ mở panel ủy quyền. Bộ serialize mặc định của TanStack JSON-hóa giá trị →
    // chuỗi '1' thành ?delegations=%221%22, còn link ?delegations=1 được parse thành SỐ 1 (str() loại bỏ). Chuẩn hóa về số 1
    // (ghi ra đúng ?delegations=1) và chấp nhận cả '1'/true/'true' từ link cũ.
    delegations: [1, '1', true, 'true'].includes(s.delegations as never) ? 1 : undefined,
  }),
  component: lazyRouteComponent(() => import('@/pages/WorkInboxPage'), 'WorkInboxPage'),
});
const deniedRoute = createRoute({
  path: '/access-denied',
  getParentRoute: () => shellRoute,
  component: () => <AccessDeniedPage />,
});
// Kiểm thử UI 01/10/2026: đường dẫn sai → trang "Không tìm thấy" TRONG khung (splat ưu tiên thấp nhất; trước đây chữ "Not Found" mặc định của TanStack)
const notFoundRoute = createRoute({ path: '$', getParentRoute: () => shellRoute, component: () => <NotFoundPage /> });

// ---- KOVIX.Product (feature đã hiện thực) ----
const productsRoute = createRoute({
  path: '/products',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="product.product.view" moduleCode="product">
      <Outlet />
    </RequireCapability>
  ),
});
const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
const productListRoute = createRoute({
  path: '/',
  getParentRoute: () => productsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: ProductStatus | 'ALL' | undefined;
    q?: string | undefined;
    cursor?: string | undefined;
    categoryId?: string | undefined;
    limit?: number | undefined;
  } => ({
    status: typeof s.status === 'string' ? (s.status as ProductStatus | 'ALL') : undefined,
    q: str(s.q),
    cursor: str(s.cursor),
    categoryId: str(s.categoryId),
    // KOVIX-3080: số dòng/trang chọn ở chân bảng — trang lọc giá trị hợp lệ (productPageSize: 20/30/40; mặc định 50 không ghi lên URL)
    limit:
      typeof s.limit === 'number' || (typeof s.limit === 'string' && /^\d+$/.test(s.limit))
        ? Number(s.limit)
        : undefined,
  }),
  component: featurePage('product', 'ProductListPage'),
});
// Dữ liệu nền + hàng đợi duyệt (mockup productCategory/productIndustry, productUoM, productApproval). Route tĩnh đứng trước $productId.
const productCategoriesRoute = createRoute({
  path: '/categories',
  getParentRoute: () => productsRoute,
  validateSearch: (s: Record<string, unknown>): { selected?: string | undefined } => ({ selected: str(s.selected) }),
  component: featurePage('product', 'ProductCategoryPage'),
});
const productUomsRoute = createRoute({
  path: '/uoms',
  getParentRoute: () => productsRoute,
  component: featurePage('product', 'ProductUomPage'),
});
// ---- W6‑a: productTenantMaster / productTenantGroups+Types / productTenantPackaging ----
const productMasterRoute = createRoute({
  path: '/master',
  getParentRoute: () => productsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: ProductStatus | undefined;
    categoryId?: string | undefined;
    packagingRuleCode?: string | undefined;
    q?: string | undefined;
  } => ({
    status: typeof s.status === 'string' ? (s.status as ProductStatus) : undefined,
    categoryId: str(s.categoryId),
    packagingRuleCode: str(s.packagingRuleCode),
    q: str(s.q),
  }),
  component: featurePage('product', 'ProductMasterPage'),
});
const productTaxonomyRoute = createRoute({
  path: '/taxonomy',
  getParentRoute: () => productsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'groups' | 'types' | undefined; groupId?: string | undefined; create?: string | undefined } => ({
    tab: s.tab === 'types' ? 'types' : s.tab === 'groups' ? 'groups' : undefined,
    groupId: str(s.groupId),
    create: createFlag(s.create),
  }),
  component: featurePage('product', 'ProductTaxonomyPage'),
});
const productPackagingRoute = createRoute({
  path: '/packaging',
  getParentRoute: () => productsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'rules' | 'uoms' | 'types' | 'levels' | undefined; create?: string | undefined } => ({
    tab:
      s.tab === 'uoms' || s.tab === 'types' || s.tab === 'levels' || s.tab === 'rules'
        ? (s.tab as 'rules' | 'uoms' | 'types' | 'levels')
        : undefined,
    create: createFlag(s.create),
  }),
  component: featurePage('product', 'PackagingMasterPage'),
});
/** FT 02/10/2026 (L-07): TanStack đọc `?create=1` thành SỐ 1 → `str()` bỏ mất, deep link không mở hộp tạo. Nhận cả số/boolean. */
function createFlag(v: unknown): string | undefined {
  return typeof v === 'number' || typeof v === 'boolean' ? String(v) : str(v);
}
const productApprovalsRoute = createRoute({
  path: '/approvals',
  getParentRoute: () => productsRoute,
  // T5-E: status=CHANGES → hàng đợi bản sửa SKU; selected = productId, changeId = bản sửa đang xem
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: string | undefined; selected?: string | undefined; changeId?: string | undefined } => ({
    status: str(s.status),
    selected: str(s.selected),
    changeId: str(s.changeId),
  }),
  component: featurePage('product', 'ProductApprovalPage'),
});
// Vòng đời tổng hợp + combo builder (mockup productLifecycle, productBundle)
const productLifecycleRoute = createRoute({
  path: '/lifecycle',
  getParentRoute: () => productsRoute,
  validateSearch: (s: Record<string, unknown>): { status?: string | undefined; selected?: string | undefined } => ({
    status: str(s.status),
    selected: str(s.selected),
  }),
  component: featurePage('product', 'ProductLifecyclePage'),
});
const productBundlesRoute = createRoute({
  path: '/bundles',
  getParentRoute: () => productsRoute,
  validateSearch: (s: Record<string, unknown>): { selected?: string | undefined } => ({ selected: str(s.selected) }),
  component: featurePage('product', 'ProductBundlePage'),
});
// T9-F2 (chéo B5 G1-P4): deep link góc nhìn theo vai trò ?lens= (trang hiện nhớ lựa chọn ở trình duyệt — đọc search là việc của fe/product)
const PRODUCT_LENSES = new Set(['executive', 'sales', 'procurement', 'quality', 'warehouse', 'master']);
type ProductLensKey = 'executive' | 'sales' | 'procurement' | 'quality' | 'warehouse' | 'master';
const productDashboardRoute = createRoute({
  path: '/dashboard',
  getParentRoute: () => productsRoute,
  validateSearch: (s: Record<string, unknown>): { lens?: ProductLensKey | undefined } => ({
    lens: typeof s.lens === 'string' && PRODUCT_LENSES.has(s.lens) ? (s.lens as ProductLensKey) : undefined,
  }),
  component: featurePage('product', 'ProductDashboardPage'),
});
// GĐ 3.1: từ điển thuộc tính động, nguồn cung theo SKU, nhập liệu Excel/CSV (mockup productIndustry "quy tắc ngành", productSourcing, productLabel "ánh xạ nhập Excel")
const productAttributesRoute = createRoute({
  path: '/attributes',
  getParentRoute: () => productsRoute,
  // 08/10/2026 — categoryId: mở "Thêm thuộc tính" từ một danh mục → mặc định tick danh mục đó + mọi danh mục con
  validateSearch: (
    s: Record<string, unknown>,
  ): { selected?: string | undefined; create?: boolean | undefined; categoryId?: string | undefined } => ({
    selected: str(s.selected),
    create: s.create === true || s.create === 'true' ? true : undefined,
    categoryId: str(s.categoryId),
  }),
  component: featurePage('product', 'ProductAttributesPage'),
});
const productSourcingRoute = createRoute({
  path: '/sourcing',
  getParentRoute: () => productsRoute,
  validateSearch: (s: Record<string, unknown>): { productId?: string | undefined; q?: string | undefined } => ({
    productId: str(s.productId),
    q: str(s.q),
  }),
  component: featurePage('product', 'ProductSourcingPage'),
});
// R4.6 (M-47) — bản dịch nội dung Tenant cho Portal
const productTranslationsRoute = createRoute({
  path: '/translations',
  getParentRoute: () => productsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'gaps' | 'queue' | undefined;
    locale?: string | undefined;
    status?: 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED' | undefined;
  } => ({
    tab: s.tab === 'queue' || s.tab === 'gaps' ? s.tab : undefined,
    locale: typeof s.locale === 'string' && /^[a-z]{2}(-[A-Z]{2})?$/.test(s.locale) ? s.locale : undefined,
    status:
      s.status === 'DRAFT' || s.status === 'PENDING_APPROVAL' || s.status === 'APPROVED' || s.status === 'REJECTED'
        ? s.status
        : undefined,
  }),
  component: featurePage('product', 'ProductTranslationsPage'),
});
const ProductImportLazy = featurePage('product', 'ProductImportPage');
const productImportRoute = createRoute({
  path: '/import',
  getParentRoute: () => productsRoute,
  component: () => (
    <RequireCapability cap="product.product.create">
      <ProductImportLazy />
    </RequireCapability>
  ),
});
const productCreateRoute = createRoute({
  path: '/new',
  getParentRoute: () => productsRoute,
  component: () => (
    <RequireCapability cap="product.product.create">
      <ProductCreateLazy />
    </RequireCapability>
  ),
});
const ProductCreateLazy = featurePage('product', 'ProductCreatePage');
const productDetailRoute = createRoute({
  path: '/$productId',
  getParentRoute: () => productsRoute,
  // T5-E: tab=changes&changeId=… — bản sửa song song SKU đang xem
  // KOVIX-3120: back=master — mở từ Product Master công ty thì nút quay lại về đó (chỉ nhận giá trị đã biết)
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: string | undefined; changeId?: string | undefined; back?: 'master' | undefined } => ({
    tab: typeof s.tab === 'string' ? s.tab : undefined,
    changeId: str(s.changeId),
    back: s.back === 'master' ? 'master' : undefined,
  }),
  component: featurePage('product', 'ProductDetailPage'),
});

// ---- KOVIX.Customer (Đợt 1 · slice 1: crm, crmCreate, crm360, crmDedup) ----
const customersRoute = createRoute({
  path: '/customers',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="crm.customer.view" moduleCode="crm">
      <Outlet />
    </RequireCapability>
  ),
});
const customerListRoute = createRoute({
  path: '/',
  getParentRoute: () => customersRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: CustomerStatus | 'ALL' | undefined;
    customerType?: CustomerType | 'ALL' | undefined;
    q?: string | undefined;
    cursor?: string | undefined;
  } => ({
    status: typeof s.status === 'string' ? (s.status as CustomerStatus | 'ALL') : undefined,
    customerType: typeof s.customerType === 'string' ? (s.customerType as CustomerType | 'ALL') : undefined,
    q: str(s.q),
    cursor: str(s.cursor),
  }),
  component: featurePage('customer', 'CustomerListPage'),
});
const CustomerCreateLazy = featurePage('customer', 'CustomerCreatePage');
const customerCreateRoute = createRoute({
  path: '/new',
  getParentRoute: () => customersRoute,
  // T9 (yêu cầu chéo B2 G3-17): Unified Inbox mở /customers/new?name=&phone= để điền sẵn (phone CHỈ khi người bấm có crm.pii.view và SĐT không bị che)
  validateSearch: (s: Record<string, unknown>): { name?: string | undefined; phone?: string | undefined } => ({
    name: str(s.name),
    phone: str(s.phone),
  }),
  component: () => (
    <RequireCapability cap="crm.customer.create">
      <CustomerCreateLazy />
    </RequireCapability>
  ),
});
/** R6.2 — nhập khách CSV/XLSX (≤ 1 000 dòng, all-or-nothing, kiểm trùng BR-2.21 từng dòng). Cùng capability tạo khách. */
const CustomerImportLazy = featurePage('customer', 'CustomerImportPage');
const customerImportRoute = createRoute({
  path: '/import',
  getParentRoute: () => customersRoute,
  component: () => (
    <RequireCapability cap="crm.customer.create">
      <CustomerImportLazy />
    </RequireCapability>
  ),
});
const customerDedupRoute = createRoute({
  path: '/dedup',
  getParentRoute: () => customersRoute,
  // KOVIX-1875: `cursor` = ngăn xếp cursor trang cặp nghi trùng (useCursorPager)
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: string | undefined; selected?: string | undefined; cursor?: string | undefined } => ({
    status: str(s.status),
    selected: str(s.selected),
    cursor: str(s.cursor),
  }),
  component: featurePage('customer', 'CustomerDedupPage'),
});
/** Y6 — tổ chức mua hàng, thẩm định KYB, yêu cầu sửa hồ sơ từ Portal (BR-2.16/2.17/2.19). Nút KYB tự ẩn theo crm.kyb.* (C‑08 chờ ký). */
const ORG_TABS = new Set(['kyb', 'changes']);
const KYB_STATUSES = new Set(['DRAFT', 'SUBMITTED', 'NEED_MORE_INFO', 'APPROVED', 'REJECTED']);
const PROFILE_CHANGE_STATUSES = new Set(['PENDING_REVIEW', 'APPLIED', 'REJECTED']);
const customerOrganizationsRoute = createRoute({
  path: '/organizations',
  getParentRoute: () => customersRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'kyb' | 'changes' | undefined;
    q?: string | undefined;
    kybStatus?: KybStatus | undefined;
    changeStatus?: ProfileChangeStatus | undefined;
  } => ({
    tab: typeof s.tab === 'string' && ORG_TABS.has(s.tab) ? (s.tab as 'kyb' | 'changes') : undefined,
    q: str(s.q),
    kybStatus:
      typeof s.kybStatus === 'string' && KYB_STATUSES.has(s.kybStatus) ? (s.kybStatus as KybStatus) : undefined,
    changeStatus:
      typeof s.changeStatus === 'string' && PROFILE_CHANGE_STATUSES.has(s.changeStatus)
        ? (s.changeStatus as ProfileChangeStatus)
        : undefined,
  }),
  component: featurePage('customer', 'OrganizationsPage'),
});
const customerApprovalsRoute = createRoute({
  path: '/approvals',
  getParentRoute: () => customersRoute,
  validateSearch: (s: Record<string, unknown>): { status?: string | undefined; selected?: string | undefined } => ({
    status: str(s.status),
    selected: str(s.selected),
  }),
  component: featurePage('customer', 'CustomerApprovalPage'),
});
const customerCatalogRoute = createRoute({
  path: '/catalog',
  getParentRoute: () => customersRoute,
  validateSearch: (s: Record<string, unknown>): { section?: string | undefined } => ({ section: str(s.section) }),
  component: featurePage('customer', 'CustomerCatalogPage'),
});
// 14/10/2026 — crmTiers: màn "Hạng KH" riêng (trước chỉ là tab Phân hạng của /customers/catalog)
const customerTiersRoute = createRoute({
  path: '/tiers',
  getParentRoute: () => customersRoute,
  component: featurePage('customer', 'CustomerTiersPage'),
});
// GĐ 3.2: crmOrgAccounts, crmActivities, crmSegments, crmContracts
const customerOrgAccountsRoute = createRoute({
  path: '/org-accounts',
  getParentRoute: () => customersRoute,
  validateSearch: (s: Record<string, unknown>): { selected?: string | undefined; q?: string | undefined } => ({
    selected: str(s.selected),
    q: str(s.q),
  }),
  component: featurePage('customer', 'CustomerOrgAccountsPage'),
});
// R5.7 — crmDataAudit: ma trận truy cập dữ liệu khách theo người (audit-svc pii-access, chỉ đọc)
const CustomerDataAuditLazy = featurePage('customer', 'CustomerDataAuditPage');
const customerDataAuditRoute = createRoute({
  path: '/data-audit',
  getParentRoute: () => customersRoute,
  component: () => (
    <RequireCapability cap="admin.audit.view">
      <CustomerDataAuditLazy />
    </RequireCapability>
  ),
});
const customerActivitiesRoute = createRoute({
  path: '/activities',
  getParentRoute: () => customersRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: string | undefined;
    type?: string | undefined;
    overdue?: boolean | undefined;
    mine?: boolean | undefined;
  } => ({
    status: str(s.status),
    type: str(s.type),
    overdue: flag(s.overdue),
    mine: flag(s.mine),
  }),
  component: featurePage('customer', 'CustomerActivitiesPage'),
});
const customerSegmentsRoute = createRoute({
  path: '/segments',
  getParentRoute: () => customersRoute,
  validateSearch: (s: Record<string, unknown>): { selected?: string | undefined; create?: boolean | undefined } => ({
    selected: str(s.selected),
    create: flag(s.create),
  }),
  component: featurePage('customer', 'CustomerSegmentsPage'),
});
// ---- W6‑b: crmPipeline / crmDeal / crmCampaign / crmTickets ----
const crmPipelineRoute = createRoute({
  path: '/pipeline',
  getParentRoute: () => customersRoute,
  // T9 (yêu cầu chéo B2 SOC-5): + leadId — nút "Tạo cơ hội" ở /social/leads/$leadId điều hướng ?create=1&leadId=<id>
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: OpportunityStatus | undefined;
    source?: OpportunitySource | undefined;
    hot?: string | undefined;
    q?: string | undefined;
    create?: string | undefined;
    leadId?: string | undefined;
  } => ({
    status:
      s.status === 'OPEN' || s.status === 'WON' || s.status === 'LOST' ? (s.status as OpportunityStatus) : undefined,
    source: typeof s.source === 'string' ? (s.source as OpportunitySource) : undefined,
    hot: str(s.hot),
    q: str(s.q),
    create: str(s.create),
    leadId: str(s.leadId),
  }),
  component: featurePage('customer', 'CrmPipelinePage'),
});
const crmDealRoute = createRoute({
  path: '/pipeline/$opportunityId',
  getParentRoute: () => customersRoute,
  component: featurePage('customer', 'CrmDealPage'),
});
const crmCampaignsRoute = createRoute({
  path: '/campaigns',
  getParentRoute: () => customersRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: CampaignStatus | undefined; create?: string | undefined } => ({
    status: typeof s.status === 'string' && CAMPAIGN_STATUSES.has(s.status) ? (s.status as CampaignStatus) : undefined,
    create: str(s.create),
  }),
  component: featurePage('customer', 'CrmCampaignsPage'),
});
const crmTicketsRoute = createRoute({
  path: '/tickets',
  getParentRoute: () => customersRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { filter?: 'open' | 'breached' | 'closed' | undefined; customerId?: string | undefined } => ({
    filter: s.filter === 'open' || s.filter === 'breached' || s.filter === 'closed' ? s.filter : undefined,
    customerId: str(s.customerId),
  }),
  component: featurePage('customer', 'CustomerTicketsPage'),
});
const customerContractsRoute = createRoute({
  path: '/contracts',
  getParentRoute: () => customersRoute,
  validateSearch: (s: Record<string, unknown>): { status?: string | undefined; customerId?: string | undefined } => ({
    status: str(s.status),
    customerId: str(s.customerId),
  }),
  component: featurePage('customer', 'CustomerContractsPage'),
});
// T9-F2 (yêu cầu chéo T9A-5 A36): yêu cầu của chủ thể dữ liệu (NĐ 13/2023) — capability ĐỀ XUẤT admin.privacy_request.view (chờ ký → chỉ '*' vào được)
const PrivacyRequestsLazy = featurePage('customer', 'PrivacyRequestsPage');
const customerPrivacyRequestsRoute = createRoute({
  path: '/privacy-requests',
  getParentRoute: () => customersRoute,
  component: () => (
    <RequireCapability cap="admin.privacy_request.view">
      <PrivacyRequestsLazy />
    </RequireCapability>
  ),
});
const customerDetailRoute = createRoute({
  path: '/$customerId',
  getParentRoute: () => customersRoute,
  validateSearch: (s: Record<string, unknown>): { tab?: string | undefined } => ({ tab: str(s.tab) }),
  component: featurePage('customer', 'Customer360Page'),
});

// ---- KOVIX.Pricing (Đợt 2: price, priceLists, priceCreate/priceView/priceMatrix, pricePromo, priceCustomerView) ----
const pricingRoute = createRoute({
  path: '/pricing',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="price.price_list.view" moduleCode="price">
      <Outlet />
    </RequireCapability>
  ),
});
const priceListsRoute = createRoute({
  path: '/',
  getParentRoute: () => pricingRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: MasterDataStatus | 'ALL' | undefined;
    scopeType?: PriceScopeType | undefined;
    q?: string | undefined;
  } => ({
    status: typeof s.status === 'string' ? (s.status as MasterDataStatus | 'ALL') : undefined,
    scopeType: typeof s.scopeType === 'string' ? (s.scopeType as PriceScopeType) : undefined,
    q: str(s.q),
  }),
  component: featurePage('pricing', 'PriceListsPage'),
});
const promotionsRoute = createRoute({
  path: '/promotions',
  getParentRoute: () => pricingRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: PromotionStatus | 'ALL' | undefined;
    selected?: string | undefined;
    create?: boolean | undefined;
  } => ({
    status: typeof s.status === 'string' ? (s.status as PromotionStatus | 'ALL') : undefined,
    selected: str(s.selected),
    // mockup pricePromoCreate — "Tạo khuyến mại" từ Tổng quan mở sẵn form
    create: s.create === true || s.create === 'true' ? true : undefined,
  }),
  component: featurePage('pricing', 'PromotionsPage'),
});
const priceResolveRoute = createRoute({
  path: '/resolve',
  getParentRoute: () => pricingRoute,
  component: featurePage('pricing', 'PriceResolvePage'),
});
const pricingApprovalsRoute = createRoute({
  path: '/approvals',
  getParentRoute: () => pricingRoute,
  validateSearch: (s: Record<string, unknown>): { queue?: string | undefined } => ({ queue: str(s.queue) }),
  component: featurePage('pricing', 'PricingApprovalPage'),
});
const pricingDashboardRoute = createRoute({
  path: '/dashboard',
  getParentRoute: () => pricingRoute,
  component: featurePage('pricing', 'PricingDashboardPage'),
});
const priceListDetailRoute = createRoute({
  path: '/$priceListId',
  getParentRoute: () => pricingRoute,
  validateSearch: (s: Record<string, unknown>): { revision?: string | undefined; import?: boolean | undefined } => ({
    revision: str(s.revision),
    import: flag(s.import),
  }),
  component: featurePage('pricing', 'PriceListDetailPage'),
});
// ---- W5: priceRebate (rebate B2B theo kỳ BR-3.4), priceSpecialRequest (đề nghị giá đặc biệt BR-3.101) ----
const REBATE_TABS = new Set(['policies', 'accruals', 'settlements']);
const pricingRebatesRoute = createRoute({
  path: '/rebates',
  getParentRoute: () => pricingRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'policies' | 'accruals' | 'settlements' | undefined;
    policyStatus?: RebatePolicyStatus | undefined;
    accrualStatus?: RebateAccrualStatus | undefined;
    settlementStatus?: RebateSettlementStatus | undefined;
    policyId?: string | undefined;
    create?: string | undefined;
  } => ({
    tab:
      typeof s.tab === 'string' && REBATE_TABS.has(s.tab)
        ? (s.tab as 'policies' | 'accruals' | 'settlements')
        : undefined,
    policyStatus: typeof s.policyStatus === 'string' ? (s.policyStatus as RebatePolicyStatus) : undefined,
    accrualStatus: typeof s.accrualStatus === 'string' ? (s.accrualStatus as RebateAccrualStatus) : undefined,
    settlementStatus:
      typeof s.settlementStatus === 'string' ? (s.settlementStatus as RebateSettlementStatus) : undefined,
    policyId: str(s.policyId),
    create: str(s.create),
  }),
  component: featurePage('pricing', 'RebatePage'),
});
const pricingSpecialRequestsRoute = createRoute({
  path: '/special-requests',
  getParentRoute: () => pricingRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: SpecialPriceRequestStatus | undefined;
    customerId?: string | undefined;
    create?: string | undefined;
  } => ({
    status: typeof s.status === 'string' ? (s.status as SpecialPriceRequestStatus) : undefined,
    customerId: str(s.customerId),
    create: str(s.create),
  }),
  component: featurePage('pricing', 'SpecialPriceRequestsPage'),
});
// Q1 A7 (26/09/2026): biên tối thiểu & giá sàn (R3.4) — ghi cần price.price_list.approve (kiểm ở nút + máy chủ)
const pricingMarginsRoute = createRoute({
  path: '/margins',
  getParentRoute: () => pricingRoute,
  component: featurePage('pricing', 'PricingMarginPage'),
});

// ---- KOVIX.Credit (Đợt 2: credit, creditProfile, creditRequest, creditApproval(+Detail), creditOverride(+Detail)) ----
const creditRoute = createRoute({
  path: '/credit',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="credit.credit_profile.view" moduleCode="credit">
      <Outlet />
    </RequireCapability>
  ),
});
const creditOverviewRoute = createRoute({
  path: '/',
  getParentRoute: () => creditRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: CreditProfileStatus | 'ALL' | undefined;
    overdueOnly?: boolean | undefined;
    riskGrade?: RiskGrade | undefined;
  } => ({
    status: typeof s.status === 'string' ? (s.status as CreditProfileStatus | 'ALL') : undefined,
    overdueOnly: s.overdueOnly === true || s.overdueOnly === 'true' ? true : undefined,
    riskGrade: typeof s.riskGrade === 'string' ? (s.riskGrade as RiskGrade) : undefined,
  }),
  component: featurePage('credit', 'CreditOverviewPage'),
});
const creditApprovalsRoute = createRoute({
  path: '/approvals',
  getParentRoute: () => creditRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: CreditChangeStatus | 'ALL' | undefined;
    selected?: string | undefined;
    propose?: string | undefined;
  } => ({
    status: typeof s.status === 'string' ? (s.status as CreditChangeStatus | 'ALL') : undefined,
    selected: str(s.selected),
    propose: str(s.propose),
  }),
  component: featurePage('credit', 'CreditApprovalPage'),
});
const creditOverridesRoute = createRoute({
  path: '/overrides',
  getParentRoute: () => creditRoute,
  validateSearch: (s: Record<string, unknown>): { view?: string | undefined } => ({ view: str(s.view) }),
  component: featurePage('credit', 'CreditOverridePage'),
});
const CreditPolicyLazy = featurePage('credit', 'CreditPolicyPage');
const creditPolicyRoute = createRoute({
  path: '/policy',
  getParentRoute: () => creditRoute,
  component: () => (
    <RequireCapability cap="credit.credit_profile.approve">
      <CreditPolicyLazy />
    </RequireCapability>
  ),
});
// W4 (16/09/2026): thu hồi nợ — creditAging, creditDunning, creditInstallment(+Create), creditRecon, creditLedger
const creditAgingRoute = createRoute({
  path: '/aging',
  getParentRoute: () => creditRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    asOf?: string | undefined;
    bucket?: AgingBucket | undefined;
    view?: 'overdue' | 'dueSoon' | undefined;
    mine?: boolean | undefined;
  } => ({
    asOf: str(s.asOf),
    bucket:
      typeof s.bucket === 'string' && ['CURRENT', 'D1_30', 'D31_60', 'D61_90', 'OVER_90'].includes(s.bucket)
        ? (s.bucket as AgingBucket)
        : undefined,
    view: s.view === 'overdue' || s.view === 'dueSoon' ? s.view : undefined,
    mine: flag(s.mine),
  }),
  component: featurePage('credit', 'CreditAgingPage'),
});
const creditDunningRoute = createRoute({
  path: '/dunning',
  getParentRoute: () => creditRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: DunningRunStatus | undefined;
    runId?: string | undefined;
    create?: boolean | undefined;
    bucket?: AgingBucket | undefined;
  } => ({
    status:
      typeof s.status === 'string' && ['DRAFT', 'SCHEDULED', 'RUNNING', 'DONE', 'CANCELLED'].includes(s.status)
        ? (s.status as DunningRunStatus)
        : undefined,
    runId: str(s.runId),
    create: flag(s.create),
    bucket:
      typeof s.bucket === 'string' && ['CURRENT', 'D1_30', 'D31_60', 'D61_90', 'OVER_90'].includes(s.bucket)
        ? (s.bucket as AgingBucket)
        : undefined,
  }),
  component: featurePage('credit', 'CreditDunningPage'),
});
const creditInstallmentsRoute = createRoute({
  path: '/installments',
  getParentRoute: () => creditRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: InstallmentPlanStatus | undefined;
    customerId?: string | undefined;
    planId?: string | undefined;
    create?: boolean | undefined;
  } => ({
    status:
      typeof s.status === 'string' &&
      ['DRAFT', 'PENDING_APPROVAL', 'ACTIVE', 'COMPLETED', 'DEFAULTED', 'CANCELLED'].includes(s.status)
        ? (s.status as InstallmentPlanStatus)
        : undefined,
    customerId: str(s.customerId),
    planId: str(s.planId),
    create: flag(s.create),
  }),
  component: featurePage('credit', 'CreditInstallmentPage'),
});
const creditReconRoute = createRoute({
  path: '/reconciliation',
  getParentRoute: () => creditRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: StatementStatus | undefined;
    customerId?: string | undefined;
    period?: string | undefined;
    statementId?: string | undefined;
    create?: boolean | undefined;
  } => ({
    status:
      typeof s.status === 'string' && ['DRAFT', 'SENT', 'CONFIRMED', 'VARIANCE', 'CLOSED'].includes(s.status)
        ? (s.status as StatementStatus)
        : undefined,
    customerId: str(s.customerId),
    period: str(s.period),
    statementId: str(s.statementId),
    create: flag(s.create),
  }),
  component: featurePage('credit', 'CreditReconPage'),
});
const creditLedgerRoute = createRoute({
  path: '/ledger',
  getParentRoute: () => creditRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { range?: 'today' | 'week' | 'month' | undefined; receiptId?: string | undefined } => ({
    range: s.range === 'today' || s.range === 'week' || s.range === 'month' ? s.range : undefined,
    receiptId: str(s.receiptId),
  }),
  component: featurePage('credit', 'CreditLedgerPage'),
});
const creditProfileRoute = createRoute({
  path: '/$customerId',
  getParentRoute: () => creditRoute,
  validateSearch: (s: Record<string, unknown>): { tab?: string | undefined } => ({ tab: str(s.tab) }),
  component: featurePage('credit', 'CreditProfilePage'),
});

// ---- KOVIX.BPM (Đợt 2: workInbox, tnBPM/adminApprovalFlows, bpmTemplateCatalog/PolicyEnvelope/Versions/Bindings, bpmRuntime, bpmDelegation) — dưới /admin/bpm ----
const bpmGuard = (children: ReactNode) => (
  <RequireCapability cap="admin.bpm.view" moduleCode="admin">
    {children}
  </RequireCapability>
);
const BpmCenterLazy = featurePage('bpm', 'BpmCenterPage');
const BpmTemplateLazy = featurePage('bpm', 'BpmTemplatePage');
const BpmRuntimeLazy = featurePage('bpm', 'BpmRuntimePage');
const BpmDelegationsLazy = featurePage('bpm', 'BpmDelegationsPage');
const bpmCenterRoute = createRoute({
  path: '/admin/bpm',
  getParentRoute: () => shellRoute,
  validateSearch: (s: Record<string, unknown>): { module?: string | undefined } => ({ module: str(s.module) }),
  component: () => bpmGuard(<BpmCenterLazy />),
});
const bpmRuntimeRoute = createRoute({
  path: '/admin/bpm/runtime',
  getParentRoute: () => shellRoute,
  validateSearch: (s: Record<string, unknown>): { status?: string | undefined; selected?: string | undefined } => ({
    status: str(s.status),
    selected: str(s.selected),
  }),
  component: () => bpmGuard(<BpmRuntimeLazy />),
});
const bpmDelegationsRoute = createRoute({
  path: '/admin/bpm/delegations',
  getParentRoute: () => shellRoute,
  component: () => bpmGuard(<BpmDelegationsLazy />),
});
// W12 — nhật ký phê duyệt (approvalHistory), ma trận tenant (bpmTenantMatrix), mô phỏng (bpmSimulation): khai TRƯỚC /$templateId
const BpmHistoryLazy = featurePage('bpm', 'BpmHistoryPage');
const BpmMatrixLazy = featurePage('bpm', 'BpmMatrixPage');
const BpmSimulationLazy = featurePage('bpm', 'BpmSimulationPage');
const bpmHistoryRoute = createRoute({
  path: '/admin/bpm/history',
  getParentRoute: () => shellRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { outcome?: string | undefined; module?: string | undefined; q?: string | undefined } => ({
    outcome: str(s.outcome),
    module: str(s.module),
    q: str(s.q),
  }),
  component: () => bpmGuard(<BpmHistoryLazy />),
});
const bpmMatrixRoute = createRoute({
  path: '/admin/bpm/matrix',
  getParentRoute: () => shellRoute,
  component: () => bpmGuard(<BpmMatrixLazy />),
});
const bpmSimulationRoute = createRoute({
  path: '/admin/bpm/simulate',
  getParentRoute: () => shellRoute,
  validateSearch: (s: Record<string, unknown>): { templateId?: string | undefined; amount?: string | undefined } => ({
    templateId: str(s.templateId),
    // KOVIX-2446 (BR-BPM-2): link ?amount=600000000 / -5 / 1e400 được TanStack JSON-parse thành SỐ (1e400 → Infinity) → str() bỏ mất,
    // trang không mô phỏng theo giá trị và không báo "Giá trị định tuyến không hợp lệ". Nhận cả số (String(Infinity) vẫn là không hợp lệ ở trang).
    amount: typeof s.amount === 'number' ? String(s.amount) : str(s.amount),
  }),
  component: () => bpmGuard(<BpmSimulationLazy />),
});
// X0‑e — BPM Builder kéo-thả (mockup bpmBuilder): khai TRƯỚC /$templateId
const BpmBuilderLazy = featurePage('bpm', 'BpmBuilderPage');
const bpmBuilderRoute = createRoute({
  path: '/admin/bpm/builder',
  getParentRoute: () => shellRoute,
  validateSearch: (s: Record<string, unknown>): { templateId?: string | undefined; tab?: string | undefined } => ({
    templateId: str(s.templateId),
    tab: str(s.tab),
  }),
  component: () => bpmGuard(<BpmBuilderLazy />),
});
const bpmTemplateRoute = createRoute({
  path: '/admin/bpm/$templateId',
  getParentRoute: () => shellRoute,
  component: () => bpmGuard(<BpmTemplateLazy />),
});
// W12 — hồ sơ nghiệm thu: độ phủ mockup (taBRDCoverage) + chuẩn chức năng & BR ↔ test (spiStandardsAudit / lomStandardsAudit)
const BrdCoverageLazy = featurePage('admin', 'BrdCoveragePage');
const StandardsAuditLazy = featurePage('admin', 'StandardsAuditPage');
const adminBrdCoverageRoute = createRoute({
  path: '/admin/brd-coverage',
  getParentRoute: () => shellRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { module?: string | undefined; status?: string | undefined; q?: string | undefined } => ({
    module: str(s.module),
    status: str(s.status),
    q: str(s.q),
  }),
  component: () => (
    <RequireCapability cap="admin.tenant.overview" moduleCode="admin">
      <BrdCoverageLazy />
    </RequireCapability>
  ),
});
const adminStandardsAuditRoute = createRoute({
  path: '/admin/standards-audit',
  getParentRoute: () => shellRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    group?: 'spi' | 'lom' | 'all' | undefined;
    brGroup?: string | undefined;
    status?: string | undefined;
    q?: string | undefined;
  } => ({
    group: s.group === 'spi' || s.group === 'lom' || s.group === 'all' ? s.group : undefined,
    brGroup: str(s.brGroup),
    status: str(s.status),
    q: str(s.q),
  }),
  component: () => (
    <RequireCapability cap="admin.tenant.overview" moduleCode="admin">
      <StandardsAuditLazy />
    </RequireCapability>
  ),
});

// ---- KOVIX.Inventory (Đợt 3: inventory — kho/tồn, phiếu kho, soạn hàng FEFO, ATP, lô/HSD; slice 2: điều chuyển, kiểm kê, cảnh báo) ----
const VOUCHER_TYPES = new Set(['RECEIPT', 'ISSUE', 'ADJUST', 'SCRAP', 'TRANSFER']);
const VOUCHER_STATUSES = new Set(['DRAFT', 'PENDING_APPROVAL', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED']);
const inventoryRoute = createRoute({
  path: '/inventory',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="inventory.stock.view" moduleCode="inventory">
      <Outlet />
    </RequireCapability>
  ),
});
const inventoryOverviewRoute = createRoute({
  path: '/',
  getParentRoute: () => inventoryRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { warehouseId?: string | undefined; productId?: string | undefined } => ({
    warehouseId: str(s.warehouseId),
    productId: str(s.productId),
  }),
  component: featurePage('inventory', 'InventoryOverviewPage'),
});
const inventoryVouchersRoute = createRoute({
  path: '/vouchers',
  getParentRoute: () => inventoryRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { type?: VoucherType | undefined; status?: VoucherStatus | undefined; warehouseId?: string | undefined } => ({
    type: typeof s.type === 'string' && VOUCHER_TYPES.has(s.type) ? (s.type as VoucherType) : undefined,
    status: typeof s.status === 'string' && VOUCHER_STATUSES.has(s.status) ? (s.status as VoucherStatus) : undefined,
    warehouseId: str(s.warehouseId),
  }),
  component: featurePage('inventory', 'VouchersPage'),
});
const inventoryVoucherDetailRoute = createRoute({
  path: '/vouchers/$voucherId',
  getParentRoute: () => inventoryRoute,
  component: featurePage('inventory', 'VoucherDetailPage'),
});
// 15/10/2026 (người dùng chốt) — Kho → Đơn cần xử lý: đơn vượt tồn / thiếu hàng / chờ soạn (bản đọc inventory-svc); orderId = mở từ thông báo
const OrderQueueLazy = featurePage('inventory', 'OrderQueuePage');
const ORDER_QUEUE_STATUSES = new Set(['STOCK_HOLD', 'BACKORDER', 'AWAITING_PICK', 'CLOSED']);
const inventoryOrderQueueRoute = createRoute({
  path: '/orders',
  getParentRoute: () => inventoryRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: OrderQueueStatus | undefined;
    warehouseId?: string | undefined;
    q?: string | undefined;
    orderId?: string | undefined;
  } => ({
    status:
      typeof s.status === 'string' && ORDER_QUEUE_STATUSES.has(s.status) ? (s.status as OrderQueueStatus) : undefined,
    warehouseId: str(s.warehouseId),
    q: str(s.q),
    orderId: str(s.orderId),
  }),
  component: () => (
    <RequireCapability cap="inventory.stock.view">
      <OrderQueueLazy />
    </RequireCapability>
  ),
});
const inventoryPicksRoute = createRoute({
  path: '/picks',
  getParentRoute: () => inventoryRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: VoucherStatus | undefined;
    warehouseId?: string | undefined;
    productId?: string | undefined;
    to?: string | undefined;
    days?: number | undefined;
  } => ({
    status: typeof s.status === 'string' && VOUCHER_STATUSES.has(s.status) ? (s.status as VoucherStatus) : undefined,
    warehouseId: str(s.warehouseId),
    // Bug KOVIX-4167 (RPT-AGE-09 ô Vi phạm FEFO): nhiệm vụ của SKU, hoàn tất trong `days` ngày tới `to` (ngày chốt)
    productId: str(s.productId),
    to: typeof s.to === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.to) ? s.to : undefined,
    days: Number.isInteger(Number(s.days)) && Number(s.days) > 0 && Number(s.days) <= 366 ? Number(s.days) : undefined,
  }),
  component: featurePage('inventory', 'PickTasksPage'),
});
const inventoryPickDetailRoute = createRoute({
  path: '/picks/$taskId',
  getParentRoute: () => inventoryRoute,
  component: featurePage('inventory', 'PickTaskPage'),
});
const inventoryAtpRoute = createRoute({
  path: '/atp',
  getParentRoute: () => inventoryRoute,
  validateSearch: (s: Record<string, unknown>): { productId?: string | undefined; orderId?: string | undefined } => ({
    productId: str(s.productId),
    orderId: str(s.orderId),
  }),
  component: featurePage('inventory', 'AtpCheckPage'),
});
const inventoryLotsRoute = createRoute({
  path: '/lots',
  getParentRoute: () => inventoryRoute,
  validateSearch: (s: Record<string, unknown>): { days?: number | undefined; productId?: string | undefined } => ({
    days:
      typeof s.days === 'number'
        ? s.days
        : typeof s.days === 'string' && /^\d+$/.test(s.days)
          ? Number(s.days)
          : undefined,
    productId: str(s.productId),
  }),
  component: featurePage('inventory', 'ExpiringLotsPage'),
});
const inventoryWarehouseRoute = createRoute({
  path: '/warehouses/$warehouseId',
  getParentRoute: () => inventoryRoute,
  validateSearch: (s: Record<string, unknown>): { tab?: 'stock' | 'bins' | 'ledger' | 'temperature' | undefined } => ({
    tab:
      s.tab === 'bins' || s.tab === 'ledger' || s.tab === 'temperature'
        ? s.tab
        : s.tab === 'stock'
          ? 'stock'
          : undefined,
  }),
  component: featurePage('inventory', 'WarehouseDetailPage'),
});

const inventoryTransfersRoute = createRoute({
  path: '/transfers',
  getParentRoute: () => inventoryRoute,
  // R6.5: to/productId/qty = điền sẵn phiếu điều chuyển từ gợi ý backorder của đơn (warehouseId = kho nguồn)
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: VoucherStatus | undefined;
    warehouseId?: string | undefined;
    to?: string | undefined;
    productId?: string | undefined;
    qty?: number | undefined;
  } => ({
    status: typeof s.status === 'string' && VOUCHER_STATUSES.has(s.status) ? (s.status as VoucherStatus) : undefined,
    warehouseId: str(s.warehouseId),
    to: str(s.to),
    productId: str(s.productId),
    qty:
      typeof s.qty === 'number' && s.qty > 0
        ? s.qty
        : typeof s.qty === 'string' && /^\d+(\.\d+)?$/.test(s.qty)
          ? Number(s.qty)
          : undefined,
  }),
  component: featurePage('inventory', 'TransfersPage'),
});
const inventoryTransferDetailRoute = createRoute({
  path: '/transfers/$transferId',
  getParentRoute: () => inventoryRoute,
  component: featurePage('inventory', 'TransferDetailPage'),
});
const inventoryCountsRoute = createRoute({
  path: '/counts',
  getParentRoute: () => inventoryRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: VoucherStatus | undefined; warehouseId?: string | undefined } => ({
    status: typeof s.status === 'string' && VOUCHER_STATUSES.has(s.status) ? (s.status as VoucherStatus) : undefined,
    warehouseId: str(s.warehouseId),
  }),
  component: featurePage('inventory', 'CountsPage'),
});
const inventoryCountDetailRoute = createRoute({
  path: '/counts/$countId',
  getParentRoute: () => inventoryRoute,
  component: featurePage('inventory', 'CountDetailPage'),
});
const ALERT_STATUSES = new Set(['OPEN', 'ACKNOWLEDGED', 'RESOLVED']);
const ALERT_TYPES = new Set(['BELOW_MIN', 'ABOVE_MAX', 'NEAR_EXPIRY', 'CAPACITY', 'WO_RESERVATION_OVERDUE']);
const inventoryAlertsRoute = createRoute({
  path: '/alerts',
  getParentRoute: () => inventoryRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'alerts' | 'rules' | undefined;
    status?: AlertStatus | undefined;
    type?: AlertType | undefined;
    warehouseId?: string | undefined;
  } => ({
    tab: s.tab === 'rules' ? 'rules' : s.tab === 'alerts' ? 'alerts' : undefined,
    status: typeof s.status === 'string' && ALERT_STATUSES.has(s.status) ? (s.status as AlertStatus) : undefined,
    type: typeof s.type === 'string' && ALERT_TYPES.has(s.type) ? (s.type as AlertType) : undefined,
    warehouseId: str(s.warehouseId),
  }),
  component: featurePage('inventory', 'AlertsPage'),
});

const inventoryLotDetailRoute = createRoute({
  path: '/lots/$lotId',
  getParentRoute: () => inventoryRoute,
  component: featurePage('inventory', 'LotDetailPage'),
});
const inventoryDashboardRoute = createRoute({
  path: '/dashboard',
  getParentRoute: () => inventoryRoute,
  component: featurePage('inventory', 'InventoryDashboardPage'),
});
const MOVEMENT_TYPES = new Set([
  'RECEIPT',
  'ISSUE',
  'TRANSFER_IN',
  'TRANSFER_OUT',
  'ADJUST',
  'COUNT',
  'SCRAP',
  'PRODUCTION_IN',
  'PRODUCTION_OUT',
  'RETURN_IN',
]);
const inventoryWastageRoute = createRoute({
  path: '/wastage',
  getParentRoute: () => inventoryRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'waste' | 'transfer' | 'catch' | undefined;
    type?: MovementType | undefined;
    warehouseId?: string | undefined;
    from?: string | undefined;
    to?: string | undefined;
    productId?: string | undefined;
    reason?: string | undefined;
    loss?: 'ADJUST_DOWN' | 'ADJUST_UP' | undefined;
  } => ({
    tab: s.tab === 'transfer' || s.tab === 'catch' ? s.tab : s.tab === 'waste' ? 'waste' : undefined,
    type: typeof s.type === 'string' && MOVEMENT_TYPES.has(s.type) ? (s.type as MovementType) : undefined,
    warehouseId: str(s.warehouseId),
    from: str(s.from),
    to: str(s.to),
    // Bug KOVIX-4167 (RPT-WASTE-10 ô hủy/điều chỉnh): lọc SKU, lý do (mã lý do phiếu / COUNT_SHORT / COUNT_OVER / UNSPECIFIED), điều chỉnh giảm/tăng
    productId: str(s.productId),
    reason: typeof s.reason === 'string' && /^[A-Z0-9_]{1,64}$/.test(s.reason) ? s.reason : undefined,
    loss: s.loss === 'ADJUST_DOWN' || s.loss === 'ADJUST_UP' ? s.loss : undefined,
  }),
  component: featurePage('inventory', 'WastagePage'),
});
const inventoryLabelsRoute = createRoute({
  path: '/labels',
  getParentRoute: () => inventoryRoute,
  validateSearch: (s: Record<string, unknown>): { productId?: string | undefined } => ({ productId: str(s.productId) }),
  component: featurePage('inventory', 'LabelPrintPage'),
});
// R6.14 (M-65): kho xe DMS — tồn trên xe, nhận/trả hàng, lịch sử (xe = kho VAN)
const inventoryVansRoute = createRoute({
  path: '/vans',
  getParentRoute: () => inventoryRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { vanId?: string | undefined; tab?: 'stock' | 'movements' | undefined } => ({
    vanId: str(s.vanId),
    tab: s.tab === 'movements' ? 'movements' : s.tab === 'stock' ? 'stock' : undefined,
  }),
  component: featurePage('inventory', 'VanStockPage'),
});
// GĐ 3.5: tìm kiếm SKU & định vị tồn / quét mã (BR-7.102)
const inventorySearchRoute = createRoute({
  path: '/search',
  getParentRoute: () => inventoryRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { q?: string | undefined; warehouseId?: string | undefined; exact?: true | undefined } => ({
    q: str(s.q),
    warehouseId: str(s.warehouseId),
    exact: s.exact === true || s.exact === 'true' ? true : undefined,
  }),
  component: featurePage('inventory', 'StockSearchPage'),
});
// T4-E (T4-C inventory-v1 1.7.0, M-252) 💰 — hàng RMA cách ly chờ QA; route KHÔNG phải view mockup (không có trong routes.json — vào từ /inventory và chi tiết RMA)
const inventoryRmaQuarantinesRoute = createRoute({
  path: '/rma-quarantines',
  getParentRoute: () => inventoryRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: 'HELD' | 'RELEASED' | 'SCRAP_PENDING' | 'SCRAPPED' | 'RETURNED_TO_CUSTOMER' | undefined;
    rmaId?: string | undefined;
  } => ({
    status:
      s.status === 'HELD' ||
      s.status === 'RELEASED' ||
      s.status === 'SCRAP_PENDING' ||
      s.status === 'SCRAPPED' ||
      s.status === 'RETURNED_TO_CUSTOMER'
        ? s.status
        : undefined,
    rmaId: str(s.rmaId),
  }),
  component: featurePage('inventory', 'RmaQuarantinesPage'),
});
// Tồn đọng N (inventory-v1 1.8.4, báo cáo L-3) — rà tồn nhập đôi hoàn kho, CHỈ ĐỌC; route KHÔNG phải view mockup (không có trong routes.json — vào từ /inventory)
const InventoryDeliveryReturnDuplicatesLazy = featurePage('inventory', 'DeliveryReturnDuplicatesPage');
const inventoryDeliveryReturnDuplicatesRoute = createRoute({
  path: '/delivery-return-duplicates',
  getParentRoute: () => inventoryRoute,
  validateSearch: (s: Record<string, unknown>): { warehouseId?: string | undefined; before?: string | undefined } => ({
    warehouseId: str(s.warehouseId),
    before: str(s.before),
  }),
  component: () => (
    <RequireCapability cap="inventory.stock.view" moduleCode="inventory">
      <InventoryDeliveryReturnDuplicatesLazy />
    </RequireCapability>
  ),
});

// ---- KOVIX.Order (Đợt 3: sales — đơn hàng, checkout 3 cửa kiểm, phân bổ; slice 2: báo giá, hợp đồng, định kỳ, trả hàng) ----
const ORDER_STATUSES = new Set([
  'DRAFT',
  'PENDING_CHECK',
  'PENDING_APPROVAL',
  'AWAITING_PAYMENT',
  'CONFIRMED',
  'ALLOCATED',
  'PICKING',
  'DELIVERING',
  'COMPLETED',
  'CANCELLED',
  'RETURNED',
  'DELIVERY_FAILED',
]);
const ordersRoute = createRoute({
  path: '/orders',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="sales.sales_order.view" moduleCode="sales">
      <Outlet />
    </RequireCapability>
  ),
});
const orderListRoute = createRoute({
  path: '/',
  getParentRoute: () => ordersRoute,
  // Q3-e bộ lọc nâng cao: kênh, thanh toán, đơn vị (chi nhánh), khoảng ngày tạo
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: OrderStatus | undefined;
    customerId?: string | undefined;
    q?: string | undefined;
    channel?: import('@kovix/api-client').OrderChannel | undefined;
    paymentMethod?: import('@kovix/api-client').OrderPaymentMethod | undefined;
    orgUnitId?: string | undefined;
    from?: string | undefined;
    to?: string | undefined;
    cur?: string | undefined;
  } => ({
    status: typeof s.status === 'string' && ORDER_STATUSES.has(s.status) ? (s.status as OrderStatus) : undefined,
    customerId: str(s.customerId),
    q: str(s.q),
    cur: str(s.cur), // T6-B #3 G1-O2 — ngăn xếp cursor (useCursorPager)
    channel:
      typeof s.channel === 'string' && Object.hasOwn(ORDER_CHANNEL_LABEL, s.channel)
        ? (s.channel as import('@kovix/api-client').OrderChannel)
        : undefined,
    paymentMethod:
      typeof s.paymentMethod === 'string' && Object.hasOwn(PAYMENT_METHOD_LABEL, s.paymentMethod)
        ? (s.paymentMethod as import('@kovix/api-client').OrderPaymentMethod)
        : undefined,
    orgUnitId: str(s.orgUnitId),
    from: typeof s.from === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.from) ? s.from : undefined,
    to: typeof s.to === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.to) ? s.to : undefined,
  }),
  component: featurePage('order', 'OrdersPage'),
});
const OrderCreateLazy = featurePage('order', 'OrderCreatePage');
const orderCreateRoute = createRoute({
  path: '/new',
  getParentRoute: () => ordersRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { customerId?: string | undefined; opportunityId?: string | undefined } => ({
    customerId: str(s.customerId),
    opportunityId: str(s.opportunityId),
  }), // W6‑b: từ cơ hội CRM
  component: () => (
    <RequireCapability cap="sales.sales_order.create">
      <OrderCreateLazy />
    </RequireCapability>
  ),
});
// GĐ 3.3: đặt nhanh, yêu cầu thay đổi sau xác nhận (BR-5.101), RFQ (BR-5.103)
const QuickOrderLazy = featurePage('order', 'QuickOrderPage');
const orderQuickRoute = createRoute({
  path: '/quick',
  getParentRoute: () => ordersRoute,
  validateSearch: (s: Record<string, unknown>): { customerId?: string | undefined } => ({
    customerId: str(s.customerId),
  }),
  component: () => (
    <RequireCapability cap="sales.sales_order.create">
      <QuickOrderLazy />
    </RequireCapability>
  ),
});
const orderChangesRoute = createRoute({
  path: '/changes',
  getParentRoute: () => ordersRoute,
  validateSearch: (s: Record<string, unknown>): { status?: OrderChangeRequestStatus | undefined } => ({
    status:
      typeof s.status === 'string' && CHANGE_STATUSES.has(s.status)
        ? (s.status as OrderChangeRequestStatus)
        : undefined,
  }),
  component: featurePage('order', 'ChangeRequestsPage'),
});
const orderRfqRoute = createRoute({
  path: '/rfq',
  getParentRoute: () => ordersRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: QuoteRequestStatus | undefined; customerId?: string | undefined; requestId?: string | undefined } => ({
    status: typeof s.status === 'string' && RFQ_STATUSES.has(s.status) ? (s.status as QuoteRequestStatus) : undefined,
    customerId: str(s.customerId),
    requestId: str(s.requestId),
  }),
  component: featurePage('order', 'QuoteRequestsPage'),
});
const orderAllocationsRoute = createRoute({
  path: '/allocations',
  getParentRoute: () => ordersRoute,
  validateSearch: (s: Record<string, unknown>): { productId?: string | undefined } => ({ productId: str(s.productId) }),
  component: featurePage('order', 'AllocationPage'),
});
const QUOTE_STATUSES = new Set([
  'DRAFT',
  'PENDING_APPROVAL',
  'APPROVED',
  'SENT',
  'ACCEPTED',
  'EXPIRED',
  'REJECTED',
  'CONVERTED',
]);
const CONTRACT_STATUSES = new Set(['DRAFT', 'PENDING_APPROVAL', 'ACTIVE', 'SUSPENDED', 'EXPIRED', 'TERMINATED']);
const REQUEST_STATUSES = new Set(['PENDING_DRAFTING', 'DRAFTING', 'SENT', 'SIGNED', 'REJECTED']);
const PLAN_STATUSES = new Set([
  'DRAFT',
  'PENDING_APPROVAL',
  'PENDING_TENANT_APPROVAL',
  'ACTIVE',
  'PAUSED',
  'COMPLETED',
  'CANCELLED',
]);
const CHANGE_STATUSES = new Set(['PENDING', 'APPROVED', 'REJECTED', 'APPLIED', 'CANCELLED']);
const RFQ_STATUSES = new Set(['PENDING', 'DRAFTING', 'QUOTED', 'REJECTED', 'CANCELLED']);
const RETURN_STATUSES = new Set([
  'DRAFT',
  'PENDING_APPROVAL',
  'APPROVED',
  'RECEIVED',
  'REFUNDED',
  'REJECTED',
  'CANCELLED',
]);
const orderQuotesRoute = createRoute({
  path: '/quotes',
  getParentRoute: () => ordersRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: QuoteStatus | undefined; customerId?: string | undefined } => ({
    status: typeof s.status === 'string' && QUOTE_STATUSES.has(s.status) ? (s.status as QuoteStatus) : undefined,
    customerId: str(s.customerId),
  }),
  component: featurePage('order', 'QuotesPage'),
});
/** W5 BR-3.102 — hàng đợi phê duyệt báo giá (mockup priceQuoteApprovalQueue). Khai báo TRƯỚC /quotes/$quoteId để không bị bắt làm quoteId. */
const orderQuoteApprovalsRoute = createRoute({
  path: '/quotes/approvals',
  getParentRoute: () => ordersRoute,
  component: featurePage('order', 'QuoteApprovalQueuePage'),
});
const orderQuoteDetailRoute = createRoute({
  path: '/quotes/$quoteId',
  getParentRoute: () => ordersRoute,
  component: featurePage('order', 'QuoteDetailPage'),
});
const orderContractsRoute = createRoute({
  path: '/contracts',
  getParentRoute: () => ordersRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'contracts' | 'requests' | undefined;
    status?: ContractStatus | undefined;
    reqStatus?: ContractRequestStatus | undefined;
    customerId?: string | undefined;
  } => ({
    tab: s.tab === 'requests' ? 'requests' : s.tab === 'contracts' ? 'contracts' : undefined,
    status: typeof s.status === 'string' && CONTRACT_STATUSES.has(s.status) ? (s.status as ContractStatus) : undefined,
    reqStatus:
      typeof s.reqStatus === 'string' && REQUEST_STATUSES.has(s.reqStatus)
        ? (s.reqStatus as ContractRequestStatus)
        : undefined,
    customerId: str(s.customerId),
  }),
  component: featurePage('order', 'ContractsPage'),
});
const orderContractDetailRoute = createRoute({
  path: '/contracts/$contractId',
  getParentRoute: () => ordersRoute,
  component: featurePage('order', 'ContractDetailPage'),
});
const orderRecurringRoute = createRoute({
  path: '/recurring',
  getParentRoute: () => ordersRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: RecurringPlanStatus | undefined; customerId?: string | undefined } => ({
    status: typeof s.status === 'string' && PLAN_STATUSES.has(s.status) ? (s.status as RecurringPlanStatus) : undefined,
    customerId: str(s.customerId),
  }),
  component: featurePage('order', 'RecurringPlansPage'),
});
/** Y6 BR-5.25 — lịch định kỳ do khách gửi từ Portal, chờ công ty duyệt. Khai báo TRƯỚC /recurring/$planId để không bị bắt làm planId. */
const orderRecurringApprovalsRoute = createRoute({
  path: '/recurring/approvals',
  getParentRoute: () => ordersRoute,
  component: featurePage('order', 'CustomerPlanApprovalsPage'),
});
/** Y6 — giỏ hàng theo workspace của khách (chỉ đọc; ghi là việc của Portal). */
const orderCartsRoute = createRoute({
  path: '/carts',
  getParentRoute: () => ordersRoute,
  validateSearch: (s: Record<string, unknown>): { customerId?: string | undefined; cartId?: string | undefined } => ({
    customerId: str(s.customerId),
    cartId: str(s.cartId),
  }),
  component: featurePage('order', 'CartsPage'),
});
const orderRecurringDetailRoute = createRoute({
  path: '/recurring/$planId',
  getParentRoute: () => ordersRoute,
  component: featurePage('order', 'RecurringPlanDetailPage'),
});
const orderReturnsRoute = createRoute({
  path: '/returns',
  getParentRoute: () => ordersRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: SalesReturnStatus | undefined; orderId?: string | undefined } => ({
    status: typeof s.status === 'string' && RETURN_STATUSES.has(s.status) ? (s.status as SalesReturnStatus) : undefined,
    orderId: str(s.orderId),
  }),
  component: featurePage('order', 'ReturnsPage'),
});
const orderReturnDetailRoute = createRoute({
  path: '/returns/$returnId',
  getParentRoute: () => ordersRoute,
  component: featurePage('order', 'ReturnDetailPage'),
});
const orderApprovalsRoute = createRoute({
  path: '/approvals',
  getParentRoute: () => ordersRoute,
  // Q3-e: tab "Nhật ký duyệt" (history) + bộ lọc hành động/ngày
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'queue' | 'history' | undefined;
    action?: 'APPROVE' | 'REJECT' | undefined;
    from?: string | undefined;
    to?: string | undefined;
  } => ({
    tab: s.tab === 'history' ? 'history' : s.tab === 'queue' ? 'queue' : undefined,
    action: s.action === 'APPROVE' || s.action === 'REJECT' ? s.action : undefined,
    from: typeof s.from === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.from) ? s.from : undefined,
    to: typeof s.to === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.to) ? s.to : undefined,
  }),
  component: featurePage('order', 'ApprovalsPage'),
});
const orderFulfillmentRoute = createRoute({
  path: '/fulfillment',
  getParentRoute: () => ordersRoute,
  validateSearch: (s: Record<string, unknown>): { status?: OrderStatus | undefined } => ({
    status: typeof s.status === 'string' && ORDER_STATUSES.has(s.status) ? (s.status as OrderStatus) : undefined,
  }),
  component: featurePage('order', 'FulfillmentPage'),
});
const HOLD_TYPES = new Set(['CREDIT', 'STOCK', 'APPROVAL', 'QUALITY', 'PRICING', 'OPTIONS']);
const orderExceptionsRoute = createRoute({
  path: '/exceptions',
  getParentRoute: () => ordersRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    kind?: 'hold' | 'backorder' | 'stale' | undefined;
    holdType?: HoldType | undefined;
    customerId?: string | undefined;
    from?: string | undefined;
    to?: string | undefined;
  } => ({
    kind: s.kind === 'backorder' || s.kind === 'stale' ? s.kind : s.kind === 'hold' ? 'hold' : undefined,
    holdType: typeof s.holdType === 'string' && HOLD_TYPES.has(s.holdType) ? (s.holdType as HoldType) : undefined,
    // V23 RPT-CRD-32 (TC-RPT-CRD-32-DRL B6): ô "Lần bị chặn" → đơn bị giữ do tín dụng của KHÁCH trong KỲ báo cáo
    customerId: str(s.customerId),
    from: str(s.from),
    to: str(s.to),
  }),
  component: featurePage('order', 'ExceptionsPage'),
});
const orderDashboardRoute = createRoute({
  path: '/dashboard',
  getParentRoute: () => ordersRoute,
  component: featurePage('order', 'OrderDashboardPage'),
});
const OrderImportLazy = featurePage('order', 'OrderImportPage');
const orderImportRoute = createRoute({
  path: '/import',
  getParentRoute: () => ordersRoute,
  component: () => (
    <RequireCapability cap="sales.sales_order.create">
      <OrderImportLazy />
    </RequireCapability>
  ),
});
const ORDER_DETAIL_TABS = new Set(['overview', 'lines', 'delivery', 'payment', 'history']);
const orderDetailRoute = createRoute({
  path: '/$orderId',
  getParentRoute: () => ordersRoute,
  // T6-B V-CM-5 — tab chi tiết đơn trên URL (Tổng quan · Sản phẩm · Giao nhận · Giá & thanh toán · Nhật ký)
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'overview' | 'lines' | 'delivery' | 'payment' | 'history' | undefined } => ({
    tab:
      typeof s.tab === 'string' && ORDER_DETAIL_TABS.has(s.tab)
        ? (s.tab as 'overview' | 'lines' | 'delivery' | 'payment' | 'history')
        : undefined,
  }),
  component: featurePage('order', 'OrderDetailPage'),
});
// T3-F1 #19 (G1-O3): sửa đơn = PUT /orders/{id} `sales.sales_order.update` — deep-link trái quyền → AccessDenied, không mở form rồi 403
const OrderEditLazy = featurePage('order', 'OrderEditPage');
const orderEditRoute = createRoute({
  path: '/$orderId/edit',
  getParentRoute: () => ordersRoute,
  component: () => (
    <RequireCapability cap="sales.sales_order.update">
      <OrderEditLazy />
    </RequireCapability>
  ),
});

// ---- KOVIX.Supply (Đợt 4: purchase — NCC, PR, PO, GRN + QC, hóa đơn & đối chiếu ba chiều, landed cost, thu mua nông sản) ----
const SUPPLIER_TYPES = new Set(['MANUFACTURER', 'DISTRIBUTOR', 'HOUSEHOLD', 'FARMER', 'SERVICE']);
const PR_STATUSES = new Set(['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED', 'CONVERTED', 'CANCELLED']);
const PR_SOURCES = new Set(['MANUAL', 'REORDER', 'MRP', 'BACKORDER']);
const PO_STATUSES = new Set([
  'DRAFT',
  'PENDING_APPROVAL',
  'SENT',
  'PARTIALLY_RECEIVED',
  'RECEIVED',
  'CLOSED',
  'CANCELLED',
]);
const RECEIPT_STATUSES = new Set(['DRAFT', 'PENDING_APPROVAL', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED']);
const MATCH_STATUSES = new Set(['MATCHED', 'VARIANCE', 'OVERRIDDEN']);
const INVOICE_STATUSES = new Set(['REGISTERED', 'MATCHED', 'VARIANCE', 'APPROVED', 'CANCELLED']);
const SLIP_STATUSES = new Set(['DRAFT', 'CONFIRMED', 'CANCELLED']);
const supplyRoute = createRoute({
  path: '/supply',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="purchase.purchase_order.view" moduleCode="purchase">
      <Outlet />
    </RequireCapability>
  ),
});
const supplyOverviewRoute = createRoute({
  path: '/',
  getParentRoute: () => supplyRoute,
  component: featurePage('supply', 'SupplyOverviewPage'),
});
const supplySuppliersRoute = createRoute({
  path: '/suppliers',
  getParentRoute: () => supplyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { type?: SupplierType | undefined; blocked?: 'true' | 'false' | undefined; q?: string | undefined } => ({
    type: typeof s.type === 'string' && SUPPLIER_TYPES.has(s.type) ? (s.type as SupplierType) : undefined,
    blocked: s.blocked === 'true' || s.blocked === 'false' ? s.blocked : undefined,
    q: str(s.q),
  }),
  component: featurePage('supply', 'SuppliersPage'),
});
const supplySupplierDetailRoute = createRoute({
  path: '/suppliers/$supplierId',
  getParentRoute: () => supplyRoute,
  validateSearch: (s: Record<string, unknown>): { tab?: 'products' | 'orders' | 'history' | undefined } => ({
    tab: s.tab === 'orders' || s.tab === 'history' ? s.tab : s.tab === 'products' ? 'products' : undefined,
  }),
  component: featurePage('supply', 'SupplierDetailPage'),
});
const supplyRequestsRoute = createRoute({
  path: '/requests',
  getParentRoute: () => supplyRoute,
  // Q3-c: create/productId/warehouseId = mở sẵn form PR điền trước (từ Tìm kiếm tồn "Tạo yêu cầu mua")
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: PurchaseRequestStatus | undefined;
    source?: PurchaseRequestSource | undefined;
    create?: true | undefined;
    productId?: string | undefined;
    warehouseId?: string | undefined;
  } => ({
    status: typeof s.status === 'string' && PR_STATUSES.has(s.status) ? (s.status as PurchaseRequestStatus) : undefined,
    source: typeof s.source === 'string' && PR_SOURCES.has(s.source) ? (s.source as PurchaseRequestSource) : undefined,
    create: s.create === true || s.create === 'true' ? true : undefined,
    productId: str(s.productId),
    warehouseId: str(s.warehouseId),
  }),
  component: featurePage('supply', 'PurchaseRequestsPage'),
});
const supplyRequestDetailRoute = createRoute({
  path: '/requests/$requestId',
  getParentRoute: () => supplyRoute,
  component: featurePage('supply', 'PurchaseRequestDetailPage'),
});
// R6.6: gom PR đã duyệt theo SKU / kho / khoảng ngày cần → một PO
const PrConsolidationLazy = featurePage('supply', 'PrConsolidationPage');
const supplyRequestConsolidateRoute = createRoute({
  path: '/requests/consolidate',
  getParentRoute: () => supplyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { warehouseId?: string | undefined; from?: string | undefined; to?: string | undefined } => ({
    warehouseId: str(s.warehouseId),
    from: str(s.from),
    to: str(s.to),
  }),
  component: () => (
    <RequireCapability cap="purchase.purchase_order.create">
      <PrConsolidationLazy />
    </RequireCapability>
  ),
});
const supplyOrdersRoute = createRoute({
  path: '/orders',
  getParentRoute: () => supplyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: PurchaseOrderStatus | undefined;
    supplierId?: string | undefined;
    warehouseId?: string | undefined;
    late?: boolean | undefined;
    limit?: number | undefined;
  } => ({
    status: typeof s.status === 'string' && PO_STATUSES.has(s.status) ? (s.status as PurchaseOrderStatus) : undefined,
    supplierId: str(s.supplierId),
    warehouseId: str(s.warehouseId),
    late: flag(s.late), // T6-G1 (V-SC-1): chip "Giao trễ"
    limit: listPageSize(s.limit), // KOVIX-4084: số dòng/trang (20/30/40/50), mặc định không ghi URL
  }),
  component: featurePage('supply', 'PurchaseOrdersPage'),
});
const SupplyOrderCreateLazy = featurePage('supply', 'PurchaseOrderCreatePage');
const supplyOrderCreateRoute = createRoute({
  path: '/orders/new',
  getParentRoute: () => supplyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { supplierId?: string | undefined; contractId?: string | undefined } => ({
    supplierId: str(s.supplierId),
    contractId: str(s.contractId),
  }),
  component: () => (
    <RequireCapability cap="purchase.purchase_order.create">
      <SupplyOrderCreateLazy />
    </RequireCapability>
  ),
});
// GĐ 3.4: RFQ mua (BR-6.101), HĐ khung (BR-6.102), thẩm định NCC (BR-6.103), Change PO (BR-6.104), corporate demand
const RFQ_STATUSES_P = new Set(['DRAFT', 'SENT', 'CLOSED', 'AWARDED', 'CANCELLED']);
const PCONTRACT_STATUSES = new Set(['DRAFT', 'PENDING_APPROVAL', 'ACTIVE', 'EXPIRED', 'TERMINATED']);
const EVAL_STATUSES = new Set(['DRAFT', 'COMPLETED', 'CANCELLED']);
const PO_REVISION_STATUSES = new Set(['PENDING', 'APPROVED', 'REJECTED', 'APPLIED', 'CANCELLED']);
const supplyRfqsRoute = createRoute({
  path: '/rfqs',
  getParentRoute: () => supplyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: PurchaseRfqStatus | undefined;
    supplierId?: string | undefined;
    prId?: string | undefined;
    create?: true | undefined;
  } => ({
    status: typeof s.status === 'string' && RFQ_STATUSES_P.has(s.status) ? (s.status as PurchaseRfqStatus) : undefined,
    supplierId: str(s.supplierId),
    prId: str(s.prId),
    create: s.create === true || s.create === 'true' ? true : undefined,
  }),
  component: featurePage('supply', 'RfqsPage'),
});
const supplyRfqDetailRoute = createRoute({
  path: '/rfqs/$rfqId',
  getParentRoute: () => supplyRoute,
  component: featurePage('supply', 'RfqDetailPage'),
});
const supplyContractsRoute = createRoute({
  path: '/contracts',
  getParentRoute: () => supplyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: PurchaseContractStatus | undefined; supplierId?: string | undefined } => ({
    status:
      typeof s.status === 'string' && PCONTRACT_STATUSES.has(s.status)
        ? (s.status as PurchaseContractStatus)
        : undefined,
    supplierId: str(s.supplierId),
  }),
  component: featurePage('supply', 'PurchaseContractsPage'),
});
const supplyContractDetailRoute = createRoute({
  path: '/contracts/$contractId',
  getParentRoute: () => supplyRoute,
  component: featurePage('supply', 'PurchaseContractDetailPage'),
});
const supplyEvaluationsRoute = createRoute({
  path: '/evaluations',
  getParentRoute: () => supplyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    supplierId?: string | undefined;
    type?: SupplierEvaluationType | undefined;
    status?: SupplierEvaluationStatus | undefined;
  } => ({
    supplierId: str(s.supplierId),
    type: s.type === 'QUALIFICATION' || s.type === 'PERIODIC' ? s.type : undefined,
    status:
      typeof s.status === 'string' && EVAL_STATUSES.has(s.status) ? (s.status as SupplierEvaluationStatus) : undefined,
  }),
  component: featurePage('supply', 'SupplierEvaluationsPage'),
});
const supplyEvaluationDetailRoute = createRoute({
  path: '/evaluations/$evaluationId',
  getParentRoute: () => supplyRoute,
  component: featurePage('supply', 'SupplierEvaluationDetailPage'),
});
const supplyRevisionsRoute = createRoute({
  path: '/revisions',
  getParentRoute: () => supplyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: PurchaseOrderRevisionStatus | undefined; poId?: string | undefined } => ({
    status:
      typeof s.status === 'string' && PO_REVISION_STATUSES.has(s.status)
        ? (s.status as PurchaseOrderRevisionStatus)
        : undefined,
    poId: str(s.poId),
  }),
  component: featurePage('supply', 'PoRevisionsPage'),
});
const supplyCorporateDemandRoute = createRoute({
  path: '/corporate-demand',
  getParentRoute: () => supplyRoute,
  validateSearch: (s: Record<string, unknown>): { customerId?: string | undefined; days?: number | undefined } => ({
    customerId: str(s.customerId),
    days:
      typeof s.days === 'number'
        ? s.days
        : typeof s.days === 'string' && /^\d+$/.test(s.days)
          ? Number(s.days)
          : undefined,
  }),
  component: featurePage('supply', 'CorporateDemandPage'),
});
const supplyOrderDetailRoute = createRoute({
  path: '/orders/$orderId',
  getParentRoute: () => supplyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'lines' | 'receipts' | 'invoices' | 'history' | undefined } => ({
    tab:
      s.tab === 'receipts' || s.tab === 'invoices' || s.tab === 'history'
        ? s.tab
        : s.tab === 'lines'
          ? 'lines'
          : undefined,
  }),
  component: featurePage('supply', 'PurchaseOrderDetailPage'),
});
const supplyReceiptsRoute = createRoute({
  path: '/receipts',
  getParentRoute: () => supplyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: ReceiptStatus | undefined; warehouseId?: string | undefined; poId?: string | undefined } => ({
    status: typeof s.status === 'string' && RECEIPT_STATUSES.has(s.status) ? (s.status as ReceiptStatus) : undefined,
    warehouseId: str(s.warehouseId),
    poId: str(s.poId),
  }),
  component: featurePage('supply', 'ReceiptsPage'),
});
const supplyReceiptDetailRoute = createRoute({
  path: '/receipts/$receiptId',
  getParentRoute: () => supplyRoute,
  component: featurePage('supply', 'ReceiptDetailPage'),
});
const supplyMatchesRoute = createRoute({
  path: '/matches',
  getParentRoute: () => supplyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'matches' | 'invoices' | undefined;
    status?: MatchStatus | undefined;
    invStatus?: SupplierInvoiceStatus | undefined;
    mine?: 'true' | undefined;
    poId?: string | undefined;
    selected?: string | undefined;
  } => ({
    tab: s.tab === 'invoices' ? 'invoices' : s.tab === 'matches' ? 'matches' : undefined,
    status: typeof s.status === 'string' && MATCH_STATUSES.has(s.status) ? (s.status as MatchStatus) : undefined,
    invStatus:
      typeof s.invStatus === 'string' && INVOICE_STATUSES.has(s.invStatus)
        ? (s.invStatus as SupplierInvoiceStatus)
        : undefined,
    mine: s.mine === 'true' ? 'true' : undefined,
    poId: str(s.poId),
    selected: str(s.selected),
  }),
  component: featurePage('supply', 'MatchesPage'),
});
const supplyPricesRoute = createRoute({
  path: '/prices',
  getParentRoute: () => supplyRoute,
  validateSearch: (s: Record<string, unknown>): { productId?: string | undefined } => ({ productId: str(s.productId) }),
  component: featurePage('supply', 'PriceListsPage'),
});
const supplyFarmerSlipsRoute = createRoute({
  path: '/farmer-slips',
  getParentRoute: () => supplyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: FarmerSlipStatus | undefined; warehouseId?: string | undefined } => ({
    status: typeof s.status === 'string' && SLIP_STATUSES.has(s.status) ? (s.status as FarmerSlipStatus) : undefined,
    warehouseId: str(s.warehouseId),
  }),
  component: featurePage('supply', 'FarmerSlipsPage'),
});
const supplyFarmerSlipDetailRoute = createRoute({
  path: '/farmer-slips/$slipId',
  getParentRoute: () => supplyRoute,
  component: featurePage('supply', 'FarmerSlipDetailPage'),
});

// ---- KOVIX.Logistics (Đợt 4: logistics — kiện giao, chuyến 5 chặng, e-POD, chuỗi lạnh, ngoại lệ, đội xe/3PL) ----
const TRIP_STATUSES = new Set(['CREATED', 'PICKED_UP', 'IN_TRANSIT', 'ARRIVED', 'DELIVERED', 'FAILED', 'RETURNED']);
const EXCEPTION_TYPES = new Set(['REFUSED', 'ABSENT', 'DAMAGED', 'SHORT', 'TEMP_BREACH']);
const logisticsRoute = createRoute({
  path: '/logistics',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="logistics.trip.view" moduleCode="logistics">
      <Outlet />
    </RequireCapability>
  ),
});
const logisticsOverviewRoute = createRoute({
  path: '/',
  getParentRoute: () => logisticsRoute,
  component: featurePage('logistics', 'LogisticsOverviewPage'),
});
const logisticsTripsRoute = createRoute({
  path: '/trips',
  getParentRoute: () => logisticsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: TripStatus | undefined; date?: string | undefined; carrierId?: string | undefined } => ({
    status: typeof s.status === 'string' && TRIP_STATUSES.has(s.status) ? (s.status as TripStatus) : undefined,
    date: str(s.date),
    carrierId: str(s.carrierId),
  }),
  component: featurePage('logistics', 'TripsPage'),
});
const TripCreateLazy = featurePage('logistics', 'TripCreatePage');
const logisticsTripCreateRoute = createRoute({
  path: '/trips/new',
  getParentRoute: () => logisticsRoute,
  // Q3-e salesFulfillment "Bàn giao vận chuyển": điền sẵn kiện chờ xếp chuyến của các đơn (orderIds = danh sách id cách nhau bởi dấu phẩy)
  validateSearch: (s: Record<string, unknown>): { orderIds?: string | undefined } => ({
    orderIds:
      typeof s.orderIds === 'string' && /^[0-9a-fA-F-]{36}(,[0-9a-fA-F-]{36}){0,49}$/.test(s.orderIds)
        ? s.orderIds
        : undefined,
  }),
  component: () => (
    <RequireCapability cap="logistics.trip.create">
      <TripCreateLazy />
    </RequireCapability>
  ),
});
const logisticsTripDetailRoute = createRoute({
  path: '/trips/$tripId',
  getParentRoute: () => logisticsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'stops' | 'temperature' | 'exceptions' | 'history' | undefined } => ({
    tab:
      s.tab === 'temperature' || s.tab === 'exceptions' || s.tab === 'history'
        ? s.tab
        : s.tab === 'stops'
          ? 'stops'
          : undefined,
  }),
  component: featurePage('logistics', 'TripDetailPage'),
});
const logisticsShipmentsRoute = createRoute({
  path: '/shipments',
  getParentRoute: () => logisticsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: TripStatus | undefined;
    warehouseId?: string | undefined;
    unassigned?: 'true' | undefined;
    orderId?: string | undefined;
  } => ({
    status: typeof s.status === 'string' && TRIP_STATUSES.has(s.status) ? (s.status as TripStatus) : undefined,
    warehouseId: str(s.warehouseId),
    // FT 02/10/2026 (E-08): deep link `?unassigned=true` được TanStack đọc thành boolean true — nhận cả hai dạng.
    unassigned: s.unassigned === 'true' || s.unassigned === true ? 'true' : undefined,
    orderId: str(s.orderId),
  }),
  component: featurePage('logistics', 'ShipmentsPage'),
});
const logisticsShipmentDetailRoute = createRoute({
  path: '/shipments/$shipmentId',
  getParentRoute: () => logisticsRoute,
  component: featurePage('logistics', 'ShipmentDetailPage'),
});
const logisticsExceptionsRoute = createRoute({
  path: '/exceptions',
  getParentRoute: () => logisticsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    type?: DeliveryExceptionType | undefined;
    open?: 'true' | 'false' | undefined;
    tripId?: string | undefined;
  } => ({
    type: typeof s.type === 'string' && EXCEPTION_TYPES.has(s.type) ? (s.type as DeliveryExceptionType) : undefined,
    open: s.open === 'true' || s.open === 'false' ? s.open : undefined,
    tripId: str(s.tripId),
  }),
  component: featurePage('logistics', 'ExceptionsPage'),
});
const logisticsFleetRoute = createRoute({
  path: '/fleet',
  getParentRoute: () => logisticsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'vehicles' | 'carriers' | 'drivers' | undefined; carrierId?: string | undefined } => ({
    tab:
      s.tab === 'carriers'
        ? 'carriers'
        : s.tab === 'vehicles'
          ? 'vehicles'
          : s.tab === 'drivers'
            ? 'drivers'
            : undefined,
    carrierId: str(s.carrierId),
  }), // Q3-e: tab tài xế thuê ngoài
  component: featurePage('logistics', 'FleetPage'),
});
// GĐ 3.7: lập & tối ưu tuyến (logiRoute, BR-10.101), sửa chuyến (logiTripEdit), phiếu giao (logiDeliveryNote), hồ sơ xe (logiFleetDetail), đối soát (logiReconcile, BR-10.103)
const RoutePlanningLazy = featurePage('logistics', 'RoutePlanningPage');
const logisticsRoutingRoute = createRoute({
  path: '/routing',
  getParentRoute: () => logisticsRoute,
  validateSearch: (s: Record<string, unknown>): { warehouseId?: string | undefined } => ({
    warehouseId: str(s.warehouseId),
  }),
  component: () => (
    <RequireCapability cap="logistics.trip.create">
      <RoutePlanningLazy />
    </RequireCapability>
  ),
});
const TripEditLazy = featurePage('logistics', 'TripEditPage');
const logisticsTripEditRoute = createRoute({
  path: '/trips/$tripId/edit',
  getParentRoute: () => logisticsRoute,
  component: () => (
    <RequireCapability cap="logistics.trip.create">
      <TripEditLazy />
    </RequireCapability>
  ),
});
const logisticsDeliveryNoteRoute = createRoute({
  path: '/trips/$tripId/note',
  getParentRoute: () => logisticsRoute,
  component: featurePage('logistics', 'DeliveryNotePage'),
});
const logisticsVehicleProfileRoute = createRoute({
  path: '/fleet/$vehicleId',
  getParentRoute: () => logisticsRoute,
  component: featurePage('logistics', 'VehicleProfilePage'),
});
const RECON_STATUSES = new Set(['DRAFT', 'MATCHED', 'VARIANCE', 'CLOSED']);
const logisticsReconcileRoute = createRoute({
  path: '/reconcile',
  getParentRoute: () => logisticsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: DeliveryReconciliationStatus | undefined;
    carrierId?: string | undefined;
    tripId?: string | undefined;
  } => ({
    status:
      typeof s.status === 'string' && RECON_STATUSES.has(s.status)
        ? (s.status as DeliveryReconciliationStatus)
        : undefined,
    carrierId: str(s.carrierId),
    tripId: str(s.tripId),
  }),
  component: featurePage('logistics', 'ReconciliationPage'),
});
// Q3-a logiColdChain — báo cáo tuân thủ chuỗi lạnh nhiều chuyến (GET /cold-chain/compliance, logistics.cold_chain.view)
const ColdChainComplianceLazy = featurePage('logistics', 'ColdChainCompliancePage');
const logisticsColdChainRoute = createRoute({
  path: '/cold-chain',
  getParentRoute: () => logisticsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { from?: string | undefined; to?: string | undefined; breachOnly?: 'true' | undefined } => ({
    from: str(s.from),
    to: str(s.to),
    breachOnly: s.breachOnly === 'true' ? 'true' : undefined,
  }),
  component: () => (
    <RequireCapability cap="logistics.cold_chain.view">
      <ColdChainComplianceLazy />
    </RequireCapability>
  ),
});

// ---- KOVIX.Production (Đợt 4: mfg — lệnh SX theo BOM chụp, NVL/thành phẩm theo lô, QC/COA, truy xuất & thu hồi, nhà máy/trạm/ca) ----
const WO_STATUSES = new Set([
  'DRAFT',
  'PENDING_APPROVAL',
  'APPROVED',
  'WAITING_MATERIAL',
  'IN_PROGRESS',
  'PENDING_QC',
  'COMPLETED',
  'CLOSED',
]); // R6.8: + chờ vật tư
const productionRoute = createRoute({
  path: '/production',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="mfg.work_order.view" moduleCode="mfg">
      <Outlet />
    </RequireCapability>
  ),
});
const productionOverviewRoute = createRoute({
  path: '/',
  getParentRoute: () => productionRoute,
  component: featurePage('production', 'ProductionOverviewPage'),
});
const productionWorkOrdersRoute = createRoute({
  path: '/work-orders',
  getParentRoute: () => productionRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: WorkOrderStatus | undefined;
    factoryId?: string | undefined;
    productId?: string | undefined;
    from?: string | undefined;
    to?: string | undefined;
  } => ({
    status: typeof s.status === 'string' && WO_STATUSES.has(s.status) ? (s.status as WorkOrderStatus) : undefined,
    factoryId: str(s.factoryId),
    productId: str(s.productId),
    from: str(s.from),
    to: str(s.to),
  }),
  component: featurePage('production', 'WorkOrdersPage'),
});
const WorkOrderCreateLazy = featurePage('production', 'WorkOrderCreatePage');
const productionWorkOrderCreateRoute = createRoute({
  path: '/work-orders/new',
  getParentRoute: () => productionRoute,
  // 08/10/2026 (mockup v130 "+ Tạo lệnh có gia công") — mở màn tạo lệnh ở chế độ gia công một số / toàn bộ công đoạn
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    outsource?: 'partial' | 'full' | undefined;
    sourceOrderId?: string | undefined;
    productId?: string | undefined;
    qty?: number | undefined;
  } => ({
    outsource: s.outsource === 'partial' || s.outsource === 'full' ? s.outsource : undefined,
    // 15/10/2026 — điền sẵn từ Kho → Đơn cần xử lý (đơn nguồn, thành phẩm, SL thiếu)
    sourceOrderId: str(s.sourceOrderId),
    productId: str(s.productId),
    qty:
      typeof s.qty === 'number' && s.qty > 0
        ? s.qty
        : typeof s.qty === 'string' && Number(s.qty) > 0
          ? Number(s.qty)
          : undefined,
  }),
  component: () => (
    <RequireCapability cap="mfg.work_order.create">
      <WorkOrderCreateLazy />
    </RequireCapability>
  ),
});
const productionWorkOrderDetailRoute = createRoute({
  path: '/work-orders/$woId',
  getParentRoute: () => productionRoute,
  // T6-G1: tab `reservations` = NVL kho đã giữ cho lệnh (T6-C H3)
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'operations' | 'materials' | 'reservations' | 'outputs' | 'qc' | 'yield' | 'history' | undefined } => ({
    tab:
      s.tab === 'materials' ||
      s.tab === 'reservations' ||
      s.tab === 'outputs' ||
      s.tab === 'qc' ||
      s.tab === 'yield' ||
      s.tab === 'history'
        ? s.tab
        : s.tab === 'operations'
          ? 'operations'
          : undefined,
  }),
  component: featurePage('production', 'WorkOrderDetailPage'),
});
// R6.8 (K-19): in tem lô có mã QR truy xuất ký HMAC
const productionWorkOrderLabelsRoute = createRoute({
  path: '/work-orders/$woId/labels',
  getParentRoute: () => productionRoute,
  validateSearch: (s: Record<string, unknown>): { outputId?: string | undefined } => ({ outputId: str(s.outputId) }),
  component: featurePage('production', 'LotLabelsPage'),
});
// GĐ 3.6: sửa lệnh nháp (mfgWOEdit), kế hoạch & công suất (mfgPlanning, BR-8.101), sơ chế/yield (mfgProcessing, BR-8.102), NCR/CAPA (mfgDeviation, BR-8.103)
const WorkOrderEditLazy = featurePage('production', 'WorkOrderEditPage');
const productionWorkOrderEditRoute = createRoute({
  path: '/work-orders/$woId/edit',
  getParentRoute: () => productionRoute,
  component: () => (
    <RequireCapability cap="mfg.work_order.create">
      <WorkOrderEditLazy />
    </RequireCapability>
  ),
});
const PRODUCTION_PLAN_STATUSES = new Set(['DRAFT', 'CONFIRMED', 'CANCELLED']);
const productionPlansRoute = createRoute({
  path: '/plans',
  getParentRoute: () => productionRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: ProductionPlanStatus | undefined;
    factoryId?: string | undefined;
    from?: string | undefined;
    to?: string | undefined;
  } => ({
    status:
      typeof s.status === 'string' && PRODUCTION_PLAN_STATUSES.has(s.status)
        ? (s.status as ProductionPlanStatus)
        : undefined,
    factoryId: str(s.factoryId),
    // KOVIX-4075: drill RPT-PLAN-12 "SL kế hoạch" mang kỳ (ngày kế hoạch hoàn thành)
    from: typeof s.from === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.from) ? s.from : undefined,
    to: typeof s.to === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.to) ? s.to : undefined,
  }),
  component: featurePage('production', 'ProductionPlansPage'),
});
const productionPlanDetailRoute = createRoute({
  path: '/plans/$planId',
  getParentRoute: () => productionRoute,
  validateSearch: (s: Record<string, unknown>): { tab?: 'lines' | 'capacity' | 'history' | undefined } => ({
    tab: s.tab === 'capacity' || s.tab === 'history' ? s.tab : s.tab === 'lines' ? 'lines' : undefined,
  }),
  component: featurePage('production', 'ProductionPlanDetailPage'),
});
const productionYieldRoute = createRoute({
  path: '/yield',
  getParentRoute: () => productionRoute,
  validateSearch: (s: Record<string, unknown>): { woId?: string | undefined; all?: boolean | undefined } => ({
    woId: str(s.woId),
    all: s.all === true || s.all === 'true' ? true : undefined,
  }),
  component: featurePage('production', 'YieldPage'),
});
const NCR_STATUSES = new Set(['OPEN', 'INVESTIGATING', 'CAPA_PLANNED', 'VERIFIED', 'CLOSED', 'CANCELLED']);
const productionNcrRoute = createRoute({
  path: '/ncr',
  getParentRoute: () => productionRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: NcrStatus | undefined;
    severity?: NcrSeverity | undefined;
    woId?: string | undefined;
    all?: boolean | undefined;
    new?: boolean | undefined;
    /** KOVIX-3803: drill từ RPT-QA-14 — SP + kỳ lập phiếu (YYYY-MM-DD). */
    productId?: string | undefined;
    from?: string | undefined;
    to?: string | undefined;
  } => ({
    status: typeof s.status === 'string' && NCR_STATUSES.has(s.status) ? (s.status as NcrStatus) : undefined,
    severity: s.severity === 'MINOR' || s.severity === 'MAJOR' || s.severity === 'CRITICAL' ? s.severity : undefined,
    woId: str(s.woId),
    all: s.all === true || s.all === 'true' ? true : undefined,
    new: s.new === true || s.new === 'true' ? true : undefined,
    productId: str(s.productId),
    from: str(s.from),
    to: str(s.to),
  }),
  component: featurePage('production', 'NcrsPage'),
});
const productionNcrDetailRoute = createRoute({
  path: '/ncr/$ncrId',
  getParentRoute: () => productionRoute,
  validateSearch: (s: Record<string, unknown>): { tab?: 'capa' | 'history' | undefined } => ({
    tab: s.tab === 'history' ? 'history' : s.tab === 'capa' ? 'capa' : undefined,
  }),
  component: featurePage('production', 'NcrDetailPage'),
});
const productionQcRoute = createRoute({
  path: '/qc',
  getParentRoute: () => productionRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { result?: QcResult | undefined; sourceType?: QcSourceType | undefined } => ({
    result: s.result === 'PASS' || s.result === 'FAIL' || s.result === 'CONDITIONAL' ? s.result : undefined,
    sourceType:
      s.sourceType === 'WO' || s.sourceType === 'RECEIPT' || s.sourceType === 'OEM' ? s.sourceType : undefined,
  }),
  component: featurePage('production', 'QcInspectionsPage'),
});
// ---- W6‑c: mfgBOM / mfgRouting master toàn tenant, qcReport (COA in) ----
const productionBomRoute = createRoute({
  path: '/bom',
  getParentRoute: () => productionRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: ProductStatus | undefined; selected?: string | undefined; create?: string | undefined } => ({
    status: typeof s.status === 'string' ? (s.status as ProductStatus) : undefined,
    selected: str(s.selected),
    create: str(s.create),
  }),
  component: featurePage('production', 'BomMasterPage'),
});
const productionRoutingRoute = createRoute({
  path: '/routing',
  getParentRoute: () => productionRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: ProductStatus | undefined; selected?: string | undefined; create?: string | undefined } => ({
    status: typeof s.status === 'string' ? (s.status as ProductStatus) : undefined,
    selected: str(s.selected),
    create: str(s.create),
  }),
  component: featurePage('production', 'RoutingMasterPage'),
});
const productionQcReportRoute = createRoute({
  path: '/qc/$inspectionId/coa',
  getParentRoute: () => productionRoute,
  component: featurePage('production', 'QcReportPage'),
});
const LotTraceLazy = featurePage('production', 'LotTracePage');
const productionTraceRoute = createRoute({
  path: '/trace',
  getParentRoute: () => productionRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { lotId?: string | undefined; lotNo?: string | undefined; productId?: string | undefined } => ({
    lotId: str(s.lotId),
    lotNo: str(s.lotNo),
    productId: str(s.productId),
  }),
  component: () => (
    <RequireCapability cap="mfg.genealogy.view">
      <LotTraceLazy />
    </RequireCapability>
  ),
});
const productionFactoriesRoute = createRoute({
  path: '/factories',
  getParentRoute: () => productionRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'factories' | 'work-centers' | 'shifts' | 'downtime' | undefined;
    factoryId?: string | undefined;
    workCenterId?: string | undefined;
  } => ({
    tab:
      s.tab === 'work-centers' || s.tab === 'shifts' || s.tab === 'downtime'
        ? s.tab
        : s.tab === 'factories'
          ? 'factories'
          : undefined,
    factoryId: str(s.factoryId),
    workCenterId: str(s.workCenterId),
  }),
  component: featurePage('production', 'FactoriesPage'),
});

// ---- KOVIX.OEM (Đợt 4: oem — đơn gia công, kho ảo NVL tại đối tác, nhận TP + QC, đối chiếu định mức, quyết toán chỉ phát sự kiện) ----
const OEM_STATUSES = new Set([
  'DRAFT',
  'PENDING_APPROVAL',
  'APPROVED',
  'MATERIAL_ISSUED',
  'PROCESSING',
  'QC_RECONCILING',
  'SETTLED',
  'CLOSED',
]);
const OEM_SETTLEMENT_STATUSES = new Set([
  'DRAFT',
  'PENDING_APPROVAL',
  'DISPUTED',
  'APPROVED',
  'SUPERSEDED',
  'CANCELLED',
]);
const oemRoute = createRoute({
  path: '/oem',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="oem.oem_order.view" moduleCode="oem">
      <Outlet />
    </RequireCapability>
  ),
});
const oemOverviewRoute = createRoute({
  path: '/',
  getParentRoute: () => oemRoute,
  component: featurePage('oem', 'OemOverviewPage'),
});
const oemOrdersRoute = createRoute({
  path: '/orders',
  getParentRoute: () => oemRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: OemOrderStatus | undefined;
    vendorId?: string | undefined;
    sourceWoId?: string | undefined;
    from?: string | undefined;
    to?: string | undefined;
  } => ({
    status: typeof s.status === 'string' && OEM_STATUSES.has(s.status) ? (s.status as OemOrderStatus) : undefined,
    vendorId: str(s.vendorId),
    sourceWoId: str(s.sourceWoId),
    // RPT-OEM-15 drill-down "Số đơn": kỳ tạo đơn (ngày tenant)
    from: str(s.from),
    to: str(s.to),
  }),
  component: featurePage('oem', 'OemOrdersPage'),
});
const OemOrderCreateLazy = featurePage('oem', 'OemOrderCreatePage');
const oemOrderCreateRoute = createRoute({
  path: '/orders/new',
  getParentRoute: () => oemRoute,
  component: () => (
    <RequireCapability cap="oem.oem_order.create">
      <OemOrderCreateLazy />
    </RequireCapability>
  ),
});
const oemOrderDetailRoute = createRoute({
  path: '/orders/$orderId',
  getParentRoute: () => oemRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'materials' | 'receipts' | 'reconciliation' | 'settlement' | 'documents' | 'history' | undefined } => ({
    tab:
      s.tab === 'receipts' ||
      s.tab === 'reconciliation' ||
      s.tab === 'settlement' ||
      s.tab === 'documents' ||
      s.tab === 'history'
        ? s.tab
        : s.tab === 'materials'
          ? 'materials'
          : undefined,
  }),
  component: featurePage('oem', 'OemOrderDetailPage'),
});
const oemVendorStockRoute = createRoute({
  path: '/vendor-stock',
  getParentRoute: () => oemRoute,
  validateSearch: (s: Record<string, unknown>): { vendorId?: string | undefined; all?: 'true' | undefined } => ({
    vendorId: str(s.vendorId),
    all: s.all === 'true' ? 'true' : undefined,
  }),
  component: featurePage('oem', 'VendorStockPage'),
});
const oemSettlementsRoute = createRoute({
  path: '/settlements',
  getParentRoute: () => oemRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: OemSettlementStatus | undefined; vendorId?: string | undefined } => ({
    status:
      typeof s.status === 'string' && OEM_SETTLEMENT_STATUSES.has(s.status)
        ? (s.status as OemSettlementStatus)
        : undefined,
    vendorId: str(s.vendorId),
  }),
  component: featurePage('oem', 'SettlementsPage'),
});
const oemVendorsRoute = createRoute({
  path: '/vendors',
  getParentRoute: () => oemRoute,
  component: featurePage('oem', 'VendorsPage'),
});
// GĐ 3.8: sửa đầu đơn nháp (+ gắn hợp đồng), hàng đợi phê duyệt, hợp đồng gia công
const OEM_CONTRACT_STATUSES = new Set(['DRAFT', 'ACTIVE', 'EXPIRED', 'TERMINATED']);
const OemOrderEditLazy = featurePage('oem', 'OemOrderEditPage');
const oemOrderEditRoute = createRoute({
  path: '/orders/$orderId/edit',
  getParentRoute: () => oemRoute,
  component: () => (
    <RequireCapability cap="oem.oem_order.create">
      <OemOrderEditLazy />
    </RequireCapability>
  ),
});
const oemApprovalsRoute = createRoute({
  path: '/approvals',
  getParentRoute: () => oemRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'orders' | 'settlements' | undefined; vendorId?: string | undefined } => ({
    tab: s.tab === 'settlements' ? 'settlements' : s.tab === 'orders' ? 'orders' : undefined,
    // RPT-OEM-15 drill-down "Đơn chờ quyết toán": hàng chờ duyệt lọc theo đối tác
    vendorId: str(s.vendorId),
  }),
  component: featurePage('oem', 'OemApprovalsPage'),
});
const oemContractsRoute = createRoute({
  path: '/contracts',
  getParentRoute: () => oemRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: OemContractStatus | undefined; vendorId?: string | undefined } => ({
    status:
      typeof s.status === 'string' && OEM_CONTRACT_STATUSES.has(s.status) ? (s.status as OemContractStatus) : undefined,
    vendorId: str(s.vendorId),
  }),
  component: featurePage('oem', 'OemContractsPage'),
});
const oemContractDetailRoute = createRoute({
  path: '/contracts/$contractId',
  getParentRoute: () => oemRoute,
  component: featurePage('oem', 'OemContractDetailPage'),
});

// ---- KOVIX.Social (GĐ 4.1: Unified Inbox, lead một owner + phân bổ + SLA, che PII, live, chốt đơn trên chat) ----
const CONVERSATION_STATUSES = new Set(['OPEN', 'PENDING', 'RESOLVED', 'SPAM']);
const LEAD_STATUSES = new Set(['NEW', 'ASSIGNED', 'NURTURING', 'QUALIFIED', 'CONVERTED', 'DISQUALIFIED']);
const LIVE_STATUSES = new Set(['PLANNED', 'LIVE', 'ENDED', 'CANCELLED']);
const CHAT_ORDER_STATUSES = new Set(['DRAFT', 'PLACED', 'FAILED', 'CANCELLED']);
const socialRoute = createRoute({
  path: '/social',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="social.conversation.view" moduleCode="social">
      <Outlet />
    </RequireCapability>
  ),
});
const socialOverviewRoute = createRoute({
  path: '/',
  getParentRoute: () => socialRoute,
  component: featurePage('social', 'SocialOverviewPage'),
});
const socialInboxRoute = createRoute({
  path: '/inbox',
  getParentRoute: () => socialRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: ConversationStatus | undefined;
    channelId?: string | undefined;
    view?: 'mine' | 'unassigned' | 'unread' | undefined;
    conversationId?: string | undefined;
  } => ({
    status:
      typeof s.status === 'string' && CONVERSATION_STATUSES.has(s.status)
        ? (s.status as ConversationStatus)
        : undefined,
    channelId: str(s.channelId),
    view: s.view === 'mine' || s.view === 'unassigned' || s.view === 'unread' ? s.view : undefined,
    conversationId: str(s.conversationId),
  }),
  component: featurePage('social', 'SocialInboxPage'),
});
const socialLeadsRoute = createRoute({
  path: '/leads',
  getParentRoute: () => socialRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: LeadStatus | undefined;
    channelId?: string | undefined;
    mine?: 'true' | undefined;
    overdue?: 'true' | undefined;
    q?: string | undefined;
    tab?: 'list' | 'pipeline' | 'duplicates' | 'rules' | undefined;
    orgUnitId?: string | undefined;
    platform?: string | undefined;
    from?: string | undefined;
    to?: string | undefined;
    slaBreached?: 'true' | undefined;
  } => ({
    status: typeof s.status === 'string' && LEAD_STATUSES.has(s.status) ? (s.status as LeadStatus) : undefined,
    // QA RPT-LEAD-18 drill "Quá SLA": nền tảng (mã, máy chủ kiểm) + kỳ tạo (ngày tenant) + đã từng trễ SLA
    platform: typeof s.platform === 'string' && /^[A-Z_]{2,32}$/.test(s.platform) ? s.platform : undefined,
    from: isoDay(s.from),
    to: isoDay(s.to),
    slaBreached: s.slaBreached === 'true' || s.slaBreached === true ? 'true' : undefined,
    channelId: str(s.channelId),
    orgUnitId: str(s.orgUnitId), // R6.15 lọc theo đơn vị (trong phạm vi dữ liệu)
    mine: s.mine === 'true' ? 'true' : undefined,
    overdue: s.overdue === 'true' ? 'true' : undefined,
    q: str(s.q),
    tab:
      s.tab === 'pipeline' || s.tab === 'duplicates' || s.tab === 'rules'
        ? s.tab
        : s.tab === 'list'
          ? 'list'
          : undefined,
  }),
  component: featurePage('social', 'SocialLeadsPage'),
});
const socialLeadDetailRoute = createRoute({
  path: '/leads/$leadId',
  getParentRoute: () => socialRoute,
  component: featurePage('social', 'SocialLeadDetailPage'),
});
const socialChannelsRoute = createRoute({
  path: '/channels',
  getParentRoute: () => socialRoute,
  validateSearch: (s: Record<string, unknown>): { tab?: 'channels' | 'sla' | undefined } => ({
    tab: s.tab === 'sla' ? 'sla' : s.tab === 'channels' ? 'channels' : undefined,
  }),
  component: featurePage('social', 'SocialChannelsPage'),
});
const socialLiveRoute = createRoute({
  path: '/live',
  getParentRoute: () => socialRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: LiveSessionStatus | undefined; liveId?: string | undefined } => ({
    status: typeof s.status === 'string' && LIVE_STATUSES.has(s.status) ? (s.status as LiveSessionStatus) : undefined,
    liveId: str(s.liveId),
  }),
  component: featurePage('social', 'SocialLivePage'),
});
const socialCommerceRoute = createRoute({
  path: '/commerce',
  getParentRoute: () => socialRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: ChatOrderStatus | undefined; conversationId?: string | undefined } => ({
    status:
      typeof s.status === 'string' && CHAT_ORDER_STATUSES.has(s.status) ? (s.status as ChatOrderStatus) : undefined,
    conversationId: str(s.conversationId),
  }),
  component: featurePage('social', 'SocialCommercePage'),
});
const socialProtectionRoute = createRoute({
  path: '/protection',
  getParentRoute: () => socialRoute,
  component: featurePage('social', 'SocialProtectionPage'),
});

// ---- KOVIX.Loyalty (GĐ 4.2: chương trình điểm/hạng, hội viên & sổ điểm, đổi thưởng, CTV/hoa hồng/đơn báo/rút tiền) ----
const LOYALTY_PROGRAM_STATUSES = new Set(['DRAFT', 'PENDING_APPROVAL', 'EFFECTIVE', 'EXPIRED']);
const MEMBER_STATUSES = new Set(['ACTIVE', 'SUSPENDED']);
const REWARD_STATUSES = new Set(['DRAFT', 'ACTIVE', 'INACTIVE']);
const LOYALTY_STATUSES = new Set(['PENDING_ELIGIBILITY', 'APPROVED', 'PAID', 'REJECTED']);
// T9 (chéo B3, LOY-6): REVERSED = Hoàn lại — chỉ lệnh rút (đổi thưởng không có trạng thái này)
// KOVIX-549: REJECTING = Đang hủy — chờ Thanh toán (từ chối sau duyệt)
const WITHDRAWAL_STATUSES = new Set([...LOYALTY_STATUSES, 'REVERSED', 'REJECTING']);
const AFFILIATE_STATUSES = new Set(['DRAFT', 'PENDING_APPROVAL', 'ACTIVE', 'LOCKED']);
const KYC_STATUSES = new Set(['PENDING', 'VERIFIED', 'REJECTED']);
// FT 02/10/2026 (F-06): thiếu DRAFT / NEED_MORE_INFO → chip "Nháp", "Cần bổ sung" không lọc. Đủ giá trị của ReportedOrderStatus.
const REPORTED_ORDER_STATUSES = new Set(['DRAFT', 'PENDING', 'NEED_MORE_INFO', 'CONFIRMED', 'REJECTED', 'DUPLICATE']);
const loyaltyRoute = createRoute({
  path: '/loyalty',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="loyalty.program.view" moduleCode="loyalty">
      <Outlet />
    </RequireCapability>
  ),
});
const loyaltyOverviewRoute = createRoute({
  path: '/',
  getParentRoute: () => loyaltyRoute,
  component: featurePage('loyalty', 'LoyaltyOverviewPage'),
});
const loyaltyProgramRoute = createRoute({
  path: '/program',
  getParentRoute: () => loyaltyRoute,
  // T4-E #23 (LOY-2): tab "revisions" — bản sửa chương trình (trước–sau, gửi/duyệt SoD)
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: LoyaltyProgramStatus | undefined;
    programId?: string | undefined;
    tab?: 'rules' | 'tiers' | 'revisions' | 'history' | undefined;
    revisionId?: string | undefined;
  } => ({
    status:
      typeof s.status === 'string' && LOYALTY_PROGRAM_STATUSES.has(s.status)
        ? (s.status as LoyaltyProgramStatus)
        : undefined,
    programId: str(s.programId),
    tab:
      s.tab === 'tiers' || s.tab === 'history' || s.tab === 'revisions'
        ? s.tab
        : s.tab === 'rules'
          ? 'rules'
          : undefined,
    revisionId: str(s.revisionId),
  }),
  component: featurePage('loyalty', 'LoyaltyProgramPage'),
});
const loyaltyMembersRoute = createRoute({
  path: '/members',
  getParentRoute: () => loyaltyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: MemberStatus | undefined;
    tierCode?: string | undefined;
    q?: string | undefined;
    create?: boolean | undefined;
  } => ({
    status: typeof s.status === 'string' && MEMBER_STATUSES.has(s.status) ? (s.status as MemberStatus) : undefined,
    tierCode: str(s.tierCode),
    q: str(s.q),
    create: flag(s.create),
  }),
  component: featurePage('loyalty', 'LoyaltyMembersPage'),
});
const loyaltyMemberDetailRoute = createRoute({
  path: '/members/$memberId',
  getParentRoute: () => loyaltyRoute,
  component: featurePage('loyalty', 'LoyaltyMemberDetailPage'),
});
const loyaltyRewardsRoute = createRoute({
  path: '/rewards',
  getParentRoute: () => loyaltyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'catalog' | 'redemptions' | undefined;
    status?: RewardStatus | undefined;
    redemptionStatus?: LoyaltyStatus | undefined;
    rewardId?: string | undefined;
    create?: boolean | undefined;
  } => ({
    tab: s.tab === 'redemptions' ? 'redemptions' : s.tab === 'catalog' ? 'catalog' : undefined,
    status: typeof s.status === 'string' && REWARD_STATUSES.has(s.status) ? (s.status as RewardStatus) : undefined,
    redemptionStatus:
      typeof s.redemptionStatus === 'string' && LOYALTY_STATUSES.has(s.redemptionStatus)
        ? (s.redemptionStatus as LoyaltyStatus)
        : undefined,
    rewardId: str(s.rewardId),
    create: flag(s.create),
  }),
  component: featurePage('loyalty', 'LoyaltyRewardsPage'),
});
const loyaltyAffiliatesRoute = createRoute({
  path: '/affiliates',
  getParentRoute: () => loyaltyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: AffiliateStatus | undefined;
    kycStatus?: AffiliateKycStatus | undefined;
    q?: string | undefined;
    create?: boolean | undefined;
  } => ({
    status:
      typeof s.status === 'string' && AFFILIATE_STATUSES.has(s.status) ? (s.status as AffiliateStatus) : undefined,
    kycStatus:
      typeof s.kycStatus === 'string' && KYC_STATUSES.has(s.kycStatus)
        ? (s.kycStatus as AffiliateKycStatus)
        : undefined,
    q: str(s.q),
    create: flag(s.create),
  }),
  component: featurePage('loyalty', 'LoyaltyAffiliatesPage'),
});
const loyaltyAffiliateDetailRoute = createRoute({
  path: '/affiliates/$affiliateId',
  getParentRoute: () => loyaltyRoute,
  // T4-E #21 (G3-1): tab "bank" — yêu cầu đổi/xác minh TK nhận hoa hồng
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'ledger' | 'links' | 'orders' | 'bank' | 'history' | undefined } => ({
    tab:
      s.tab === 'links' || s.tab === 'orders' || s.tab === 'history' || s.tab === 'bank'
        ? s.tab
        : s.tab === 'ledger'
          ? 'ledger'
          : undefined,
  }),
  component: featurePage('loyalty', 'LoyaltyAffiliateDetailPage'),
});
const loyaltyReportedOrdersRoute = createRoute({
  path: '/reported-orders',
  getParentRoute: () => loyaltyRoute,
  // T9 (chéo B3, LOY-7): awaitingMatch=true = chip "Khách mới chờ ghép" (filter[awaitingMatch])
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: ReportedOrderStatus | undefined;
    affiliateId?: string | undefined;
    reportId?: string | undefined;
    create?: boolean | undefined;
    awaitingMatch?: boolean | undefined;
  } => ({
    status:
      typeof s.status === 'string' && REPORTED_ORDER_STATUSES.has(s.status)
        ? (s.status as ReportedOrderStatus)
        : undefined,
    affiliateId: str(s.affiliateId),
    reportId: str(s.reportId),
    create: flag(s.create),
    awaitingMatch: flag(s.awaitingMatch),
  }),
  component: featurePage('loyalty', 'LoyaltyReportedOrdersPage'),
});
const loyaltyCommissionRoute = createRoute({
  path: '/commission',
  getParentRoute: () => loyaltyRoute,
  // T6-G1: tab `rule-changes` = hàng chờ đề nghị đổi quy tắc CTV (T6-C H6)
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'rules' | 'revisions' | 'rule-changes' | 'floors' | 'catalog' | 'program' | undefined;
    ruleId?: string | undefined;
  } => ({
    tab:
      s.tab === 'floors'
        ? 'floors'
        : s.tab === 'rules'
          ? 'rules'
          : s.tab === 'revisions'
            ? 'revisions'
            : s.tab === 'rule-changes'
              ? 'rule-changes'
              : s.tab === 'catalog'
                ? 'catalog'
                : s.tab === 'program'
                  ? 'program'
                  : undefined,
    ruleId: str(s.ruleId),
  }),
  component: featurePage('loyalty', 'LoyaltyCommissionPage'),
});
// BRD 4.0 / v153 — Hoa hồng bán hàng: 6 tab quản lý + "Hoa hồng của tôi" (loyalty.sales_commission.self). Menu: routes.json `salesCommission`.
const loyaltySalesCommissionRoute = createRoute({
  path: '/sales-commission',
  getParentRoute: () => loyaltyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'overview' | 'employees' | 'orders' | 'policy' | 'targets' | 'periods' | undefined;
    period?: string | undefined;
    periodId?: string | undefined;
  } => ({
    tab:
      s.tab === 'employees' || s.tab === 'orders' || s.tab === 'policy' || s.tab === 'targets' || s.tab === 'periods'
        ? s.tab
        : s.tab === 'overview'
          ? 'overview'
          : undefined,
    period: typeof s.period === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(s.period) ? s.period : undefined,
    periodId: str(s.periodId),
  }),
  component: featurePage('loyalty', 'LoyaltySalesCommissionPage'),
});
const loyaltyWithdrawalsRoute = createRoute({
  path: '/withdrawals',
  getParentRoute: () => loyaltyRoute,
  // T6-G1: `status=ALL` = chip "Tất cả" (không có status = mặc định Chờ duyệt — V-EN-12)
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: LoyaltyStatus | 'ALL' | undefined;
    affiliateId?: string | undefined;
    create?: boolean | undefined;
  } => ({
    status:
      s.status === 'ALL'
        ? 'ALL'
        : typeof s.status === 'string' && WITHDRAWAL_STATUSES.has(s.status)
          ? (s.status as LoyaltyStatus)
          : undefined,
    affiliateId: str(s.affiliateId),
    create: flag(s.create),
  }),
  component: featurePage('loyalty', 'LoyaltyWithdrawalsPage'),
});
const loyaltyWithdrawalDetailRoute = createRoute({
  path: '/withdrawals/$withdrawalId',
  getParentRoute: () => loyaltyRoute,
  component: featurePage('loyalty', 'LoyaltyWithdrawalDetailPage'),
});

// ---- KOVIX.Care (GĐ 4.3: ticket & SLA, chuyển tiếp/leo thang có lý do, RMA theo chính sách, khiếu nại → QA, thu hồi, tri thức, khảo sát CSAT/NPS) ----
const CARE_TICKET_STATUSES = new Set(['NEW', 'IN_PROGRESS', 'PENDING', 'RESOLVED', 'CLOSED', 'CANCELLED']);
const TICKET_CATEGORIES = new Set([
  'QUALITY_COMPLAINT',
  'RETURN_EXCHANGE',
  'DELIVERY',
  'PAYMENT',
  'CONSULTING',
  'OTHER',
]);
const TICKET_PRIORITIES = new Set(['URGENT', 'HIGH', 'NORMAL', 'LOW']);
const TICKET_CHANNELS = new Set(['HOTLINE', 'SOCIAL', 'EMAIL', 'COUNTER', 'APP', 'SURVEY', 'SYSTEM']);
const CARE_GROUPS = new Set(['CSKH', 'SUPERVISOR', 'QA', 'WAREHOUSE', 'LOGISTICS', 'ACCOUNTING', 'RMA']);
const RMA_STATUSES = new Set([
  'REQUESTED',
  'INFO_REQUESTED',
  'PENDING_APPROVAL',
  'APPROVED',
  'RETURNING',
  'INSPECTING',
  'COMPLETED',
  'REJECTED',
  'CANCELLED',
]); // T9 (chéo B2 CS-04): + INFO_REQUESTED
const QA_STATUSES = new Set(['PENDING', 'INVESTIGATING', 'CONCLUDED']);
const RECALL_STATUSES = new Set(['DECLARED', 'NOTIFYING', 'RECOVERING', 'CLOSED']);
const KNOWLEDGE_STATUSES = new Set(['DRAFT', 'PUBLISHED', 'ARCHIVED']);
const KNOWLEDGE_CATEGORIES = new Set(['RETURN', 'DELIVERY', 'QUALITY', 'PAYMENT', 'PRODUCT', 'LOYALTY', 'OTHER']);
const RESPONSE_STATUSES = new Set(['SENT', 'ANSWERED', 'EXPIRED']);
const careRoute = createRoute({
  path: '/care',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="aftersales.ticket.view" moduleCode="aftersales">
      <Outlet />
    </RequireCapability>
  ),
});
const careOverviewRoute = createRoute({
  path: '/',
  getParentRoute: () => careRoute,
  component: featurePage('care', 'CareOverviewPage'),
});
const careTicketsRoute = createRoute({
  path: '/tickets',
  getParentRoute: () => careRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: CareTicketStatus | undefined;
    view?: 'open' | 'mine' | 'unassigned' | 'breached' | 'closed' | undefined;
    category?: TicketCategory | undefined;
    priority?: TicketPriority | undefined;
    group?: CareGroup | undefined;
    q?: string | undefined;
    create?: boolean | undefined;
    channel?: import('@kovix/api-client').TicketChannel | undefined;
    owner?: string | undefined;
    from?: string | undefined;
    to?: string | undefined;
  } => ({
    // QA RPT-CUS-06 drill "Ticket mới": kỳ tạo phiếu (ngày tenant yyyy-MM-dd)
    from: isoDay(s.from),
    to: isoDay(s.to),
    status:
      typeof s.status === 'string' && CARE_TICKET_STATUSES.has(s.status) ? (s.status as CareTicketStatus) : undefined,
    // T6-G1 (V-EN-16): lọc Kênh + Phụ trách trên URL
    channel:
      typeof s.channel === 'string' && TICKET_CHANNELS.has(s.channel)
        ? (s.channel as import('@kovix/api-client').TicketChannel)
        : undefined,
    owner: str(s.owner),
    view:
      s.view === 'open' || s.view === 'mine' || s.view === 'unassigned' || s.view === 'breached' || s.view === 'closed'
        ? s.view
        : undefined,
    category:
      typeof s.category === 'string' && TICKET_CATEGORIES.has(s.category) ? (s.category as TicketCategory) : undefined,
    priority:
      typeof s.priority === 'string' && TICKET_PRIORITIES.has(s.priority) ? (s.priority as TicketPriority) : undefined,
    group: typeof s.group === 'string' && CARE_GROUPS.has(s.group) ? (s.group as CareGroup) : undefined,
    q: str(s.q),
    create: flag(s.create),
  }),
  component: featurePage('care', 'CareTicketsPage'),
});
const careTicketDetailRoute = createRoute({
  path: '/tickets/$ticketId',
  getParentRoute: () => careRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'timeline' | 'escalations' | 'knowledge' | 'history' | undefined;
    dialog?: 'transfer' | 'rma' | undefined;
  } => ({
    tab:
      s.tab === 'escalations' || s.tab === 'knowledge' || s.tab === 'history'
        ? s.tab
        : s.tab === 'timeline'
          ? 'timeline'
          : undefined,
    dialog: s.dialog === 'transfer' || s.dialog === 'rma' ? s.dialog : undefined,
  }),
  component: featurePage('care', 'CareTicketDetailPage'),
});
const careRmasRoute = createRoute({
  path: '/rmas',
  getParentRoute: () => careRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: RmaStatus | undefined;
    orderId?: string | undefined;
    ticketId?: string | undefined;
    create?: boolean | undefined;
  } => ({
    status: typeof s.status === 'string' && RMA_STATUSES.has(s.status) ? (s.status as RmaStatus) : undefined,
    orderId: str(s.orderId),
    ticketId: str(s.ticketId),
    create: flag(s.create),
  }),
  component: featurePage('care', 'CareRmasPage'),
});
const careRmaDetailRoute = createRoute({
  path: '/rmas/$rmaId',
  getParentRoute: () => careRoute,
  component: featurePage('care', 'CareRmaDetailPage'),
});
const careComplaintsRoute = createRoute({
  path: '/complaints',
  getParentRoute: () => careRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { qa?: QaStatus | undefined; lotId?: string | undefined; from?: string | undefined; to?: string | undefined } => ({
    qa: typeof s.qa === 'string' && QA_STATUSES.has(s.qa) ? (s.qa as QaStatus) : undefined,
    lotId: str(s.lotId),
    // QA RPT-CARE-21 drill "Chuyển QA điều tra": kỳ tạo ticket (ngày tenant yyyy-MM-dd)
    from: isoDay(s.from),
    to: isoDay(s.to),
  }),
  component: featurePage('care', 'CareComplaintsPage'),
});
const careRecallsRoute = createRoute({
  path: '/recalls',
  getParentRoute: () => careRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: RecallStatus | undefined;
    recallId?: string | undefined;
    create?: boolean | undefined;
    /** KOVIX-3803: drill từ RPT-QA-14 — kỳ công bố thu hồi (YYYY-MM-DD). */
    from?: string | undefined;
    to?: string | undefined;
  } => ({
    status: typeof s.status === 'string' && RECALL_STATUSES.has(s.status) ? (s.status as RecallStatus) : undefined,
    recallId: str(s.recallId),
    create: flag(s.create),
    from: str(s.from),
    to: str(s.to),
  }),
  component: featurePage('care', 'CareRecallsPage'),
});
const careKnowledgeRoute = createRoute({
  path: '/knowledge',
  getParentRoute: () => careRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: KnowledgeStatus | undefined;
    category?: KnowledgeCategory | undefined;
    q?: string | undefined;
    articleId?: string | undefined;
    create?: boolean | undefined;
  } => ({
    status:
      typeof s.status === 'string' && KNOWLEDGE_STATUSES.has(s.status) ? (s.status as KnowledgeStatus) : undefined,
    category:
      typeof s.category === 'string' && KNOWLEDGE_CATEGORIES.has(s.category)
        ? (s.category as KnowledgeCategory)
        : undefined,
    q: str(s.q),
    articleId: str(s.articleId),
    create: flag(s.create),
  }),
  component: featurePage('care', 'CareKnowledgePage'),
});
/** Ngày yyyy-MM-dd trên URL (khác → bỏ). */
function isoDay(v: unknown): string | undefined {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined;
}
const careSurveysRoute = createRoute({
  path: '/surveys',
  getParentRoute: () => careRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'responses' | 'config' | undefined;
    status?: SurveyResponseStatus | undefined;
    low?: boolean | undefined;
    from?: string | undefined;
    to?: string | undefined;
  } => ({
    tab: s.tab === 'config' ? 'config' : s.tab === 'responses' ? 'responses' : undefined,
    // QA RPT-CUS-06 drill "Số đánh giá": kỳ gửi khảo sát (ngày tenant yyyy-MM-dd)
    from: isoDay(s.from),
    to: isoDay(s.to),
    status:
      typeof s.status === 'string' && RESPONSE_STATUSES.has(s.status) ? (s.status as SurveyResponseStatus) : undefined,
    // KOVIX-179 b5 — lọc phản hồi điểm thấp
    low: s.low === true || s.low === 'true' ? true : undefined,
  }),
  component: featurePage('care', 'CareSurveysPage'),
});
const carePoliciesRoute = createRoute({
  path: '/policies',
  getParentRoute: () => careRoute,
  validateSearch: (s: Record<string, unknown>): { tab?: 'sla' | 'rma' | undefined } => ({
    tab: s.tab === 'rma' ? 'rma' : s.tab === 'sla' ? 'sla' : undefined,
  }),
  component: featurePage('care', 'CarePoliciesPage'),
});

// ---- KOVIX.DMS (GĐ 4.4: điểm bán, tuyến MCP, viếng thăm 5 bước check-in GPS máy chủ, khảo sát/kiểm tồn, đơn tại điểm, thu công nợ qua payment, KPI) ----
const OUTLET_STATUSES = new Set(['NEW', 'ACTIVE', 'INACTIVE']);
const OUTLET_TYPES = new Set(['GROCERY', 'RESTAURANT', 'CONVENIENCE', 'MINIMART', 'HORECA', 'WHOLESALE', 'OTHER']);
const DMS_ROUTE_STATUSES = new Set(['DRAFT', 'ACTIVE', 'SUSPENDED', 'ARCHIVED']);
const VISIT_STATUSES = new Set(['PLANNED', 'CHECKED_IN', 'COMPLETED', 'MISSED', 'CANCELLED']);
const GPS_VERIFICATIONS = new Set(['VERIFIED', 'OUT_OF_RADIUS', 'UNVERIFIED']);
const SURVEY_RESULTS = new Set(['PASS', 'WARN', 'FAIL']);
const dmsRoute = createRoute({
  path: '/dms',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="dms.route.view" moduleCode="dms">
      <Outlet />
    </RequireCapability>
  ),
});
const dmsOverviewRoute = createRoute({
  path: '/',
  getParentRoute: () => dmsRoute,
  component: featurePage('dms', 'DmsOverviewPage'),
});
const dmsOutletsRoute = createRoute({
  path: '/outlets',
  getParentRoute: () => dmsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: OutletStatus | undefined;
    type?: OutletType | undefined;
    routeId?: string | undefined;
    unassigned?: boolean | undefined;
    q?: string | undefined;
    create?: boolean | undefined;
    orgUnitId?: string | undefined;
  } => ({
    status: typeof s.status === 'string' && OUTLET_STATUSES.has(s.status) ? (s.status as OutletStatus) : undefined,
    orgUnitId: str(s.orgUnitId), // R6.15
    type: typeof s.type === 'string' && OUTLET_TYPES.has(s.type) ? (s.type as OutletType) : undefined,
    routeId: str(s.routeId),
    unassigned: flag(s.unassigned),
    q: str(s.q),
    create: flag(s.create),
  }),
  component: featurePage('dms', 'DmsOutletsPage'),
});
const dmsOutletDetailRoute = createRoute({
  path: '/outlets/$outletId',
  getParentRoute: () => dmsRoute,
  validateSearch: (s: Record<string, unknown>): { edit?: boolean | undefined } => ({ edit: flag(s.edit) }),
  component: featurePage('dms', 'DmsOutletDetailPage'),
});
const dmsRoutesRoute = createRoute({
  path: '/routes',
  getParentRoute: () => dmsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: RouteStatus | undefined; q?: string | undefined; create?: boolean | undefined } => ({
    status: typeof s.status === 'string' && DMS_ROUTE_STATUSES.has(s.status) ? (s.status as RouteStatus) : undefined,
    q: str(s.q),
    create: flag(s.create),
  }),
  component: featurePage('dms', 'DmsRoutesPage'),
});
const dmsRouteDetailRoute = createRoute({
  path: '/routes/$routeId',
  getParentRoute: () => dmsRoute,
  validateSearch: (s: Record<string, unknown>): { edit?: boolean | undefined; stops?: boolean | undefined } => ({
    edit: flag(s.edit),
    stops: flag(s.stops),
  }),
  component: featurePage('dms', 'DmsRouteDetailPage'),
});
const dmsVisitsRoute = createRoute({
  path: '/visits',
  getParentRoute: () => dmsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: VisitStatus | undefined;
    view?: 'today' | 'open' | 'mine' | 'offroute' | 'gps' | undefined;
    gps?: GpsVerification | undefined;
    routeId?: string | undefined;
    outletId?: string | undefined;
    date?: string | undefined;
    orgUnitId?: string | undefined;
  } => ({
    status: typeof s.status === 'string' && VISIT_STATUSES.has(s.status) ? (s.status as VisitStatus) : undefined,
    orgUnitId: str(s.orgUnitId), // R6.15
    view:
      s.view === 'today' || s.view === 'open' || s.view === 'mine' || s.view === 'offroute' || s.view === 'gps'
        ? s.view
        : undefined,
    gps: typeof s.gps === 'string' && GPS_VERIFICATIONS.has(s.gps) ? (s.gps as GpsVerification) : undefined,
    routeId: str(s.routeId),
    outletId: str(s.outletId),
    date: str(s.date),
  }),
  component: featurePage('dms', 'DmsVisitsPage'),
});
const dmsVisitDetailRoute = createRoute({
  path: '/visits/$visitId',
  getParentRoute: () => dmsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'steps' | 'survey' | 'stock' | 'orders' | 'collections' | 'history' | undefined;
    dialog?: 'checkin' | 'survey' | 'stock' | 'order' | 'collect' | 'checkout' | undefined;
  } => ({
    tab:
      s.tab === 'survey' || s.tab === 'stock' || s.tab === 'orders' || s.tab === 'collections' || s.tab === 'history'
        ? s.tab
        : s.tab === 'steps'
          ? 'steps'
          : undefined,
    dialog:
      s.dialog === 'checkin' ||
      s.dialog === 'survey' ||
      s.dialog === 'stock' ||
      s.dialog === 'order' ||
      s.dialog === 'collect' ||
      s.dialog === 'checkout'
        ? s.dialog
        : undefined,
  }),
  component: featurePage('dms', 'DmsVisitDetailPage'),
});
const dmsSurveyRoute = createRoute({
  path: '/survey',
  getParentRoute: () => dmsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'display' | 'stock' | 'templates' | undefined;
    result?: DisplaySurveyResult | undefined;
    oos?: boolean | undefined;
    outletId?: string | undefined;
  } => ({
    tab: s.tab === 'stock' || s.tab === 'templates' ? s.tab : s.tab === 'display' ? 'display' : undefined,
    result:
      typeof s.result === 'string' && SURVEY_RESULTS.has(s.result) ? (s.result as DisplaySurveyResult) : undefined,
    oos: flag(s.oos),
    outletId: str(s.outletId),
  }),
  component: featurePage('dms', 'DmsSurveyPage'),
});
const dmsCollectionsRoute = createRoute({
  path: '/collections',
  getParentRoute: () => dmsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    cashPending?: boolean | undefined;
    mine?: boolean | undefined;
    outletId?: string | undefined;
    from?: string | undefined;
    to?: string | undefined;
  } => ({
    cashPending: flag(s.cashPending),
    mine: flag(s.mine),
    outletId: str(s.outletId),
    from: str(s.from),
    to: str(s.to),
  }),
  component: featurePage('dms', 'DmsCollectionsPage'),
});
// T3-F1 #18 (G2-5): GET /dms/kpi đòi `dms.kpi.view` (C-05) — menu còn theo routes.json (dms.route.view) tới khi routes.json được sửa
const DmsKpiLazy = featurePage('dms', 'DmsKpiPage');
const dmsKpiRoute = createRoute({
  path: '/kpi',
  getParentRoute: () => dmsRoute,
  validateSearch: (s: Record<string, unknown>): { period?: string | undefined } => ({ period: str(s.period) }),
  component: () => (
    <RequireCapability cap="dms.kpi.view">
      <DmsKpiLazy />
    </RequireCapability>
  ),
});

// ---- KOVIX.HR (X0‑e: slice HR tối thiểu — nghỉ phép → BPM/ủy quyền, chức danh, ca làm & chấm công, vào/nghỉ việc; hr-svc 8109; capability hr.* đề xuất chờ ký) ----
const HR_LEAVE_STATUSES = new Set(['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED', 'CANCELLED']);
const HR_EMPLOYEE_STATUSES = new Set(['ONBOARDING', 'ACTIVE', 'OFFBOARDING', 'LEFT']);
const hrRoute = createRoute({
  path: '/hr',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="hr.leave.self" moduleCode="hr">
      <Outlet />
    </RequireCapability>
  ),
});
const hrIndexRoute = createRoute({
  path: '/',
  getParentRoute: () => hrRoute,
  beforeLoad: () => {
    throw redirect({ to: '/hr/leave', search: {} });
  },
});
const hrLeaveRoute = createRoute({
  path: '/leave',
  getParentRoute: () => hrRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'approve' | 'all' | undefined; status?: LeaveStatus | undefined } => ({
    tab: s.tab === 'approve' || s.tab === 'all' ? s.tab : undefined,
    status: typeof s.status === 'string' && HR_LEAVE_STATUSES.has(s.status) ? (s.status as LeaveStatus) : undefined,
  }),
  component: featurePage('hr', 'HrLeavePage'),
});
const hrLeaveCreateRoute = createRoute({
  path: '/leave/new',
  getParentRoute: () => hrRoute,
  component: () => (
    <RequireCapability cap="hr.leave.request">
      <HrLeaveCreateLazy />
    </RequireCapability>
  ),
});
const hrLeaveDetailRoute = createRoute({
  path: '/leave/$leaveId',
  getParentRoute: () => hrRoute,
  component: featurePage('hr', 'HrLeaveDetailPage'),
});
const hrOpsRoute = createRoute({
  path: '/ops',
  getParentRoute: () => hrRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'shifts' | 'attendance' | 'employees' | undefined;
    from?: string | undefined;
    to?: string | undefined;
    status?: EmployeeStatus | undefined;
  } => ({
    tab: s.tab === 'shifts' || s.tab === 'attendance' || s.tab === 'employees' ? s.tab : undefined,
    from: str(s.from),
    to: str(s.to),
    status:
      typeof s.status === 'string' && HR_EMPLOYEE_STATUSES.has(s.status) ? (s.status as EmployeeStatus) : undefined,
  }),
  component: () => (
    <RequireCapability cap="hr.shift.view">
      <HrOpsLazy />
    </RequireCapability>
  ),
});
const hrTitlesRoute = createRoute({
  path: '/titles',
  getParentRoute: () => hrRoute,
  validateSearch: (s: Record<string, unknown>): { create?: boolean | undefined } => ({ create: flag(s.create) }),
  component: () => (
    <RequireCapability cap="hr.title.view">
      <HrTitlesLazy />
    </RequireCapability>
  ),
});
const HrLeaveCreateLazy = featurePage('hr', 'HrLeaveCreatePage');
const HrOpsLazy = featurePage('hr', 'HrOpsPage');
const HrTitlesLazy = featurePage('hr', 'HrTitlesPage');

// ---- KOVIX.Connect (Đợt 4: integration — khung connector, credential vault, mapping, webhook, job, dead-letter, API key; 3PL giả lập) ----
const CONNECTOR_STATUSES = new Set(['DRAFT', 'TESTING', 'ACTIVE', 'DEGRADED', 'PAUSED', 'REVOKED']);
const CONNECTOR_TYPES = new Set([
  'PAYMENT',
  'EINVOICE',
  'SOCIAL',
  'THREE_PL',
  'ACCOUNTING',
  'SMS',
  'ECOMMERCE',
  'BANK',
]);
const SYNC_MESSAGE_STATUSES = new Set(['SUCCESS', 'FAILED', 'RETRYING', 'DEAD_LETTER']);
const DEAD_LETTER_STATUSES = new Set(['OPEN', 'REPLAYED', 'SKIPPED']);
const API_KEY_STATUSES = new Set(['ACTIVE', 'REVOKED', 'EXPIRED']);
const connectRoute = createRoute({
  path: '/connect',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="integration.connector.view" moduleCode="integration">
      <Outlet />
    </RequireCapability>
  ),
});
const connectOverviewRoute = createRoute({
  path: '/',
  getParentRoute: () => connectRoute,
  component: featurePage('connect', 'ConnectOverviewPage'),
});
const connectConnectorsRoute = createRoute({
  path: '/connectors',
  getParentRoute: () => connectRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: ConnectorStatus | undefined; type?: ConnectorType | undefined } => ({
    status:
      typeof s.status === 'string' && CONNECTOR_STATUSES.has(s.status) ? (s.status as ConnectorStatus) : undefined,
    type: typeof s.type === 'string' && CONNECTOR_TYPES.has(s.type) ? (s.type as ConnectorType) : undefined,
  }),
  component: featurePage('connect', 'ConnectorsPage'),
});
const ConnectorCreateLazy = featurePage('connect', 'ConnectorCreatePage');
const connectConnectorCreateRoute = createRoute({
  path: '/connectors/new',
  getParentRoute: () => connectRoute,
  validateSearch: (s: Record<string, unknown>): { type?: ConnectorType | undefined } => ({
    type:
      typeof s.type === 'string' && (CONNECTOR_TYPES as ReadonlySet<string>).has(s.type)
        ? (s.type as ConnectorType)
        : undefined,
  }),
  component: () => (
    <RequireCapability cap="integration.connector.manage">
      <ConnectorCreateLazy />
    </RequireCapability>
  ),
});
const connectConnectorDetailRoute = createRoute({
  path: '/connectors/$connectorId',
  getParentRoute: () => connectRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'config' | 'credential' | 'mapping' | 'webhook' | 'jobs' | 'log' | 'history' | undefined } => ({
    tab:
      s.tab === 'credential' ||
      s.tab === 'mapping' ||
      s.tab === 'webhook' ||
      s.tab === 'jobs' ||
      s.tab === 'log' ||
      s.tab === 'history'
        ? s.tab
        : s.tab === 'config'
          ? 'config'
          : undefined,
  }),
  component: featurePage('connect', 'ConnectorDetailPage'),
});
const connectSyncLogRoute = createRoute({
  path: '/sync-log',
  getParentRoute: () => connectRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    view?: 'log' | 'dlq' | undefined;
    connectorId?: string | undefined;
    status?: SyncMessageStatus | undefined;
    direction?: 'IN' | 'OUT' | undefined;
    dlqStatus?: DeadLetterStatus | undefined;
    deadLetterId?: string | undefined;
  } => ({
    view: s.view === 'dlq' ? 'dlq' : s.view === 'log' ? 'log' : undefined,
    connectorId: str(s.connectorId),
    status:
      typeof s.status === 'string' && SYNC_MESSAGE_STATUSES.has(s.status) ? (s.status as SyncMessageStatus) : undefined,
    direction: s.direction === 'IN' || s.direction === 'OUT' ? s.direction : undefined,
    dlqStatus:
      typeof s.dlqStatus === 'string' && DEAD_LETTER_STATUSES.has(s.dlqStatus)
        ? (s.dlqStatus as DeadLetterStatus)
        : undefined,
    deadLetterId: str(s.deadLetterId),
  }),
  component: featurePage('connect', 'SyncLogPage'),
});
const connectApiKeysRoute = createRoute({
  path: '/api-keys',
  getParentRoute: () => connectRoute,
  validateSearch: (s: Record<string, unknown>): { status?: ApiKeyStatus | undefined } => ({
    status: typeof s.status === 'string' && API_KEY_STATUSES.has(s.status) ? (s.status as ApiKeyStatus) : undefined,
  }),
  component: featurePage('connect', 'ApiWebhookPage'),
});
const connectVaultRoute = createRoute({
  path: '/vault',
  getParentRoute: () => connectRoute,
  component: featurePage('connect', 'VaultPage'),
});
// R7.5 — thiết bị IoT (bí mật HMAC theo thiết bị, lấy một lần)
const connectIotDevicesRoute = createRoute({
  path: '/iot-devices',
  getParentRoute: () => connectRoute,
  component: featurePage('connect', 'TelemetryDevicesPage'),
});
// GĐ 3.9: vận đơn 3PL thật + đối soát drift
const DRIFT_STATUSES = new Set(['NONE', 'OPEN', 'RESOLVED']);

const connectDriftRoute = createRoute({
  path: '/drift',
  getParentRoute: () => connectRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { connectorId?: string | undefined; driftStatus?: DriftStatus | undefined; tripId?: string | undefined } => ({
    connectorId: str(s.connectorId),
    driftStatus:
      typeof s.driftStatus === 'string' && DRIFT_STATUSES.has(s.driftStatus)
        ? (s.driftStatus as DriftStatus)
        : undefined,
    tripId: str(s.tripId),
  }),
  component: featurePage('connect', 'DriftPage'),
});
// W7 — đơn đa kênh: sàn TMĐT / app giao đồ ăn (connector ECOMMERCE) — đơn sàn + ánh xạ SKU kênh
const MARKETPLACE_STATUSES = new Set(['RECEIVED', 'CREATED', 'SHIPPED', 'COMPLETED', 'REJECTED', 'CANCELLED']);
const connectMarketplaceRoute = createRoute({
  path: '/marketplace',
  getParentRoute: () => connectRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    connectorId?: string | undefined;
    status?: MarketplaceOrderSyncStatus | undefined;
    shortfall?: boolean | undefined;
    returns?: boolean | undefined;
    q?: string | undefined;
    syncId?: string | undefined;
  } => ({
    connectorId: str(s.connectorId),
    status:
      typeof s.status === 'string' && MARKETPLACE_STATUSES.has(s.status)
        ? (s.status as MarketplaceOrderSyncStatus)
        : undefined,
    shortfall: flag(s.shortfall),
    returns: flag(s.returns),
    q: str(s.q),
    syncId: str(s.syncId),
  }),
  component: featurePage('connect', 'MarketplaceOrdersPage'),
});
const connectMarketplaceListingsRoute = createRoute({
  path: '/marketplace/listings',
  getParentRoute: () => connectRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { connectorId?: string | undefined; status?: MarketplaceListingStatus | undefined; q?: string | undefined } => ({
    connectorId: str(s.connectorId),
    status: s.status === 'ACTIVE' || s.status === 'PAUSED' || s.status === 'UNMAPPED' ? s.status : undefined,
    q: str(s.q),
  }),
  component: featurePage('connect', 'MarketplaceListingsPage'),
});

// ---- KOVIX.Notify (GĐ 1.2: notify — rule engine bốn bước, hộp thư, tùy chọn cá nhân, mẫu, quy tắc, kênh, nhật ký, chiến dịch) ----
const NOTIFY_CATEGORIES = new Set([
  'ORDER',
  'DELIVERY',
  'RECEIVABLE',
  'PAYMENT',
  'AFFILIATE',
  'PROMOTION',
  'LOYALTY',
  'SUPPORT',
  'APPROVAL',
  'INVENTORY',
  'SYSTEM',
]);
const NOTIFY_CHANNELS = new Set(['INAPP', 'PUSH', 'ZNS', 'EMAIL', 'SMS', 'TELEGRAM', 'TEAMS']);
const TEMPLATE_STATUSES = new Set([
  'DRAFT',
  'PENDING_APPROVAL',
  'TESTING',
  'EFFECTIVE',
  'EXPIRED',
  'PAUSED',
  'REVOKED',
]);
const RULE_STATUSES = new Set(['DRAFT', 'EFFECTIVE', 'PAUSED', 'REVOKED']);
const ATTEMPT_STATUSES = new Set(['QUEUED', 'SENT', 'DELIVERED', 'FAILED', 'SKIPPED']);
const CAMPAIGN_STATUSES = new Set([
  'DRAFT',
  'PENDING_APPROVAL',
  'APPROVED',
  'SCHEDULED',
  'RUNNING',
  'PAUSED',
  'DONE',
  'CANCELLED',
]); // R6.11: + duyệt nội dung
const notifyRoute = createRoute({
  path: '/notifications',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="notify.preference.self" moduleCode="notify">
      <Outlet />
    </RequireCapability>
  ),
});
const NotifyOverviewLazy = featurePage('notify', 'NotifyOverviewPage');
const notifyOverviewRoute = createRoute({
  path: '/',
  getParentRoute: () => notifyRoute,
  component: () => (
    <RequireCapability cap="notify.notification.view">
      <NotifyOverviewLazy />
    </RequireCapability>
  ),
});
const notifyInboxRoute = createRoute({
  path: '/inbox',
  getParentRoute: () => notifyRoute,
  // T9-F2 (chéo T9A-2): + archived — chip "Đã lưu trữ" (InboxPage hiện còn state cục bộ; đổi sang search ở vùng fe/notify)
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    category?: NotificationCategory | undefined;
    unread?: boolean | undefined;
    notificationId?: string | undefined;
    archived?: boolean | undefined;
  } => ({
    category:
      typeof s.category === 'string' && NOTIFY_CATEGORIES.has(s.category)
        ? (s.category as NotificationCategory)
        : undefined,
    unread: s.unread === true || s.unread === 'true' ? true : undefined,
    notificationId: str(s.notificationId),
    archived: flag(s.archived),
  }),
  component: featurePage('notify', 'InboxPage'),
});
const notifyPreferencesRoute = createRoute({
  path: '/preferences',
  getParentRoute: () => notifyRoute,
  component: featurePage('notify', 'PreferencesPage'),
});
const notifyTemplatesRoute = createRoute({
  path: '/templates',
  getParentRoute: () => notifyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    category?: NotificationCategory | undefined;
    status?: TemplateStatus | undefined;
    channel?: NotificationChannel | undefined;
  } => ({
    category:
      typeof s.category === 'string' && NOTIFY_CATEGORIES.has(s.category)
        ? (s.category as NotificationCategory)
        : undefined,
    status: typeof s.status === 'string' && TEMPLATE_STATUSES.has(s.status) ? (s.status as TemplateStatus) : undefined,
    channel:
      typeof s.channel === 'string' && NOTIFY_CHANNELS.has(s.channel) ? (s.channel as NotificationChannel) : undefined,
  }),
  component: () => (
    <RequireCapability cap="notify.notification.view">
      <NotifyTemplatesLazy />
    </RequireCapability>
  ),
});
const NotifyTemplatesLazy = featurePage('notify', 'TemplatesPage');
const NotifyTemplateDetailLazy = featurePage('notify', 'TemplateDetailPage');
const notifyTemplateDetailRoute = createRoute({
  path: '/templates/$templateId',
  getParentRoute: () => notifyRoute,
  component: () => (
    <RequireCapability cap="notify.notification.view">
      <NotifyTemplateDetailLazy />
    </RequireCapability>
  ),
});
const NotifyRulesLazy = featurePage('notify', 'RulesPage');
const notifyRulesRoute = createRoute({
  path: '/rules',
  getParentRoute: () => notifyRoute,
  // T6-G1 (V-EN-33): `category` = lọc nhóm quy tắc
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    eventType?: string | undefined;
    status?: RuleStatus | undefined;
    category?: import('@kovix/api-client').NotificationCategory | undefined;
  } => ({
    eventType: str(s.eventType),
    status: typeof s.status === 'string' && RULE_STATUSES.has(s.status) ? (s.status as RuleStatus) : undefined,
    category:
      typeof s.category === 'string' && NOTIFY_CATEGORIES.has(s.category)
        ? (s.category as import('@kovix/api-client').NotificationCategory)
        : undefined,
  }),
  component: () => (
    <RequireCapability cap="notify.notification.view">
      <NotifyRulesLazy />
    </RequireCapability>
  ),
});
const NotifyChannelsLazy = featurePage('notify', 'ChannelsPage');
const notifyChannelsRoute = createRoute({
  path: '/channels',
  getParentRoute: () => notifyRoute,
  component: () => (
    <RequireCapability cap="notify.notification.view">
      <NotifyChannelsLazy />
    </RequireCapability>
  ),
});
const NotifyLogLazy = featurePage('notify', 'DeliveryLogPage');
const notifyLogRoute = createRoute({
  path: '/log',
  getParentRoute: () => notifyRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    channel?: NotificationChannel | undefined;
    status?: DeliveryAttemptStatus | undefined;
    notificationId?: string | undefined;
    /** TC-RPT-CMP-35-DRL: drill "Không gửi được" của RPT-CMP-35 — lọc chiến dịch + "FAILED,SKIPPED". */
    campaignId?: string | undefined;
    statuses?: string | undefined;
  } => ({
    channel:
      typeof s.channel === 'string' && NOTIFY_CHANNELS.has(s.channel) ? (s.channel as NotificationChannel) : undefined,
    status:
      typeof s.status === 'string' && ATTEMPT_STATUSES.has(s.status) ? (s.status as DeliveryAttemptStatus) : undefined,
    notificationId: str(s.notificationId),
    campaignId: str(s.campaignId),
    statuses:
      typeof s.statuses === 'string' && s.statuses.split(',').every((x) => ATTEMPT_STATUSES.has(x))
        ? s.statuses
        : undefined,
  }),
  component: () => (
    <RequireCapability cap="notify.notification.view">
      <NotifyLogLazy />
    </RequireCapability>
  ),
});
const NotifyCampaignsLazy = featurePage('notify', 'CampaignsPage');
const notifyCampaignsRoute = createRoute({
  path: '/campaigns',
  getParentRoute: () => notifyRoute,
  validateSearch: (s: Record<string, unknown>): { status?: CampaignStatus | undefined } => ({
    status: typeof s.status === 'string' && CAMPAIGN_STATUSES.has(s.status) ? (s.status as CampaignStatus) : undefined,
  }),
  component: () => (
    <RequireCapability cap="notify.notification.view">
      <NotifyCampaignsLazy />
    </RequireCapability>
  ),
});
const NotifyCampaignDetailLazy = featurePage('notify', 'CampaignDetailPage');
const notifyCampaignDetailRoute = createRoute({
  path: '/campaigns/$campaignId',
  getParentRoute: () => notifyRoute,
  component: () => (
    <RequireCapability cap="notify.notification.view">
      <NotifyCampaignDetailLazy />
    </RequireCapability>
  ),
});

// ---- KOVIX.File (GĐ 1.3: nền tảng dùng chung — kho tệp, xuất dữ liệu, mẫu in; không theo entitlement module) ----
const FILE_STATUSES = new Set(['PENDING', 'UPLOADED', 'SCANNING', 'CLEAN', 'INFECTED', 'DELETED']);
const EXPORT_STATUSES = new Set(['QUEUED', 'RUNNING', 'DONE', 'FAILED', 'CANCELLED', 'EXPIRED']);
const DOC_TEMPLATE_STATUSES = new Set([
  'DRAFT',
  'PENDING_APPROVAL',
  'TESTING',
  'EFFECTIVE',
  'EXPIRED',
  'PAUSED',
  'REVOKED',
]);
const filesRoute = createRoute({
  path: '/files',
  getParentRoute: () => shellRoute,
  // KOVIX-2911 (RJ-SYS-03): Admin / Hệ thống chỉ có file.file.manage (không view) — manage là tập trên của đọc (BR-18.105, file-svc anyOf)
  component: () => (
    <RequireCapability anyOf={['file.file.view', 'file.file.manage']}>
      <Outlet />
    </RequireCapability>
  ),
});
const filesIndexRoute = createRoute({
  path: '/',
  getParentRoute: () => filesRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    scope?: 'all' | undefined;
    module?: string | undefined;
    status?: FileStatus | undefined;
    fileId?: string | undefined;
  } => ({
    scope: s.scope === 'all' ? 'all' : undefined,
    module: str(s.module),
    status: typeof s.status === 'string' && FILE_STATUSES.has(s.status) ? (s.status as FileStatus) : undefined,
    fileId: str(s.fileId),
  }),
  component: featurePage('file', 'FilesPage'),
});
const FilesExportsLazy = featurePage('file', 'ExportsPage');
const filesExportsRoute = createRoute({
  path: '/exports',
  getParentRoute: () => filesRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { scope?: 'all' | undefined; status?: ExportJobStatus | undefined } => ({
    scope: s.scope === 'all' ? 'all' : undefined,
    status: typeof s.status === 'string' && EXPORT_STATUSES.has(s.status) ? (s.status as ExportJobStatus) : undefined,
  }),
  component: () => (
    <RequireCapability cap="file.export.run">
      <FilesExportsLazy />
    </RequireCapability>
  ),
});
const filesTemplatesRoute = createRoute({
  path: '/templates',
  getParentRoute: () => filesRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: DocumentTemplateStatus | undefined; templateId?: string | undefined } => ({
    status:
      typeof s.status === 'string' && DOC_TEMPLATE_STATUSES.has(s.status)
        ? (s.status as DocumentTemplateStatus)
        : undefined,
    templateId: str(s.templateId),
  }),
  component: featurePage('file', 'DocumentTemplatesPage'),
});

// ---- KOVIX.Audit (GĐ 1.4: /admin/audit — sổ bất biến, admin.audit.view) ----
const AUDIT_KINDS = new Set(['STATE', 'SENSITIVE', 'EXPORT', 'PII_ACCESS', 'AUTH', 'CONFIG', 'ACCESS_DENIED']);
const AuditLogLazy = featurePage('admin', 'AuditLogPage');
const adminAuditRoute = createRoute({
  path: '/admin/audit',
  getParentRoute: () => shellRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'chain' | 'exports' | 'pii' | undefined;
    kind?: AuditKind | undefined;
    outcome?: AuditOutcome | undefined;
    resourceType?: string | undefined;
    resourceId?: string | undefined;
    actor?: string | undefined;
    afterSeq?: number | undefined;
    entryId?: string | undefined;
  } => ({
    tab: s.tab === 'chain' || s.tab === 'exports' || s.tab === 'pii' ? s.tab : undefined,
    kind: typeof s.kind === 'string' && AUDIT_KINDS.has(s.kind) ? (s.kind as AuditKind) : undefined,
    outcome: s.outcome === 'DENIED' || s.outcome === 'FAILED' || s.outcome === 'SUCCESS' ? s.outcome : undefined,
    resourceType: str(s.resourceType),
    resourceId: str(s.resourceId),
    actor: str(s.actor),
    afterSeq:
      typeof s.afterSeq === 'number'
        ? s.afterSeq
        : typeof s.afterSeq === 'string' && /^\d+$/.test(s.afterSeq)
          ? Number(s.afterSeq)
          : undefined,
    entryId: str(s.entryId),
  }),
  component: () => (
    <RequireCapability cap="admin.audit.view" moduleCode="admin">
      <AuditLogLazy />
    </RequireCapability>
  ),
});

// GĐ 3.2: /admin/org (mockup "hr" — Tổ chức): cây đơn vị iam-svc + gán phạm vi dữ liệu (BR-18.16)
const OrgUnitsLazy = featurePage('admin', 'OrgUnitsPage');
const adminOrgRoute = createRoute({
  path: '/admin/org',
  getParentRoute: () => shellRoute,
  validateSearch: (s: Record<string, unknown>): { selected?: string | undefined; userId?: string | undefined } => ({
    selected: str(s.selected),
    userId: str(s.userId),
  }),
  component: () => (
    <RequireCapability cap="admin.user.view" moduleCode="admin">
      <OrgUnitsLazy />
    </RequireCapability>
  ),
});

// W1: /admin/users (taUsers/taUserDetail/taUserCreate) + /admin/roles (taRoles/taRoleDetail/taRoleCreate, rbacRoles, dataScope, taTrustedDevices) — iam-svc
const USER_STATUSES = new Set(['PENDING_ACTIVATION', 'ACTIVE', 'LOCKED', 'INACTIVE']);
const AdminUsersLazy = featurePage('admin', 'UsersPage');
const adminUsersRoute = createRoute({
  path: '/admin/users',
  getParentRoute: () => shellRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: AppUserStatus | undefined;
    roleId?: string | undefined;
    orgUnitId?: string | undefined;
    q?: string | undefined;
    create?: boolean | undefined;
  } => ({
    status: typeof s.status === 'string' && USER_STATUSES.has(s.status) ? (s.status as AppUserStatus) : undefined,
    roleId: str(s.roleId),
    orgUnitId: str(s.orgUnitId),
    q: str(s.q),
    create: flag(s.create),
  }),
  component: () => (
    <RequireCapability cap="admin.user.view" moduleCode="admin">
      <AdminUsersLazy />
    </RequireCapability>
  ),
});
// Y6: /admin/portal-access (mockup v152 portalAccess, 7 tab) — portal-svc + identity-svc. Capability portal.* thuộc C‑08 (chờ ký).
const PORTAL_ACCESS_TABS = new Set(['requests', 'accounts', 'business', 'ctv', 'policy', 'audit']);
const PORTAL_REG_STATUSES = new Set([
  'PENDING_MATCH',
  'PENDING_TENANT_ADMIN',
  'PENDING_BPM',
  'NEED_MORE_INFO',
  'APPROVED',
  'REJECTED',
]);
const PORTAL_WORKSPACE_TYPES = new Set(['PERSONAL', 'BUSINESS', 'AFFILIATE']);
const PortalAccessLazy = featurePage('admin', 'PortalAccessPage');
const adminPortalAccessRoute = createRoute({
  path: '/admin/portal-access',
  getParentRoute: () => shellRoute,
  validateSearch: (s: Record<string, unknown>): PortalAccessSearch => ({
    tab: typeof s.tab === 'string' && PORTAL_ACCESS_TABS.has(s.tab) ? (s.tab as PortalAccessSearch['tab']) : undefined,
    q: str(s.q),
    status:
      typeof s.status === 'string' && PORTAL_REG_STATUSES.has(s.status) ? (s.status as RegistrationStatus) : undefined,
    type: typeof s.type === 'string' && PORTAL_WORKSPACE_TYPES.has(s.type) ? (s.type as WorkspaceType) : undefined,
    requestId: str(s.requestId),
    accountId: str(s.accountId),
    organizationId: str(s.organizationId),
    invite: flag(s.invite),
  }),
  component: () => (
    <RequireCapability cap="portal.account.view" moduleCode="admin">
      <PortalAccessLazy />
    </RequireCapability>
  ),
});
const AdminUserDetailLazy = featurePage('admin', 'UserDetailPage');
const adminUserDetailRoute = createRoute({
  path: '/admin/users/$userId',
  getParentRoute: () => shellRoute,
  validateSearch: (s: Record<string, unknown>): { tab?: 'roles' | 'scope' | 'devices' | 'history' | undefined } => ({
    tab: s.tab === 'roles' || s.tab === 'scope' || s.tab === 'devices' || s.tab === 'history' ? s.tab : undefined,
  }),
  component: () => (
    <RequireCapability cap="admin.user.view" moduleCode="admin">
      <AdminUserDetailLazy />
    </RequireCapability>
  ),
});
const AdminRolesLazy = featurePage('admin', 'RolesPage');
const adminRolesRoute = createRoute({
  path: '/admin/roles',
  getParentRoute: () => shellRoute,
  // T3-F2 #35: tab=changes — hàng chờ "Thay đổi quyền chờ duyệt"
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'sod' | 'changes' | undefined;
    includeInactive?: boolean | undefined;
    create?: boolean | undefined;
    copyFrom?: string | undefined;
  } => ({
    tab: s.tab === 'sod' || s.tab === 'changes' ? s.tab : undefined,
    includeInactive: flag(s.includeInactive),
    create: flag(s.create),
    copyFrom: str(s.copyFrom),
  }),
  component: () => (
    <RequireCapability cap="admin.role.view" moduleCode="admin">
      <AdminRolesLazy />
    </RequireCapability>
  ),
});
const AdminRoleDetailLazy = featurePage('admin', 'RoleDetailPage');
const adminRoleDetailRoute = createRoute({
  path: '/admin/roles/$roleId',
  getParentRoute: () => shellRoute,
  validateSearch: (s: Record<string, unknown>): { tab?: 'users' | undefined } => ({
    tab: s.tab === 'users' ? 'users' : undefined,
  }),
  component: () => (
    <RequireCapability cap="admin.role.view" moduleCode="admin">
      <AdminRoleDetailLazy />
    </RequireCapability>
  ),
});

// ---- KOVIX.Payment (GĐ 2.1: sổ AR/AP — /payments, entitlement module payment, đọc = payment.receivable.view) ----
const PAYMENT_STATUSES = new Set([
  'INITIATED',
  'PENDING',
  'PARTIALLY_PAID',
  'PAID',
  'EXPIRED',
  'ADJUSTED',
  'CANCELLED',
  'FAILED',
  'VOIDED',
]);
const pStatus = (v: unknown) => (typeof v === 'string' && PAYMENT_STATUSES.has(v) ? (v as PaymentStatus) : undefined);
const flag = (v: unknown) => (v === true || v === 'true' ? true : undefined);
const paymentsRoute = createRoute({
  path: '/payments',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="payment.receivable.view" moduleCode="payment">
      <Outlet />
    </RequireCapability>
  ),
});
const paymentOverviewRoute = createRoute({
  path: '/',
  getParentRoute: () => paymentsRoute,
  component: featurePage('payment', 'PaymentOverviewPage'),
});
const paymentReceivablesRoute = createRoute({
  path: '/receivables',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    customerId?: string | undefined;
    status?: PaymentStatus | undefined;
    overdue?: boolean | undefined;
    receivableId?: string | undefined;
    orderId?: string | undefined;
  } => ({
    customerId: str(s.customerId),
    status: pStatus(s.status),
    overdue: flag(s.overdue),
    receivableId: str(s.receivableId),
    orderId: str(s.orderId),
  }),
  component: featurePage('payment', 'ReceivablesPage'),
});
const paymentReceiptsRoute = createRoute({
  path: '/receipts',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    customerId?: string | undefined;
    status?: PaymentStatus | undefined;
    cashPending?: boolean | undefined;
    receiptId?: string | undefined;
    create?: boolean | undefined;
    orderId?: string | undefined;
    channel?: 'OFFICE' | 'FIELD' | 'COD' | undefined;
    /** 28/10/2026 (KOVIX-3725, drill từ RPT-PAY-26 ô "Chưa đối soát"): false = phiếu chờ đối soát · true = đã ghi sổ. */
    reconciled?: boolean | undefined;
    /** Kỳ theo ngày hạch toán (YYYY-MM-DD) — lọc trên danh sách đã tải. */
    from?: string | undefined;
    to?: string | undefined;
  } => ({
    customerId: str(s.customerId),
    status: pStatus(s.status),
    cashPending: flag(s.cashPending),
    receiptId: str(s.receiptId),
    create: flag(s.create),
    orderId: str(s.orderId),
    // KOVIX-99 B5: chip "Thu hộ (COD)" — lọc máy chủ filter[channel]
    channel: s.channel === 'OFFICE' || s.channel === 'FIELD' || s.channel === 'COD' ? s.channel : undefined,
    reconciled:
      s.reconciled === true || s.reconciled === 'true'
        ? true
        : s.reconciled === false || s.reconciled === 'false'
          ? false
          : undefined,
    from: typeof s.from === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.from) ? s.from : undefined,
    to: typeof s.to === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.to) ? s.to : undefined,
  }),
  component: featurePage('payment', 'ReceiptsPage'),
});
const paymentCreditNotesRoute = createRoute({
  path: '/credit-notes',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    customerId?: string | undefined;
    status?: PaymentStatus | undefined;
    creditNoteId?: string | undefined;
    create?: boolean | undefined;
  } => ({
    customerId: str(s.customerId),
    status: pStatus(s.status),
    creditNoteId: str(s.creditNoteId),
    create: flag(s.create),
  }),
  component: featurePage('payment', 'CreditNotesPage'),
});
const paymentCashDepositsRoute = createRoute({
  path: '/cash-deposits',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    collector?: string | undefined;
    status?: DepositStatus | undefined;
    depositId?: string | undefined;
    create?: boolean | undefined;
  } => ({
    collector: str(s.collector),
    status: s.status === 'PENDING' || s.status === 'CONFIRMED' || s.status === 'REJECTED' ? s.status : undefined,
    depositId: str(s.depositId),
    create: flag(s.create),
  }),
  component: featurePage('payment', 'CashDepositsPage'),
});
/** Q3-b: liên kết chéo từ màn nguồn (hóa đơn NCC, quyết toán OEM, rút hoa hồng CTV) → phải trả của đúng chứng từ. */
const PAYABLE_SOURCES = new Set<string>([
  'SUPPLIER_INVOICE',
  'OEM_SETTLEMENT',
  'FARMER_SLIP',
  'EXPENSE',
  'ADJUSTMENT',
  'AFFILIATE_WITHDRAWAL',
  'CARRIER_FEE',
]);
const paymentPayablesRoute = createRoute({
  path: '/payables',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    partyId?: string | undefined;
    status?: PaymentStatus | undefined;
    overdue?: boolean | undefined;
    payableId?: string | undefined;
    sourceType?: PayableSourceType | undefined;
    sourceId?: string | undefined;
    create?: boolean | undefined;
  } => ({
    partyId: str(s.partyId),
    status: pStatus(s.status),
    overdue: flag(s.overdue),
    payableId: str(s.payableId),
    // mockup v152 P87 "+ Đề nghị chi" → hộp "Lập đề nghị chi" (chọn khoản phải trả nguồn rồi lập phiếu chi)
    create: flag(s.create),
    sourceType:
      typeof s.sourceType === 'string' && PAYABLE_SOURCES.has(s.sourceType)
        ? (s.sourceType as PayableSourceType)
        : undefined,
    sourceId: str(s.sourceId),
  }),
  component: featurePage('payment', 'PayablesPage'),
});
const paymentDisbursementsRoute = createRoute({
  path: '/disbursements',
  getParentRoute: () => paymentsRoute,
  validateSearch: (s: Record<string, unknown>): { status?: PaymentStatus | undefined } => ({
    status: pStatus(s.status),
  }),
  component: featurePage('payment', 'DisbursementsPage'),
});
const paymentSessionsRoute = createRoute({
  path: '/sessions',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    orderId?: string | undefined;
    customerId?: string | undefined;
    gateway?: string | undefined;
    status?: PaymentStatus | undefined;
    sessionId?: string | undefined;
    create?: boolean | undefined;
  } => ({
    orderId: str(s.orderId),
    customerId: str(s.customerId),
    gateway: str(s.gateway),
    status: pStatus(s.status),
    sessionId: str(s.sessionId),
    create: flag(s.create),
  }),
  component: featurePage('payment', 'GatewaySessionsPage'),
});
const PaymentCallbacksLazy = featurePage('payment', 'GatewayCallbacksPage');
const paymentCallbacksRoute = createRoute({
  path: '/callbacks',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { gateway?: string | undefined; outcome?: 'ACCEPTED' | 'REJECTED' | 'IGNORED' | undefined } => ({
    gateway: str(s.gateway),
    outcome: s.outcome === 'ACCEPTED' || s.outcome === 'REJECTED' || s.outcome === 'IGNORED' ? s.outcome : undefined,
  }),
  component: () => (
    <RequireCapability cap="payment.gateway_callback.view">
      <PaymentCallbacksLazy />
    </RequireCapability>
  ),
});
const paymentBankRoute = createRoute({
  path: '/bank',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'accounts' | undefined;
    accountId?: string | undefined;
    status?: 'UNMATCHED' | 'MATCHED' | 'MANUAL' | 'IGNORED' | undefined;
    createAccount?: boolean | undefined;
    ingest?: boolean | undefined;
  } => ({
    tab: s.tab === 'accounts' ? 'accounts' : undefined,
    accountId: str(s.accountId),
    status:
      s.status === 'UNMATCHED' || s.status === 'MATCHED' || s.status === 'MANUAL' || s.status === 'IGNORED'
        ? s.status
        : undefined,
    createAccount: flag(s.createAccount),
    ingest: flag(s.ingest),
  }),
  component: featurePage('payment', 'BankPage'),
});
const RECON_SOURCES = new Set(['COD_3PL', 'GATEWAY', 'MARKETPLACE', 'BANK']);
const PAYMENT_RECON_STATUSES = new Set(['DRAFT', 'MATCHED', 'VARIANCE', 'CLOSED', 'CANCELLED']);
const REFUND_STATUSES = new Set(['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED', 'CANCELLED']);
const paymentReconciliationsRoute = createRoute({
  path: '/reconciliations',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    source?: ReconciliationSource | undefined;
    status?: ReconciliationStatus | undefined;
    batchId?: string | undefined;
    create?: boolean | undefined;
  } => ({
    source:
      typeof s.source === 'string' && RECON_SOURCES.has(s.source) ? (s.source as ReconciliationSource) : undefined,
    status:
      typeof s.status === 'string' && PAYMENT_RECON_STATUSES.has(s.status)
        ? (s.status as ReconciliationStatus)
        : undefined,
    batchId: str(s.batchId),
    create: flag(s.create),
  }),
  component: featurePage('payment', 'ReconciliationsPage'),
});
const paymentRefundsRoute = createRoute({
  path: '/refunds',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    customerId?: string | undefined;
    status?: RefundStatus | undefined;
    refundId?: string | undefined;
    creditNoteId?: string | undefined;
    create?: boolean | undefined;
  } => ({
    customerId: str(s.customerId),
    status: typeof s.status === 'string' && REFUND_STATUSES.has(s.status) ? (s.status as RefundStatus) : undefined,
    refundId: str(s.refundId),
    creditNoteId: str(s.creditNoteId),
    create: flag(s.create),
  }),
  component: featurePage('payment', 'RefundsPage'),
});
const EINVOICE_STATUSES = new Set(['DRAFT', 'SIGNED', 'ISSUED', 'ACCEPTED', 'REJECTED', 'REPLACED', 'CANCELLED']);
const paymentEInvoicesRoute = createRoute({
  path: '/einvoices',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    customerId?: string | undefined;
    status?: EInvoiceStatus | undefined;
    orderId?: string | undefined;
    einvoiceId?: string | undefined;
    receivableId?: string | undefined;
    create?: boolean | undefined;
  } => ({
    customerId: str(s.customerId),
    status: typeof s.status === 'string' && EINVOICE_STATUSES.has(s.status) ? (s.status as EInvoiceStatus) : undefined,
    orderId: str(s.orderId),
    einvoiceId: str(s.einvoiceId),
    receivableId: str(s.receivableId),
    create: flag(s.create),
  }),
  component: featurePage('payment', 'EInvoicesPage'),
});
const paymentEInvoiceConfigRoute = createRoute({
  path: '/einvoices/config',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'buyers' | undefined; missing?: boolean | undefined; customerId?: string | undefined } => ({
    tab: s.tab === 'buyers' ? 'buyers' : undefined,
    missing: flag(s.missing),
    customerId: str(s.customerId),
  }),
  component: featurePage('payment', 'EInvoiceConfigPage'),
});
// W6‑d: màn lẻ Payment — nông hộ (payFarmerPayout), landed cost (payLandedCost, dữ liệu supply-svc), quỹ & tài khoản (payAccounts), dashboard tài chính (dashFinance)
const paymentFarmerPayoutsRoute = createRoute({
  path: '/farmer-payouts',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: PaymentStatus | undefined; overdue?: boolean | undefined } => ({
    status: pStatus(s.status),
    overdue: flag(s.overdue),
  }),
  component: featurePage('payment', 'FarmerPayoutPage'),
});
const LANDED_TYPES = new Set(['FREIGHT', 'DUTY', 'INSURANCE', 'HANDLING', 'INSPECTION']); // T9-F2 (chéo B4 SU-04): + INSPECTION "Kiểm định"
const PaymentLandedCostLazy = featurePage('payment', 'LandedCostPage');
const paymentLandedCostRoute = createRoute({
  path: '/landed-cost',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'allocated' | 'all' | undefined; type?: LandedCostType | undefined; receiptId?: string | undefined } => ({
    tab: s.tab === 'allocated' || s.tab === 'all' ? s.tab : undefined,
    type: typeof s.type === 'string' && LANDED_TYPES.has(s.type) ? (s.type as LandedCostType) : undefined,
    receiptId: str(s.receiptId),
  }),
  component: () => (
    <RequireCapability cap="purchase.purchase_order.view">
      <PaymentLandedCostLazy />
    </RequireCapability>
  ),
});
const paymentAccountsRoute = createRoute({
  path: '/accounts',
  getParentRoute: () => paymentsRoute,
  validateSearch: (s: Record<string, unknown>): { tab?: 'BANK' | 'CASH' | 'WALLET' | undefined } => ({
    tab: s.tab === 'BANK' || s.tab === 'CASH' || s.tab === 'WALLET' ? s.tab : undefined,
  }),
  component: featurePage('payment', 'PaymentAccountsPage'),
});
const paymentDashboardRoute = createRoute({
  path: '/dashboard',
  getParentRoute: () => paymentsRoute,
  component: featurePage('payment', 'FinanceDashboardPage'),
});
// ---- R7 (25/09/2026): sổ quỹ tiền mặt chi nhánh (T-08) + xuất bút toán ERP (D-24, chỉ đọc) ----
const paymentCashFundsRoute = createRoute({
  path: '/cash-funds',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { fundId?: string | undefined; tab?: 'entries' | 'counts' | undefined; create?: boolean | undefined } => ({
    fundId: str(s.fundId),
    tab: s.tab === 'entries' || s.tab === 'counts' ? s.tab : undefined,
    create: flag(s.create),
  }),
  component: featurePage('payment', 'CashFundsPage'),
});
const paymentJournalRoute = createRoute({
  path: '/journal',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { period?: string | undefined; tab?: 'preview' | 'mapping' | undefined } => ({
    period: typeof s.period === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(s.period) ? s.period : undefined,
    tab: s.tab === 'preview' || s.tab === 'mapping' ? s.tab : undefined,
  }),
  component: featurePage('payment', 'JournalExportPage'),
});
/** Q5 D6 — BR-17.1 đối chiếu sổ theo kỳ đã khóa (payment.reconciliation.execute). */
const paymentLedgerReconciliationRoute = createRoute({
  path: '/ledger-reconciliation',
  getParentRoute: () => paymentsRoute,
  validateSearch: (s: Record<string, unknown>): { period?: string | undefined } => ({
    period: typeof s.period === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(s.period) ? s.period : undefined,
  }),
  component: featurePage('payment', 'LedgerReconciliationPage'),
});
const paymentPeriodsRoute = createRoute({
  path: '/periods',
  getParentRoute: () => paymentsRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { status?: PeriodStatus | undefined; periodId?: string | undefined; create?: boolean | undefined } => ({
    status: s.status === 'OPEN' || s.status === 'CLOSING' || s.status === 'CLOSED' ? s.status : undefined,
    periodId: str(s.periodId),
    create: flag(s.create),
  }),
  component: featurePage('payment', 'PeriodsPage'),
});

// ---- W6‑e: lens Corporate Account theo vai trò — một component, 5 đường dẫn trong module tương ứng (mockup salesCorporateAccounts / creditCorporateAccounts / logiCorporateDelivery / mfgCorporateQuality / socialCorporateSegments) ----
const CorporateLensLazy = featureModule<typeof import('@/features/customer/pages/CorporateLensPage').CorporateLensPage>(
  'customer',
  'pages/CorporateLensPage',
  'CorporateLensPage',
);
type CorporateLensKind = 'sales' | 'finance' | 'warehouse' | 'quality' | 'marketing';
// biome-ignore lint/suspicious/noExplicitAny: cha là route module đã gate entitlement; kiểu cụ thể không quan trọng ở đây
const corporateLensRoute = (path: string, lens: CorporateLensKind, cap: string, parent: () => any) => {
  const route = createRoute({
    path,
    getParentRoute: parent,
    validateSearch: (s: Record<string, unknown>): { parent?: string | undefined } => ({ parent: str(s.parent) }),
    component: function CorporateLensRoute() {
      const search: { parent?: string | undefined } = route.useSearch();
      // navigate của CHÍNH route này (from = fullPath) — search reducer có kiểu { parent? } thay vì hợp mọi route
      const navigate = route.useNavigate();
      return (
        <RequireCapability cap={cap}>
          <CorporateLensLazy
            lens={lens}
            parentId={search.parent}
            onSelectParent={(id) => void navigate({ search: (prev) => ({ ...prev, parent: id }) })}
          />
        </RequireCapability>
      );
    },
  });
  return route;
};
const salesCorporateRoute = corporateLensRoute(
  '/corporate-accounts',
  'sales',
  'sales.sales_order.view',
  () => ordersRoute,
);
const creditCorporateRoute = corporateLensRoute(
  '/corporate-accounts',
  'finance',
  'credit.credit_profile.view',
  () => creditRoute,
);
const logisticsCorporateRoute = corporateLensRoute(
  '/corporate-delivery',
  'warehouse',
  'logistics.trip.view',
  () => logisticsRoute,
);
const productionCorporateRoute = corporateLensRoute(
  '/corporate-quality',
  'quality',
  'mfg.work_order.view',
  () => productionRoute,
);
const socialCorporateRoute = corporateLensRoute(
  '/corporate-segments',
  'marketing',
  'social.conversation.view',
  () => socialRoute,
);
// W6‑e: mockup loyBPM / loyBPMRuntime = cấu hình BPM Loyalty nằm trong Trung tâm BPM (/admin/bpm) — chuyển hướng, không dựng màn riêng
const loyaltyBpmRedirectRoute = createRoute({
  path: '/bpm',
  getParentRoute: () => loyaltyRoute,
  beforeLoad: () => {
    throw redirect({ to: '/admin/bpm' });
  },
  component: () => null,
});
const loyaltyBpmRuntimeRedirectRoute = createRoute({
  path: '/bpm/runtime',
  getParentRoute: () => loyaltyRoute,
  beforeLoad: () => {
    throw redirect({ to: '/admin/bpm/runtime' });
  },
  component: () => null,
});

// ---- W10: KOVIX.Insight (module `report`) — insight-svc + forecast-svc: hub /insight (6 tab), báo cáo, Builder, dashboard theo lens, lịch gửi, KPI, dự báo ----
const InsightCenterLazy = featurePage('insight', 'InsightCenterPage');
const ReportDetailLazy = featurePage('insight', 'ReportDetailPage');
const ReportBuilderLazy = featurePage('insight', 'ReportBuilderPage');
const ScheduleCreateLazy = featurePage('insight', 'ScheduleCreatePage');
const KpiCreateLazy = featurePage('insight', 'KpiCreatePage');
const InsightDashboardLazy = featurePage('insight', 'DashboardPage');
const ForecastLibraryLazy = featurePage('insight', 'ForecastLibraryPage');
const ForecastDetailLazy = featurePage('insight', 'ForecastDetailPage');
const INSIGHT_TABS = new Set<string>([
  'overview',
  'library',
  'indicators',
  'dashboards',
  'schedules',
  'kpi',
  'governance',
]); // R6.12: + indicators
const REPORT_CATEGORIES = new Set<string>([
  'EXECUTIVE',
  'SALES',
  'FINANCE',
  'INVENTORY',
  'PROCUREMENT',
  'PRODUCTION',
  'QUALITY',
  'OEM',
  'DMS',
  'CUSTOMER',
  'LEAD',
  'LOYALTY',
  'CARE',
  'LOGISTICS',
  'PEOPLE',
  'OPERATIONS',
]);
const GRAINS = new Set<string>(['DAY', 'WEEK', 'MONTH', 'QUARTER', 'YEAR']); // T9-F2 (chéo B1): + QUARTER/YEAR
const INSIGHT_COMPARE = new Set<string>(['PREV_PERIOD', 'YOY']);
const insightRoute = createRoute({
  path: '/insight',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="report.report.view" moduleCode="report">
      <Outlet />
    </RequireCapability>
  ),
});
const insightCenterRoute = createRoute({
  path: '/',
  getParentRoute: () => insightRoute,
  // R6.12: tab indicators + ind (mã chỉ số), from/to, pg (nhóm SP), ou (đơn vị), rg (vùng)
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'overview' | 'library' | 'indicators' | 'dashboards' | 'schedules' | 'kpi' | 'governance' | undefined;
    category?: ReportCategory | undefined;
    q?: string | undefined;
    scheduleId?: string | undefined;
    kpi?: string | undefined;
    ind?: string | undefined;
    from?: string | undefined;
    to?: string | undefined;
    pg?: string | undefined;
    ou?: string | undefined;
    rg?: string | undefined;
  } => ({
    tab: typeof s.tab === 'string' && INSIGHT_TABS.has(s.tab) ? (s.tab as 'overview') : undefined,
    category:
      typeof s.category === 'string' && REPORT_CATEGORIES.has(s.category) ? (s.category as ReportCategory) : undefined,
    q: str(s.q),
    scheduleId: str(s.scheduleId),
    kpi: str(s.kpi),
    ind: str(s.ind),
    from: str(s.from),
    to: str(s.to),
    pg: str(s.pg),
    ou: str(s.ou),
    rg: str(s.rg),
  }),
  component: InsightCenterLazy,
});
// 12/10/2026 (kiểm thử BƯỚC 7: "Không tìm thấy trang /insight/reports") — danh sách báo cáo nằm ở Trung tâm /insight; /insight/reports chuyển về đó
const insightReportsRedirectRoute = createRoute({
  path: '/reports',
  getParentRoute: () => insightRoute,
  beforeLoad: () => {
    throw redirect({ to: '/insight', search: {} });
  },
});
const insightReportDetailRoute = createRoute({
  path: '/reports/$code',
  getParentRoute: () => insightRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    from?: string | undefined;
    to?: string | undefined;
    grain?: Grain | undefined;
    dims?: string | undefined;
    measures?: string | undefined;
    filter?: string | undefined;
    compare?: InsightCompareMode | undefined;
    includeMerged?: string | undefined;
    /** Thanh lọc riêng của báo cáo — `presentation.filters` (gồm BRD 4.2 Phụ lục A): `dim:a|b;dim2:x` (`*` = người dùng chọn Tất cả ở tham số có mặc định). */
    pf?: string | undefined;
    pg?: string | undefined;
    br?: string | undefined;
    ch?: string | undefined;
    pop?: number | undefined;
  } => ({
    from: str(s.from),
    to: str(s.to),
    grain: typeof s.grain === 'string' && GRAINS.has(s.grain) ? (s.grain as Grain) : undefined,
    dims: str(s.dims),
    measures: str(s.measures),
    filter: str(s.filter),
    compare:
      typeof s.compare === 'string' && INSIGHT_COMPARE.has(s.compare) ? (s.compare as InsightCompareMode) : undefined,
    // RPT-LEAD-18 (insight V17): "Gồm lead đã gộp"
    includeMerged: s.includeMerged === '1' || s.includeMerged === 1 ? '1' : undefined,
    // Khung trình bày: thanh lọc riêng của báo cáo `vendor:a|b;status:DRAFT`
    pf: str(s.pf),
    // KOVIX-3284 RPT-MENU-04: Nhóm SP / Chi nhánh / Kênh / Ngưỡng phổ biến
    pg: str(s.pg),
    br: str(s.br),
    ch: str(s.ch),
    pop: typeof s.pop === 'number' ? s.pop : typeof s.pop === 'string' && s.pop !== '' ? Number(s.pop) : undefined,
  }),
  component: ReportDetailLazy,
});
const insightBuilderRoute = createRoute({
  path: '/builder',
  getParentRoute: () => insightRoute,
  validateSearch: (s: Record<string, unknown>): { code?: string | undefined } => ({ code: str(s.code) }),
  component: () => (
    <RequireCapability cap="report.report.build">
      <ReportBuilderLazy />
    </RequireCapability>
  ),
});
const insightScheduleCreateRoute = createRoute({
  path: '/schedules/new',
  getParentRoute: () => insightRoute,
  validateSearch: (s: Record<string, unknown>): { report?: string | undefined } => ({ report: str(s.report) }),
  component: () => (
    <RequireCapability cap="report.schedule.manage">
      <ScheduleCreateLazy />
    </RequireCapability>
  ),
});
const insightKpiCreateRoute = createRoute({
  path: '/kpis/new',
  getParentRoute: () => insightRoute,
  component: () => (
    <RequireCapability cap="report.kpi.manage">
      <KpiCreateLazy />
    </RequireCapability>
  ),
});
const InsightManualCostsLazy = featurePage('insight', 'ManualCostsPage'); // Q5-I D16: chi phí nhập tay — trang tự kiểm report.metric.manage | report.dashboard.finance
const insightManualCostsRoute = createRoute({
  path: '/manual-costs',
  getParentRoute: () => insightRoute,
  validateSearch: (): Record<string, never> => ({}),
  component: InsightManualCostsLazy,
});
const InsightSalesTargetsLazy = featurePage('insight', 'SalesTargetsPage'); // insight-v1 1.13.0 (RPT-SAL-33): chỉ tiêu doanh thu nhân viên × tháng
const insightSalesTargetsRoute = createRoute({
  path: '/sales-targets',
  getParentRoute: () => insightRoute,
  validateSearch: (): Record<string, never> => ({}),
  // cùng anyOf với GET /insight/sales-targets máy chủ — chặn ở route trước khi nạp trang lười (trang vẫn tự ẩn nút ghi khi thiếu report.kpi.manage)
  component: () => (
    <RequireCapability anyOf={['report.kpi.manage', 'report.dashboard.business']}>
      <InsightSalesTargetsLazy />
    </RequireCapability>
  ),
});
const insightDashboardRoute = createRoute({
  path: '/dashboards/$code',
  getParentRoute: () => insightRoute,
  // TC-DB-finance-FLT: ou = Chi nhánh (đơn vị phụ trách khách), cmp = So sánh (vắng = Kỳ trước)
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    from?: string | undefined;
    to?: string | undefined;
    ou?: string | undefined;
    cmp?: 'PREV_PERIOD' | 'YOY' | 'NONE' | undefined;
    df?: string | undefined;
    view?: 'MINE' | 'TEAM' | 'ALL' | undefined;
  } => ({
    from: str(s.from),
    to: str(s.to),
    ou: str(s.ou),
    // KOVIX-4257 (dashboard Hoa hồng bán hàng): Chế độ xem — máy chủ chỉ nhận lựa chọn trong phạm vi
    view: s.view === 'MINE' || s.view === 'TEAM' || s.view === 'ALL' ? s.view : undefined,
    cmp: s.cmp === 'PREV_PERIOD' || s.cmp === 'YOY' || s.cmp === 'NONE' ? s.cmp : undefined,
    // dashboard CSKH: bộ lọc riêng "chiều:giá trị;…" (máy chủ chỉ nhận khóa khai ở layout.filters)
    df: str(s.df),
  }),
  component: InsightDashboardLazy,
});
const insightForecastRoute = createRoute({
  path: '/forecast',
  getParentRoute: () => insightRoute,
  validateSearch: (): Record<string, never> => ({}),
  component: ForecastLibraryLazy,
});
const insightForecastDetailRoute = createRoute({
  path: '/forecast/$code',
  getParentRoute: () => insightRoute,
  validateSearch: (s: Record<string, unknown>): { runId?: string | undefined } => ({ runId: str(s.runId) }),
  component: ForecastDetailLazy,
});

// ---- W2: nhóm quyền SaaS — 17 màn xxxPermissions của mockup = một component, đường dẫn /<module>/permissions (routes.json `modulePermissions`) ----
const ModulePermissionsLazy = featureModule<
  typeof import('@/features/admin/components/ModulePermissions').ModulePermissionsPage
>('admin', 'components/ModulePermissions', 'ModulePermissionsPage');
type PermissionScreen = Parameters<
  typeof import('@/features/admin/components/ModulePermissions').ModulePermissionsPage
>[0]['screen'];
// biome-ignore lint/suspicious/noExplicitAny: cha là route module đã gate entitlement; kiểu cụ thể không quan trọng ở đây
const permissionsRoute = (screen: PermissionScreen, parent: () => any) =>
  createRoute({
    path: '/permissions',
    getParentRoute: parent,
    component: () => (
      <RequireCapability cap="admin.role.view">
        <ModulePermissionsLazy screen={screen} />
      </RequireCapability>
    ),
  });
const productPermissionsRoute = permissionsRoute('productPermissions', () => productsRoute);
const crmPermissionsRoute = permissionsRoute('crmPermissions', () => customersRoute);
const pricePermissionsRoute = permissionsRoute('pricePermissions', () => pricingRoute);
const creditPermissionsRoute = permissionsRoute('creditPermissions', () => creditRoute);
const salesPermissionsRoute = permissionsRoute('salesPermissions', () => ordersRoute);
const purchasePermissionsRoute = permissionsRoute('purchasePermissions', () => supplyRoute);
const invPermissionsRoute = permissionsRoute('invPermissions', () => inventoryRoute);
const mfgPermissionsRoute = permissionsRoute('mfgPermissions', () => productionRoute);
const payPermissionsRoute = permissionsRoute('payPermissions', () => paymentsRoute);
const logiPermissionsRoute = permissionsRoute('logiPermissions', () => logisticsRoute);
const oemPermissionsRoute = permissionsRoute('oemPermissions', () => oemRoute);
const socialPermissionsRoute = permissionsRoute('socialPermissions', () => socialRoute);
const loyPermissionsRoute = permissionsRoute('loyPermissions', () => loyaltyRoute);
const asPermissionsRoute = permissionsRoute('asPermissions', () => careRoute);
const dmsPermissionsRoute = permissionsRoute('dmsPermissions', () => dmsRoute);
const ntfPermissionsRoute = permissionsRoute('ntfPermissions', () => notifyRoute);
const intgPermissionsRoute = permissionsRoute('intgPermissions', () => connectRoute);
const rptPermissionsRoute = permissionsRoute('rptPermissions', () => insightRoute); // W10

// W2: roleHub (bản đồ nghiệp vụ & vai trò) + accountProfile (hồ sơ cá nhân) — mọi người dùng đăng nhập
const roleHubRoute = createRoute({
  path: '/role-hub',
  getParentRoute: () => shellRoute,
  component: featurePage('admin', 'RoleHubPage'),
});
const accountRoute = createRoute({
  path: '/account',
  getParentRoute: () => shellRoute,
  validateSearch: (s: Record<string, unknown>): { tab?: 'security' | 'history' | 'devices' | undefined } => ({
    tab: s.tab === 'security' || s.tab === 'history' || s.tab === 'devices' ? s.tab : undefined,
  }),
  component: featurePage('admin', 'AccountProfilePage'),
});

// ---- W8: control plane 5.2 — admin center nền tảng (/platform/**, tenant-svc + billing-svc) ----
const PlatformDashboardLazy = featurePage('platform', 'PlatformDashboardPage');
const PlatformTenantsLazy = featurePage('platform', 'TenantsPage');
const PlatformTenantCreateLazy = featurePage('platform', 'TenantCreatePage');
const PlatformChangesLazy = featurePage('platform', 'SubscriptionChangesPage');
const PlatformTenantDetailLazy = featurePage('platform', 'TenantDetailPage');
const PlatformPlansLazy = featurePage('platform', 'PlansPage');
const PlatformPlanApprovalsLazy = featurePage('platform', 'PlanApprovalsPage');
const PlatformPlanConfigLazy = featurePage('platform', 'PlanConfigPage');
const PlatformAddonsLazy = featurePage('platform', 'AddonsPage');
const PlatformInvoicesLazy = featurePage('platform', 'InvoicesPage');
const PlatformInvoiceDetailLazy = featurePage('platform', 'InvoiceDetailPage');
const TenantAdminDashboardLazy = featurePage('admin', 'TenantAdminDashboardPage');
const TenantProfileLazy = featurePage('admin', 'TenantProfilePage');
const TenantConfigLazy = featurePage('admin', 'TenantConfigPage');
const MySubscriptionLazy = featurePage('admin', 'MySubscriptionPage');
const EntitlementsLazy = featurePage('admin', 'EntitlementsPage');
const BranchesLazy = featurePage('admin', 'BranchesPage');
// W9 — platform-ops đầy đủ + monitoring-svc
const PlatformMonitoringLazy = featurePage('platform', 'MonitoringPage');
const PlatformSupportLazy = featurePage('platform', 'SupportSessionsPage');
const PlatformConfigLazy = featurePage('platform', 'PlatformConfigPage');
const TENANT_STATUSES = new Set<string>(['PROVISIONING', 'ACTIVE', 'SUSPENDED', 'TERMINATING', 'TERMINATED']);
const SUBSCRIPTION_CHANGE_STATUSES = new Set<string>(['PENDING', 'APPROVED', 'REJECTED', 'APPLIED', 'CANCELLED']);
const BILLING_PLAN_STATUSES = new Set<string>(['DRAFT', 'ACTIVE', 'HIDDEN', 'RETIRED']);
const SAAS_INVOICE_STATUSES = new Set<string>(['DRAFT', 'ISSUED', 'PARTIALLY_PAID', 'PAID', 'OVERDUE', 'VOID']); // R7.7 thêm PARTIALLY_PAID
type TenantDetailTab = 'overview' | 'provisioning' | 'subscription' | 'entitlements' | 'config' | 'billing' | 'history';
const TENANT_TABS = new Set<string>([
  'overview',
  'provisioning',
  'subscription',
  'entitlements',
  'config',
  'billing',
  'history',
]);
const platformDashboardRoute = createRoute({
  path: '/platform',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="platform.tenant.overview">
      <PlatformDashboardLazy />
    </RequireCapability>
  ),
});
const platformTenantsRoute = createRoute({
  path: '/platform/tenants',
  getParentRoute: () => shellRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    status?: TenantStatus | undefined;
    plan?: string | undefined;
    industry?: string | undefined;
    q?: string | undefined;
  } => ({
    status: typeof s.status === 'string' && TENANT_STATUSES.has(s.status) ? (s.status as TenantStatus) : undefined,
    plan: str(s.plan),
    industry: str(s.industry),
    q: str(s.q),
  }),
  component: () => (
    <RequireCapability cap="platform.tenant.view">
      <PlatformTenantsLazy />
    </RequireCapability>
  ),
});
const platformTenantCreateRoute = createRoute({
  path: '/platform/tenants/new',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="platform.tenant.manage">
      <PlatformTenantCreateLazy />
    </RequireCapability>
  ),
});
const platformChangesRoute = createRoute({
  path: '/platform/tenants/changes',
  getParentRoute: () => shellRoute,
  validateSearch: (s: Record<string, unknown>): { status?: SubscriptionChangeStatus | undefined } => ({
    status:
      typeof s.status === 'string' && SUBSCRIPTION_CHANGE_STATUSES.has(s.status)
        ? (s.status as SubscriptionChangeStatus)
        : undefined,
  }),
  component: () => (
    <RequireCapability cap="platform.tenant.view">
      <PlatformChangesLazy />
    </RequireCapability>
  ),
});
const platformTenantDetailRoute = createRoute({
  path: '/platform/tenants/$tenantId',
  getParentRoute: () => shellRoute,
  validateSearch: (s: Record<string, unknown>): { tab?: TenantDetailTab | undefined } => ({
    tab: typeof s.tab === 'string' && TENANT_TABS.has(s.tab) ? (s.tab as TenantDetailTab) : undefined,
  }),
  component: () => (
    <RequireCapability cap="platform.tenant.view">
      <PlatformTenantDetailLazy />
    </RequireCapability>
  ),
});
const platformPlansRoute = createRoute({
  path: '/platform/plans',
  getParentRoute: () => shellRoute,
  validateSearch: (s: Record<string, unknown>): { status?: PlanStatus | undefined } => ({
    status: typeof s.status === 'string' && BILLING_PLAN_STATUSES.has(s.status) ? (s.status as PlanStatus) : undefined,
  }),
  component: () => (
    <RequireCapability cap="platform.billing.view">
      <PlatformPlansLazy />
    </RequireCapability>
  ),
});
const platformPlanApprovalsRoute = createRoute({
  path: '/platform/plans/approvals',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="platform.billing.view">
      <PlatformPlanApprovalsLazy />
    </RequireCapability>
  ),
});
const platformPlanConfigRoute = createRoute({
  path: '/platform/plans/$planCode',
  getParentRoute: () => shellRoute,
  validateSearch: (s: Record<string, unknown>): { versionId?: string | undefined } => ({ versionId: str(s.versionId) }),
  component: () => (
    <RequireCapability cap="platform.billing.view">
      <PlatformPlanConfigLazy />
    </RequireCapability>
  ),
});
const platformAddonsRoute = createRoute({
  path: '/platform/addons',
  getParentRoute: () => shellRoute,
  validateSearch: (): Record<string, never> => ({}),
  component: () => (
    <RequireCapability cap="platform.billing.view">
      <PlatformAddonsLazy />
    </RequireCapability>
  ),
});
const platformInvoicesRoute = createRoute({
  path: '/platform/invoices',
  getParentRoute: () => shellRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { tenantId?: string | undefined; status?: SaasInvoiceStatus | undefined; period?: string | undefined } => ({
    tenantId: str(s.tenantId),
    status:
      typeof s.status === 'string' && SAAS_INVOICE_STATUSES.has(s.status) ? (s.status as SaasInvoiceStatus) : undefined,
    period: str(s.period),
  }),
  component: () => (
    <RequireCapability cap="platform.billing.view">
      <PlatformInvoicesLazy />
    </RequireCapability>
  ),
});
const platformInvoiceDetailRoute = createRoute({
  path: '/platform/invoices/$invoiceId',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="platform.billing.view">
      <PlatformInvoiceDetailLazy />
    </RequireCapability>
  ),
});
// ---- W9: giám sát (6 tab), phiên hỗ trợ, tham số hệ thống (monitoring-svc + platform-ops-svc) ----
const MON_TABS = new Set<string>(['overview', 'alerts', 'incidents', 'slo', 'resources', 'settings']);
const SUPPORT_TABS = new Set<string>(['queue', 'mine', 'all']);
const CONFIG_TABS = new Set<string>(['flags', 'runbooks', 'jobs', 'holidays', 'areas']); // R6.13: + lịch nghỉ dùng chung · Q5-H: + mã địa giới ↔ 3PL
const platformMonitoringRoute = createRoute({
  path: '/platform/monitoring',
  getParentRoute: () => shellRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'overview' | 'alerts' | 'incidents' | 'slo' | 'resources' | 'settings' | undefined;
    alertId?: string | undefined;
    incidentId?: string | undefined;
    severity?: string | undefined;
    status?: string | undefined;
    q?: string | undefined;
    ruleCode?: string | undefined;
  } => ({
    tab: typeof s.tab === 'string' && MON_TABS.has(s.tab) ? (s.tab as 'overview') : undefined,
    alertId: str(s.alertId),
    incidentId: str(s.incidentId),
    severity: str(s.severity),
    status: str(s.status),
    q: str(s.q),
    ruleCode: str(s.ruleCode),
  }),
  component: () => (
    <RequireCapability cap="platform.operations.view">
      <PlatformMonitoringLazy />
    </RequireCapability>
  ),
});
// R5.2 — Trung tâm BPM nền tảng + việc của quản trị nền tảng (chỉ đọc, /api/v1/platform/bpm)
const PlatformBpmLazy = featurePage('platform', 'PlatformBpmPage');
const PlatformWorkInboxLazy = featurePage('platform', 'PlatformWorkInboxPage');
const platformBpmRoute = createRoute({
  path: '/platform/bpm',
  getParentRoute: () => shellRoute,
  // Tồn đọng P (bpm-v1 1.4.0, K-25) — tab "Theo tenant": kỳ from/to (ngày nghiệp vụ) + lọc một tenant
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'templates' | 'instances' | 'tenants' | undefined;
    from?: string | undefined;
    to?: string | undefined;
    tenant?: string | undefined;
  } => ({
    tab: s.tab === 'instances' || s.tab === 'tenants' ? s.tab : undefined,
    from: typeof s.from === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.from) ? s.from : undefined,
    to: typeof s.to === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.to) ? s.to : undefined,
    tenant: str(s.tenant),
  }),
  component: () => (
    <RequireCapability cap="platform.automation.view">
      <PlatformBpmLazy />
    </RequireCapability>
  ),
});
const platformWorkInboxRoute = createRoute({
  path: '/platform/work-inbox',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="platform.automation.view">
      <PlatformWorkInboxLazy />
    </RequireCapability>
  ),
});
// T9 A43 — nhật ký control plane + thao tác trong phiên hỗ trợ tenant (audit-svc /api/v1/platform/audit, platform.audit.view)
const PlatformAuditLazy = featurePage('platform', 'PlatformAuditPage');
const PLATFORM_AUDIT_SCOPES = new Set<string>(['CONTROL_PLANE', 'SUPPORT']);
const platformAuditRoute = createRoute({
  path: '/platform/audit',
  getParentRoute: () => shellRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    scope?: 'CONTROL_PLANE' | 'SUPPORT' | undefined;
    tenantId?: string | undefined;
    kind?: AuditKind | undefined;
    sessionId?: string | undefined;
  } => ({
    scope: typeof s.scope === 'string' && PLATFORM_AUDIT_SCOPES.has(s.scope) ? (s.scope as 'CONTROL_PLANE') : undefined,
    tenantId: str(s.tenantId),
    kind: typeof s.kind === 'string' && AUDIT_KINDS.has(s.kind) ? (s.kind as AuditKind) : undefined,
    sessionId: str(s.sessionId),
  }),
  component: () => (
    <RequireCapability cap="platform.audit.view">
      <PlatformAuditLazy />
    </RequireCapability>
  ),
});
const platformSupportRoute = createRoute({
  path: '/platform/support',
  getParentRoute: () => shellRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: 'queue' | 'mine' | 'all' | undefined;
    sessionId?: string | undefined;
    status?: string | undefined;
    tenantId?: string | undefined;
  } => ({
    tab: typeof s.tab === 'string' && SUPPORT_TABS.has(s.tab) ? (s.tab as 'queue') : undefined,
    sessionId: str(s.sessionId),
    status: str(s.status),
    tenantId: str(s.tenantId),
  }),
  component: () => (
    <RequireCapability cap="platform.operations.view">
      <PlatformSupportLazy />
    </RequireCapability>
  ),
});
const platformConfigRoute = createRoute({
  path: '/platform/config',
  getParentRoute: () => shellRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'flags' | 'runbooks' | 'jobs' | 'holidays' | 'areas' | undefined; code?: string | undefined } => ({
    tab: typeof s.tab === 'string' && CONFIG_TABS.has(s.tab) ? (s.tab as 'flags') : undefined,
    code: str(s.code),
  }),
  component: () => (
    <RequireCapability cap="platform.operations.view">
      <PlatformConfigLazy />
    </RequireCapability>
  ),
});

// ---- W8: tenant tự phục vụ (/admin, /admin/profile|config|subscription|entitlements|branches — tenant-svc + billing-svc) ----
type MyPlanTab = 'plan' | 'upgrade' | 'addons' | 'invoices';
const adminDashboardRoute = createRoute({
  path: '/admin',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="admin.tenant.overview" moduleCode="admin">
      <TenantAdminDashboardLazy />
    </RequireCapability>
  ),
});
const adminProfileRoute = createRoute({
  path: '/admin/profile',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="admin.tenant.overview" moduleCode="admin">
      <TenantProfileLazy />
    </RequireCapability>
  ),
});
// R5.3 (mockup intgChannels) — kênh Portal/App theo tenant: cấu hình tenant nên gác admin.config.view + module admin, KHÔNG phụ thuộc module integration (phản biện R5 #9)
const PortalChannelsLazy = featurePage('connect', 'PortalChannelsPage');
const adminPortalChannelsRoute = createRoute({
  path: '/admin/portal-channels',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="admin.config.view" moduleCode="admin">
      <PortalChannelsLazy />
    </RequireCapability>
  ),
});
const adminConfigRoute = createRoute({
  path: '/admin/config',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="admin.config.view" moduleCode="admin">
      <TenantConfigLazy />
    </RequireCapability>
  ),
});
const adminSubscriptionRoute = createRoute({
  path: '/admin/subscription',
  getParentRoute: () => shellRoute,
  validateSearch: (s: Record<string, unknown>): { tab?: MyPlanTab | undefined; invoiceId?: string | undefined } => ({
    tab: s.tab === 'plan' || s.tab === 'upgrade' || s.tab === 'addons' || s.tab === 'invoices' ? s.tab : undefined,
    invoiceId: str(s.invoiceId),
  }),
  component: () => (
    <RequireCapability cap="admin.subscription.view" moduleCode="admin">
      <MySubscriptionLazy />
    </RequireCapability>
  ),
});
const adminEntitlementsRoute = createRoute({
  path: '/admin/entitlements',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="admin.tenant.overview" moduleCode="admin">
      <EntitlementsLazy />
    </RequireCapability>
  ),
});
const adminBranchesRoute = createRoute({
  path: '/admin/branches',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="admin.tenant.overview" moduleCode="admin">
      <BranchesLazy />
    </RequireCapability>
  ),
});
// X2 — taBackup: cam kết RPO/RTO + tình trạng phục hồi của tenant (BR-18.11/18.12), chỉ đọc
const RecoveryLazy = featurePage('admin', 'RecoveryPage');
const adminRecoveryRoute = createRoute({
  path: '/admin/recovery',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="admin.tenant.overview" moduleCode="admin">
      <RecoveryLazy />
    </RequireCapability>
  ),
});
// X3 — tnSecurity (tenant): chính sách bảo mật có phiên bản BR-18.134; tnUsers/tnPermissions/tnSecurity (platform): /platform/access?tab=users|roles|security (iam-svc /api/v1/platform/iam)
const SecurityPolicyLazy = featurePage('admin', 'SecurityPolicyPage');
const adminSecurityRoute = createRoute({
  path: '/admin/security',
  getParentRoute: () => shellRoute,
  component: () => (
    <RequireCapability cap="admin.config.view" moduleCode="admin">
      <SecurityPolicyLazy />
    </RequireCapability>
  ),
});
const PlatformAccessLazy = featurePage('platform', 'PlatformAccessPage');
const ACCESS_TABS = new Set<string>(['users', 'roles', 'security', 'identity', 'changes']); // Q1 A4: + identity (khóa toàn cục KOVIX ID) · T3-F2: + changes (hàng chờ hai người duyệt)
const platformAccessRoute = createRoute({
  path: '/platform/access',
  getParentRoute: () => shellRoute,
  validateSearch: (
    s: Record<string, unknown>,
  ): { tab?: 'users' | 'roles' | 'security' | 'identity' | 'changes' | undefined; userId?: string | undefined } => ({
    tab: typeof s.tab === 'string' && ACCESS_TABS.has(s.tab) ? (s.tab as 'users') : undefined,
    userId: str(s.userId),
  }),
  component: () => (
    <RequireCapability cap="platform.admin_user.view">
      <PlatformAccessLazy />
    </RequireCapability>
  ),
});

// T6 (quyết định #17, 29/09/2026): mọi mục menu (routes.json tenant + platform) đều đã có trang thật — bỏ route giữ chỗ
// `ModulePlaceholderPage` (đã xóa). Test `src/pages/t6NavRoutes.test.ts` chặn mục menu mới không có route thật (thay vì hiện trang "đang xây").

const routeTree = rootRoute.addChildren([
  authCallbackRoute,
  shellRoute.addChildren([
    dashboardRoute,
    fbOpsRoute,
    workInboxRoute,
    deniedRoute,
    productsRoute.addChildren([
      productListRoute,
      productCreateRoute,
      productImportRoute,
      productAttributesRoute,
      productSourcingRoute,
      productTranslationsRoute,
      productDashboardRoute,
      productCategoriesRoute,
      productUomsRoute,
      productMasterRoute,
      productTaxonomyRoute,
      productPackagingRoute,
      productApprovalsRoute,
      productLifecycleRoute,
      productBundlesRoute,
      productDetailRoute,
      productPermissionsRoute,
    ]),
    customersRoute.addChildren([
      customerListRoute,
      customerCreateRoute,
      customerImportRoute,
      customerDedupRoute,
      customerOrganizationsRoute,
      customerApprovalsRoute,
      customerCatalogRoute,
      customerTiersRoute,
      customerOrgAccountsRoute,
      customerActivitiesRoute,
      customerDataAuditRoute,
      customerSegmentsRoute,
      crmPipelineRoute,
      crmDealRoute,
      crmCampaignsRoute,
      crmTicketsRoute,
      customerContractsRoute,
      customerPrivacyRequestsRoute,
      customerDetailRoute,
      crmPermissionsRoute,
    ]),
    pricingRoute.addChildren([
      priceListsRoute,
      promotionsRoute,
      priceResolveRoute,
      pricingApprovalsRoute,
      pricingDashboardRoute,
      pricingRebatesRoute,
      pricingSpecialRequestsRoute,
      pricingMarginsRoute,
      priceListDetailRoute,
      pricePermissionsRoute,
    ]),
    creditRoute.addChildren([
      creditOverviewRoute,
      creditApprovalsRoute,
      creditOverridesRoute,
      creditPolicyRoute,
      creditAgingRoute,
      creditDunningRoute,
      creditInstallmentsRoute,
      creditReconRoute,
      creditLedgerRoute,
      creditCorporateRoute,
      creditProfileRoute,
      creditPermissionsRoute,
    ]),
    inventoryRoute.addChildren([
      inventoryOverviewRoute,
      inventorySearchRoute,
      inventoryRmaQuarantinesRoute,
      inventoryDeliveryReturnDuplicatesRoute,
      inventoryVansRoute,
      inventoryVouchersRoute,
      inventoryVoucherDetailRoute,
      inventoryPicksRoute,
      inventoryOrderQueueRoute,
      inventoryPickDetailRoute,
      inventoryAtpRoute,
      inventoryLotsRoute,
      inventoryWarehouseRoute,
      inventoryTransfersRoute,
      inventoryTransferDetailRoute,
      inventoryCountsRoute,
      inventoryCountDetailRoute,
      inventoryAlertsRoute,
      inventoryLotDetailRoute,
      inventoryDashboardRoute,
      inventoryWastageRoute,
      inventoryLabelsRoute,
      invPermissionsRoute,
    ]),
    ordersRoute.addChildren([
      orderListRoute,
      orderCreateRoute,
      orderQuickRoute,
      orderChangesRoute,
      orderRfqRoute,
      orderAllocationsRoute,
      orderApprovalsRoute,
      orderFulfillmentRoute,
      orderExceptionsRoute,
      orderDashboardRoute,
      orderImportRoute,
      orderQuotesRoute,
      orderQuoteApprovalsRoute,
      orderQuoteDetailRoute,
      orderContractsRoute,
      orderContractDetailRoute,
      orderRecurringRoute,
      orderRecurringApprovalsRoute,
      orderRecurringDetailRoute,
      orderCartsRoute,
      orderReturnsRoute,
      orderReturnDetailRoute,
      orderDetailRoute,
      orderEditRoute,
      salesCorporateRoute,
      salesPermissionsRoute,
    ]),
    supplyRoute.addChildren([
      supplyOverviewRoute,
      supplySuppliersRoute,
      supplySupplierDetailRoute,
      supplyRequestsRoute,
      supplyRequestConsolidateRoute,
      supplyRequestDetailRoute,
      supplyOrdersRoute,
      supplyOrderCreateRoute,
      supplyRfqsRoute,
      supplyRfqDetailRoute,
      supplyContractsRoute,
      supplyContractDetailRoute,
      supplyEvaluationsRoute,
      supplyEvaluationDetailRoute,
      supplyRevisionsRoute,
      supplyCorporateDemandRoute,
      supplyOrderDetailRoute,
      supplyReceiptsRoute,
      supplyReceiptDetailRoute,
      supplyMatchesRoute,
      supplyPricesRoute,
      supplyFarmerSlipsRoute,
      supplyFarmerSlipDetailRoute,
      purchasePermissionsRoute,
    ]),
    logisticsRoute.addChildren([
      logisticsOverviewRoute,
      logisticsTripsRoute,
      logisticsTripCreateRoute,
      logisticsTripDetailRoute,
      logisticsShipmentsRoute,
      logisticsShipmentDetailRoute,
      logisticsExceptionsRoute,
      logisticsFleetRoute,
      logisticsRoutingRoute,
      logisticsTripEditRoute,
      logisticsDeliveryNoteRoute,
      logisticsVehicleProfileRoute,
      logisticsReconcileRoute,
      logisticsColdChainRoute,
      logisticsCorporateRoute,
      logiPermissionsRoute,
    ]),
    productionRoute.addChildren([
      productionOverviewRoute,
      productionBomRoute,
      productionRoutingRoute,
      productionQcReportRoute,
      productionWorkOrdersRoute,
      productionWorkOrderCreateRoute,
      productionWorkOrderDetailRoute,
      productionWorkOrderLabelsRoute,
      productionQcRoute,
      productionTraceRoute,
      productionFactoriesRoute,
      productionWorkOrderEditRoute,
      productionPlansRoute,
      productionPlanDetailRoute,
      productionYieldRoute,
      productionNcrRoute,
      productionNcrDetailRoute,
      productionCorporateRoute,
      mfgPermissionsRoute,
    ]),
    oemRoute.addChildren([
      oemOverviewRoute,
      oemOrdersRoute,
      oemOrderCreateRoute,
      oemOrderDetailRoute,
      oemOrderEditRoute,
      oemApprovalsRoute,
      oemContractsRoute,
      oemContractDetailRoute,
      oemVendorStockRoute,
      oemSettlementsRoute,
      oemVendorsRoute,
      oemPermissionsRoute,
    ]),
    socialRoute.addChildren([
      socialOverviewRoute,
      socialInboxRoute,
      socialLeadsRoute,
      socialLeadDetailRoute,
      socialChannelsRoute,
      socialLiveRoute,
      socialCommerceRoute,
      socialProtectionRoute,
      socialCorporateRoute,
      socialPermissionsRoute,
    ]),
    loyaltyRoute.addChildren([
      loyaltyOverviewRoute,
      loyaltyProgramRoute,
      loyaltyMembersRoute,
      loyaltyMemberDetailRoute,
      loyaltyRewardsRoute,
      loyaltyAffiliatesRoute,
      loyaltyAffiliateDetailRoute,
      loyaltyReportedOrdersRoute,
      loyaltyCommissionRoute,
      loyaltySalesCommissionRoute,
      loyaltyWithdrawalsRoute,
      loyaltyWithdrawalDetailRoute,
      loyaltyBpmRedirectRoute,
      loyaltyBpmRuntimeRedirectRoute,
      loyPermissionsRoute,
    ]),
    careRoute.addChildren([
      careOverviewRoute,
      careTicketsRoute,
      careTicketDetailRoute,
      careRmasRoute,
      careRmaDetailRoute,
      careComplaintsRoute,
      careRecallsRoute,
      careKnowledgeRoute,
      careSurveysRoute,
      carePoliciesRoute,
      asPermissionsRoute,
    ]),
    dmsRoute.addChildren([
      dmsOverviewRoute,
      dmsOutletsRoute,
      dmsOutletDetailRoute,
      dmsRoutesRoute,
      dmsRouteDetailRoute,
      dmsVisitsRoute,
      dmsVisitDetailRoute,
      dmsSurveyRoute,
      dmsCollectionsRoute,
      dmsKpiRoute,
      dmsPermissionsRoute,
    ]),
    hrRoute.addChildren([
      hrIndexRoute,
      hrLeaveRoute,
      hrLeaveCreateRoute,
      hrLeaveDetailRoute,
      hrOpsRoute,
      hrTitlesRoute,
    ]),
    connectRoute.addChildren([
      connectOverviewRoute,
      connectConnectorsRoute,
      connectConnectorCreateRoute,
      connectConnectorDetailRoute,
      connectSyncLogRoute,
      connectApiKeysRoute,
      connectVaultRoute,
      connectIotDevicesRoute,
      connectDriftRoute,
      connectMarketplaceRoute,
      connectMarketplaceListingsRoute,
      intgPermissionsRoute,
    ]),
    notifyRoute.addChildren([
      notifyOverviewRoute,
      notifyInboxRoute,
      notifyPreferencesRoute,
      notifyTemplatesRoute,
      notifyTemplateDetailRoute,
      notifyRulesRoute,
      notifyChannelsRoute,
      notifyLogRoute,
      notifyCampaignsRoute,
      notifyCampaignDetailRoute,
      ntfPermissionsRoute,
    ]),
    filesRoute.addChildren([filesIndexRoute, filesExportsRoute, filesTemplatesRoute]),
    paymentsRoute.addChildren([
      paymentOverviewRoute,
      paymentReceivablesRoute,
      paymentReceiptsRoute,
      paymentCreditNotesRoute,
      paymentCashDepositsRoute,
      paymentPayablesRoute,
      paymentDisbursementsRoute,
      paymentSessionsRoute,
      paymentCallbacksRoute,
      paymentBankRoute,
      paymentReconciliationsRoute,
      paymentRefundsRoute,
      paymentEInvoicesRoute,
      paymentEInvoiceConfigRoute,
      paymentPeriodsRoute,
      paymentFarmerPayoutsRoute,
      paymentLandedCostRoute,
      paymentAccountsRoute,
      paymentDashboardRoute,
      paymentCashFundsRoute,
      paymentJournalRoute,
      paymentLedgerReconciliationRoute,
      payPermissionsRoute,
    ]),
    insightRoute.addChildren([
      insightCenterRoute,
      insightReportsRedirectRoute,
      insightReportDetailRoute,
      insightBuilderRoute,
      insightScheduleCreateRoute,
      insightKpiCreateRoute,
      insightManualCostsRoute,
      insightSalesTargetsRoute,
      insightDashboardRoute,
      insightForecastRoute,
      insightForecastDetailRoute,
      rptPermissionsRoute,
    ]),
    adminAuditRoute,
    adminOrgRoute,
    roleHubRoute,
    accountRoute,
    adminUsersRoute,
    adminUserDetailRoute,
    adminPortalAccessRoute,
    adminRolesRoute,
    adminRoleDetailRoute,
    bpmCenterRoute,
    bpmRuntimeRoute,
    bpmDelegationsRoute,
    bpmHistoryRoute,
    bpmMatrixRoute,
    bpmSimulationRoute,
    bpmBuilderRoute,
    bpmTemplateRoute,
    // W12 — hồ sơ nghiệm thu
    adminBrdCoverageRoute,
    adminStandardsAuditRoute,
    // W8 — control plane 5.2
    adminDashboardRoute,
    adminProfileRoute,
    adminConfigRoute,
    adminPortalChannelsRoute,
    adminSubscriptionRoute,
    adminEntitlementsRoute,
    adminBranchesRoute,
    adminRecoveryRoute,
    adminSecurityRoute,
    platformAccessRoute,
    platformDashboardRoute,
    platformTenantsRoute,
    platformTenantCreateRoute,
    platformChangesRoute,
    platformTenantDetailRoute,
    platformPlansRoute,
    platformPlanApprovalsRoute,
    platformPlanConfigRoute,
    platformAddonsRoute,
    platformInvoicesRoute,
    platformInvoiceDetailRoute,
    // W9
    platformMonitoringRoute,
    platformSupportRoute,
    platformConfigRoute,
    platformBpmRoute,
    platformWorkInboxRoute,
    // T9 A43
    platformAuditRoute,
    // Kiểm thử UI 01/10/2026 — splat cuối cùng
    notFoundRoute,
  ]),
]);

// 12/10/2026 — màn lỗi mặc định tiếng Việt, không lộ lỗi kỹ thuật (thay "Something went wrong! / Show Error"); lỗi đăng nhập tự đăng nhập lại
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultErrorComponent: ({ error, reset }) => <RouteErrorPage error={error} reset={reset} />,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
